import React, { useState, useEffect } from 'react';
import { AiTextAssistant } from '../components/AiTextAssistant';
import {
  uploadImageApi,
  createNewsApi,
  updateNewsApi,
  deleteNewsApi,
  createEventApi,
  updateEventApi,
  deleteEventApi,
  createActivityApi,
  updateActivityApi,
  deleteActivityApi,
  createCultureApi,
  updateCultureApi,
  deleteCultureApi,
  createBoardMemberApi,
  deleteBoardMemberApi,
  createMemberApi,
  updateMemberApi,
  deleteMemberApi,
  submitMembershipApplicationApi,
  updateMembershipApplicationApi,
  deleteMembershipApplicationApi,
  createDonationApi,
  updateDonationApi,
  deleteDonationApi,
  createGalleryApi,
  deleteGalleryApi,
  createDocumentApi,
  deleteDocumentApi,
  deleteContactMessageApi,
  updateSettingsApi,
  createAdApi,
  updateAdApi,
  deleteAdApi,
  createEventTicketApi,
  updateEventTicketApi,
  deleteEventTicketApi,
  updateTicketPurchaseApi,
  deleteTicketPurchaseApi,
  validateTicketQrApi,
} from '../services/api';
import {
  NewsItem,
  EventItem,
  ActivityItem,
  CultureItem,
  MemberUser,
  DonationRecord,
  MediaGalleryItem,
  DocumentItem,
  SiteSettings,
  LanguageCode,
  AdminAccount,
  DonationCampaign,
  PaymentGatewaySettings,
  AdvertisementItem,
  EventTicket,
  UserTicketPurchase,
} from '../types';
import { translations } from '../i18n/translations';
import { parseStreamEmbedUrl } from '../utils/stream';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import {
  LayoutDashboard,
  Newspaper,
  Calendar,
  Layers,
  Sparkles,
  Users,
  HeartHandshake,
  Image as ImageIcon,
  FileText,
  Settings,
  Tv,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  CheckCircle2,
  AlertCircle,
  Download,
  Eye,
  LogOut,
  Sliders,
  Search,
  Filter,
  Check,
  Mail,
  QrCode,
  Ticket,
  ListCheck,
  FileSpreadsheet,
  Video,
  ShieldAlert,
  Server,
  HardDrive,
  Cpu,
  Globe,
  Clock,
  ExternalLink,
  Copy,
  Printer,
  Send,
  Share2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Database,
  Terminal,
  Radio,
  MapPin,
  Lock,
  Target,
  CreditCard,
  Landmark,
  UserCheck,
  FileCode,
  MessageSquare,
  CheckSquare,
  Upload,
  UserPlus,
  Crown,
  Edit3,
  List,
  ChevronRight,
  ChevronLeft,
  Home,
  Tag,
  Hash,
  PenTool,
  Archive,
  BarChart3,
  Receipt,
  TrendingUp,
  TrendingDown,
  FolderPlus,
  Wrench,
  BookOpen,
  GraduationCap,
  Book,
  FileCheck,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  PhoneCall,
  FileEdit,
  Megaphone,
} from 'lucide-react';

interface AdminPanelProps {
  currentLang: LanguageCode;
  news: NewsItem[];
  setNews: React.Dispatch<React.SetStateAction<NewsItem[]>>;
  events: EventItem[];
  setEvents: React.Dispatch<React.SetStateAction<EventItem[]>>;
  activities: ActivityItem[];
  setActivities: React.Dispatch<React.SetStateAction<ActivityItem[]>>;
  cultureItems: CultureItem[];
  setCultureItems: React.Dispatch<React.SetStateAction<CultureItem[]>>;
  members: MemberUser[];
  setMembers: React.Dispatch<React.SetStateAction<MemberUser[]>>;
  donations: DonationRecord[];
  setDonations: React.Dispatch<React.SetStateAction<DonationRecord[]>>;
  gallery: MediaGalleryItem[];
  setGallery: React.Dispatch<React.SetStateAction<MediaGalleryItem[]>>;
  documents: DocumentItem[];
  setDocuments: React.Dispatch<React.SetStateAction<DocumentItem[]>>;
  settings: SiteSettings;
  setSettings: React.Dispatch<React.SetStateAction<SiteSettings>>;
  ads?: AdvertisementItem[];
  setAds?: React.Dispatch<React.SetStateAction<AdvertisementItem[]>>;
  eventTickets?: EventTicket[];
  setEventTickets?: React.Dispatch<React.SetStateAction<EventTicket[]>>;
  userPurchases?: UserTicketPurchase[];
  setUserPurchases?: React.Dispatch<React.SetStateAction<UserTicketPurchase[]>>;
  adminAccounts?: AdminAccount[];
  setAdminAccounts?: React.Dispatch<React.SetStateAction<AdminAccount[]>>;
  currentAdminUser?: AdminAccount | null;
  onLogout: () => void;
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info', title?: string) => void;
}

interface ImageUploadInputProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
}

const ImageUploadInput: React.FC<ImageUploadInputProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Görsel URL adresi veya cihazdan yükleyin',
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadImageApi(file);
      onChange(uploadedUrl);
    } catch {
      // Fallback to data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          onChange(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-1.5 text-xs">
      <label className="text-gray-300 font-bold block">{label}</label>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <label className="cursor-pointer px-3.5 py-2.5 rounded-xl bg-[#0B3D2E] border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#081B16] font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-md shrink-0">
          <Upload className="w-4 h-4" />
          <span>{isUploading ? 'Görsel Sunucuya Yükleniyor...' : 'Galeriden / Cihazdan Resim Seç'}</span>
          <input
            type="file"
            accept="image/*"
            disabled={isUploading}
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
        <span className="text-[10px] text-gray-400 self-center hidden sm:inline">veya URL</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono text-xs"
        />
      </div>
      {value && (
        <div className="mt-2 p-1.5 rounded-xl bg-black/50 border border-white/10 w-fit flex items-center gap-3">
          <img src={value} alt="Önizleme" className="h-16 rounded-lg object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-red-400 hover:text-red-300 text-xs font-bold underline"
          >
            Kaldır
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminPanel: React.FC<AdminPanelProps> = ({
  currentLang,
  news,
  setNews,
  events,
  setEvents,
  activities,
  setActivities,
  cultureItems,
  setCultureItems,
  members,
  setMembers,
  donations,
  setDonations,
  gallery,
  setGallery,
  documents,
  setDocuments,
  settings,
  setSettings,
  ads = [],
  setAds,
  eventTickets = [],
  setEventTickets,
  userPurchases = [],
  setUserPurchases,
  adminAccounts = [],
  setAdminAccounts,
  currentAdminUser,
  onLogout,
  onShowToast,
}) => {
  const t = translations[currentLang];

  // Role-Based Access Control Flags
  const isSuperAdmin = !currentAdminUser || currentAdminUser.role === 'SuperAdmin' || currentAdminUser.role === 'Admin';
  const isEditor = currentAdminUser?.role === 'Editor';

  // Main Tabs
  const [activeTab, setActiveTab] = useState<
    | 'dashboard'
    | 'news'
    | 'events'
    | 'activities'
    | 'culture'
    | 'members'
    | 'donations'
    | 'gallery'
    | 'documents'
    | 'settings'
    | 'live'
    | 'admins'
    | 'ads'
    | 'tickets'
  >('dashboard');

  // Navigation View Level: 'main_grid' | 'sub_grid' | 'action'
  const [viewLevel, setViewLevel] = useState<'main_grid' | 'sub_grid' | 'action'>('main_grid');

  useEffect(() => {
    const handleReset = () => {
      setActiveTab('dashboard');
      setViewLevel('main_grid');
    };
    const handleChangeTab = (e: CustomEvent<string>) => {
      if (e.detail) {
        setActiveTab(e.detail as any);
        setViewLevel('sub_grid');
      }
    };
    window.addEventListener('reset-admin-dashboard', handleReset);
    window.addEventListener('change-admin-tab', handleChangeTab as EventListener);
    return () => {
      window.removeEventListener('reset-admin-dashboard', handleReset);
      window.removeEventListener('change-admin-tab', handleChangeTab as EventListener);
    };
  }, []);

  // Sub-tab States for Each Module
  const [newsSubTab, setNewsSubTab] = useState<
    'list' | 'add' | 'drafts' | 'published' | 'archive' | 'categories' | 'tags' | 'authors' | 'seo' | 'comments'
  >('list');

  const [eventsSubTab, setEventsSubTab] = useState<
    'list' | 'add' | 'calendar' | 'attendees' | 'qrcode' | 'quota' | 'map'
  >('list');

  const [activitiesSubTab, setActivitiesSubTab] = useState<
    'list' | 'add' | 'categories' | 'files' | 'gallery'
  >('list');

  const [cultureCategoryFilter, setCultureCategoryFilter] = useState<string>('Tümü');
  const [cultureSubTab, setCultureSubTab] = useState<'all' | 'lessons' | 'dictionary' | 'history'>('all');
  const [expandedMenuTab, setExpandedMenuTab] = useState<string | null>('news');

  const [membersSubTab, setMembersSubTab] = useState<
    'list' | 'add' | 'applications' | 'pending' | 'dues' | 'qrcard' | 'roles' | 'groups' | 'message'
  >('list');

  const [donationsSubTab, setDonationsSubTab] = useState<
    'list' | 'campaigns' | 'pos_settings' | 'add' | 'receipts' | 'income' | 'expense' | 'charts' | 'export'
  >('list');

  const [donationApprovalFilter, setDonationApprovalFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const [gallerySubTab, setGallerySubTab] = useState<
    'photos' | 'videos' | 'new_album' | 'add_photo' | 'add_video' | 'cover_order'
  >('photos');

  const [documentsSubTab, setDocumentsSubTab] = useState<
    'all' | 'pdf' | 'word' | 'excel' | 'zip' | 'charter' | 'reports' | 'forms' | 'stats'
  >('all');

  const [settingsSubTab, setSettingsSubTab] = useState<
    | 'site'
    | 'logo_header_footer'
    | 'menu'
    | 'lang'
    | 'seo'
    | 'smtp'
    | 'whatsapp_phone_map'
    | 'social'
    | 'api_theme'
    | 'maintenance'
    | 'backup_logs'
  >('site');

  const [liveSubTab, setLiveSubTab] = useState<
    'config' | 'obs' | 'status' | 'recordings'
  >('config');

  // Dues tracking filter and receipt modal state
  const [adminDuesFilter, setAdminDuesFilter] = useState<'all' | 'paid' | 'overdue'>('all');
  const [selectedAdminReceipt, setSelectedAdminReceipt] = useState<{
    receiptNo: string;
    payerName: string;
    memberNo?: string;
    date: string;
    amount: number;
    currency: string;
    title: string;
    paymentMethod: string;
  } | null>(null);

  // 12 Main Modules Configuration for Grid Navigation
  const mainGridModules = [
    {
      id: 'news',
      title: 'HABER VE DUYURULAR',
      subtitle: '10 Alt Menü',
      icon: Newspaper,
      badge: '10 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'HABER LİSTESİ', icon: List, action: () => setNewsSubTab('list') },
        { id: 'add', title: 'HABER EKLE', icon: Plus, action: () => { setEditingNewsId(null); setNewsSubTab('add'); } },
        { id: 'drafts', title: 'TASLAK HABERLER', icon: FileEdit, action: () => setNewsSubTab('drafts') },
        { id: 'published', title: 'YAYINDA OLANLAR', icon: CheckCircle2, action: () => setNewsSubTab('published') },
        { id: 'archive', title: 'ARŞİV', icon: Archive, action: () => setNewsSubTab('archive') },
        { id: 'categories', title: 'KATEGORİ YÖNETİMİ', icon: Tag, action: () => setNewsSubTab('categories') },
        { id: 'tags', title: 'ETİKET YÖNETİMİ', icon: Hash, action: () => setNewsSubTab('tags') },
        { id: 'authors', title: 'YAZAR YÖNETİMİ', icon: PenTool, action: () => setNewsSubTab('authors') },
        { id: 'seo', title: 'SEO YÖNETİMİ', icon: Globe, action: () => setNewsSubTab('seo') },
        { id: 'comments', title: 'YORUM YÖNETİMİ', icon: MessageSquare, action: () => setNewsSubTab('comments') },
      ],
    },
    {
      id: 'events',
      title: 'ETKİNLİKLER',
      subtitle: '7 Alt Menü',
      icon: Calendar,
      badge: '7 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'ETKİNLİK LİSTESİ', icon: List, action: () => setEventsSubTab('list') },
        { id: 'add', title: 'ETKİNLİK EKLE', icon: Plus, action: () => { setEditingEventId(null); setEventsSubTab('add'); } },
        { id: 'calendar', title: 'TAKVİM', icon: Calendar, action: () => setEventsSubTab('calendar') },
        { id: 'attendees', title: 'KATILIMCILAR', icon: Users, action: () => setEventsSubTab('attendees') },
        { id: 'qrcode', title: 'QR BİLET', icon: QrCode, action: () => setEventsSubTab('qrcode') },
        { id: 'quota', title: 'KONTENJAN', icon: BarChart3, action: () => setEventsSubTab('quota') },
        { id: 'map', title: 'KONUM & HARİTA', icon: MapPin, action: () => setEventsSubTab('map') },
      ],
    },
    {
      id: 'activities',
      title: 'FAALİYETLERİM',
      subtitle: '5 Alt Menü',
      icon: Layers,
      badge: '5 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'FAALİYET LİSTESİ', icon: List, action: () => setActivitiesSubTab('list') },
        { id: 'add', title: 'FAALİYET EKLE', icon: Plus, action: () => setActivitiesSubTab('add') },
        { id: 'categories', title: 'KATEGORİLER', icon: Tag, action: () => setActivitiesSubTab('categories') },
        { id: 'files', title: 'DOKÜMAN & BELGELER', icon: FileText, action: () => setActivitiesSubTab('files') },
        { id: 'gallery', title: 'FAALİYET GALERİSİ', icon: ImageIcon, action: () => setActivitiesSubTab('gallery') },
      ],
    },
    {
      id: 'culture',
      title: 'KÜRT KÜLTÜRÜ & DİL',
      subtitle: '4 Alt Menü',
      icon: BookOpen,
      badge: '4 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'all', title: 'TÜM İÇERİKLER', icon: BookOpen, action: () => setCultureSubTab('all') },
        { id: 'lessons', title: 'KÜRTÇE DİL DERSLERİ', icon: GraduationCap, action: () => setCultureSubTab('lessons') },
        { id: 'dictionary', title: 'SÖZLÜK', icon: Book, action: () => setCultureSubTab('dictionary') },
        { id: 'history', title: 'TARİH & KÜLTÜR ARŞİVİ', icon: Landmark, action: () => setCultureSubTab('history') },
      ],
    },
    {
      id: 'live',
      title: 'CANLI YAYIN',
      subtitle: '2 Alt Menü',
      icon: Tv,
      badge: 'ŞİMDİ CANLI',
      badgeBg: 'bg-red-600 text-white font-bold border border-red-400 animate-pulse',
      subItems: [
        { id: 'config', title: 'CANLI YAYIN YAPILANDIRMASI', icon: Tv, action: () => setLiveSubTab('config') },
        { id: 'recordings', title: 'GEÇMİŞ YAYIN KAYITLARI', icon: Video, action: () => setLiveSubTab('recordings') },
      ],
    },
    {
      id: 'members',
      title: 'ÜYELİK YÖNETİMİ',
      subtitle: '9 Alt Menü',
      icon: UserCheck,
      badge: '9 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'ÜYE LİSTESİ', icon: List, action: () => setMembersSubTab('list') },
        { id: 'add', title: 'YENİ ÜYE KAYDI', icon: Plus, action: () => setMembersSubTab('add') },
        { id: 'applications', title: 'BAŞVURULAR', icon: FileCheck, action: () => setMembersSubTab('applications') },
        { id: 'pending', title: 'ONAY BEKLEYENLER', icon: Clock, action: () => setMembersSubTab('pending') },
        { id: 'dues', title: 'AİDAT TAKİPLERİ', icon: CreditCard, action: () => setMembersSubTab('dues') },
        { id: 'qrcard', title: 'ÜYE QR KART', icon: QrCode, action: () => setMembersSubTab('qrcard') },
        { id: 'roles', title: 'ROL & YETKİLER', icon: ShieldCheck, action: () => setMembersSubTab('roles') },
        { id: 'groups', title: 'GRUPLAR & KOMİTELER', icon: Users, action: () => setMembersSubTab('groups') },
        { id: 'message', title: 'TOPLU SMS / BİLDİRİM', icon: Send, action: () => setMembersSubTab('message') },
      ],
    },
    {
      id: 'donations',
      title: 'BAĞIŞ YAP & YÖNETİMİ',
      subtitle: '9 Alt Menü',
      icon: HeartHandshake,
      badge: '9 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'BAĞIŞ GEÇMİŞİ', icon: BarChart3, action: () => setDonationsSubTab('list') },
        { id: 'campaigns', title: 'KAMPANYALAR', icon: Target, action: () => setDonationsSubTab('campaigns') },
        { id: 'pos_settings', title: 'SANAL POS & STRIPE', icon: CreditCard, action: () => setDonationsSubTab('pos_settings') },
        { id: 'add', title: 'MANUEL BAĞIŞ GİR / PARA YATIR', icon: Plus, action: () => setDonationsSubTab('add') },
        { id: 'receipts', title: 'MAKBUZLAR (RECEIPTS)', icon: Receipt, action: () => setDonationsSubTab('receipts') },
        { id: 'income', title: 'GELİR KAYITLARI', icon: TrendingUp, action: () => setDonationsSubTab('income') },
        { id: 'expense', title: 'GİDER KAYITLARI', icon: TrendingDown, action: () => setDonationsSubTab('expense') },
        { id: 'charts', title: 'MALİ RAPORLAR', icon: PieChart, action: () => setDonationsSubTab('charts') },
        { id: 'export', title: 'DIŞA AKTAR', icon: Download, action: () => setDonationsSubTab('export') },
      ],
    },
    {
      id: 'gallery',
      title: 'GALERİ YÖNETİMİ',
      subtitle: '6 Alt Menü',
      icon: ImageIcon,
      badge: '6 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'photos', title: 'FOTOĞRAFLAR', icon: ImageIcon, action: () => setGallerySubTab('photos') },
        { id: 'videos', title: 'VİDEOLAR', icon: Video, action: () => setGallerySubTab('videos') },
        { id: 'add_photo', title: 'FOTOĞRAF YÜKLE', icon: Plus, action: () => setGallerySubTab('add_photo') },
        { id: 'add_video', title: 'VİDEO EKLE', icon: Plus, action: () => setGallerySubTab('add_video') },
        { id: 'new_album', title: 'YENİ ALBÜM', icon: FolderPlus, action: () => setGallerySubTab('new_album') },
        { id: 'cover_order', title: 'KAPAK & SIRALAMA', icon: Sliders, action: () => setGallerySubTab('cover_order') },
      ],
    },
    {
      id: 'documents',
      title: 'BELGE & DOKÜMANLAR',
      subtitle: '8 Alt Menü',
      icon: FileText,
      badge: '8 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'all', title: 'TÜM BELGELER', icon: FileText, action: () => setDocumentsSubTab('all') },
        { id: 'charter', title: 'TÜZÜK & EVRAKLAR', icon: FileCheck, action: () => setDocumentsSubTab('charter') },
        { id: 'pdf', title: 'PDF DOSYALARI', icon: FileCode, action: () => setDocumentsSubTab('pdf') },
        { id: 'word', title: 'WORD BELGELERİ', icon: FileText, action: () => setDocumentsSubTab('word') },
        { id: 'excel', title: 'EXCEL RAPORLARI', icon: FileSpreadsheet, action: () => setDocumentsSubTab('excel') },
        { id: 'zip', title: 'ARŞİV / ZIP', icon: Archive, action: () => setDocumentsSubTab('zip') },
        { id: 'reports', title: 'İSTATİSTİKLER', icon: BarChart3, action: () => setDocumentsSubTab('reports') },
        { id: 'forms', title: 'BAŞVURU FORMLARI', icon: FileEdit, action: () => setDocumentsSubTab('forms') },
      ],
    },
    {
      id: 'contact',
      title: 'İLETİŞİM BİLGİLERİ',
      subtitle: '3 Alt Menü',
      icon: PhoneCall,
      badge: '3 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'info', title: 'İLETİŞİM BİLGİLERİ', icon: PhoneCall, action: () => setSettingsSubTab('whatsapp_phone_map') },
        { id: 'map', title: 'HARİTA & ADRES', icon: MapPin, action: () => setSettingsSubTab('whatsapp_phone_map') },
        { id: 'social', title: 'SOSYAL MEDYA', icon: Share2, action: () => setSettingsSubTab('social') },
      ],
    },
    {
      id: 'settings',
      title: 'SİTE & DERNEK AYARLARI',
      subtitle: '11 Alt Menü',
      icon: Settings,
      badge: '11 Alt Menü',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'site', title: 'GENEL AYARLAR', icon: Settings, action: () => setSettingsSubTab('site') },
        { id: 'logo_header_footer', title: 'LOGO & BAŞLIK', icon: ImageIcon, action: () => setSettingsSubTab('logo_header_footer') },
        { id: 'menu', title: 'MENÜ YAPISI', icon: Layers, action: () => setSettingsSubTab('menu') },
        { id: 'lang', title: 'ÇEVİRİ & DİL', icon: Globe, action: () => setSettingsSubTab('lang') },
        { id: 'seo', title: 'SEO & META', icon: Search, action: () => setSettingsSubTab('seo') },
        { id: 'smtp', title: 'SMTP MAIL & SMS', icon: Mail, action: () => setSettingsSubTab('smtp') },
        { id: 'whatsapp_phone_map', title: 'İLETİŞİM & HARİTA', icon: PhoneCall, action: () => setSettingsSubTab('whatsapp_phone_map') },
        { id: 'social', title: 'SOSYAL MEDYA', icon: Share2, action: () => setSettingsSubTab('social') },
        { id: 'api_theme', title: 'API & TEMA', icon: Sliders, action: () => setSettingsSubTab('api_theme') },
        { id: 'maintenance', title: 'BAKIM MODU', icon: Wrench, action: () => setSettingsSubTab('maintenance') },
        { id: 'backup_logs', title: 'YEDEK & LOGLAR', icon: Database, action: () => setSettingsSubTab('backup_logs') },
      ],
    },
    {
      id: 'admins',
      title: 'YÖNETİCİ HESAPLARI',
      subtitle: '2 Alt Menü',
      icon: ShieldAlert,
      badge: 'Güvenlik',
      badgeBg: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold',
      subItems: [
        { id: 'list', title: 'YÖNETİCİ LİSTESİ', icon: Users, action: () => {} },
        { id: 'add', title: 'YENİ YÖNETİCİ TANIMLA', icon: Plus, action: () => {} },
      ],
    },
    {
      id: 'ads',
      title: 'REKLAM YÖNETİMİ',
      subtitle: 'Firma & Şahıs Reklamları',
      icon: Megaphone,
      badge: '1 Aylık Yayın',
      badgeBg: 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40 font-bold',
      subItems: [
        { id: 'list', title: 'REKLAM LİSTESİ', icon: List, action: () => setAdsSubTab('list') },
        { id: 'add', title: 'YENİ REKLAM EKLE', icon: Plus, action: () => setAdsSubTab('add') },
      ],
    },
    {
      id: 'tickets',
      title: 'BİLET YÖNETİMİ & QR KONTROL',
      subtitle: 'Bilet Oluşturma, Sipariş Onay & QR Scanner',
      icon: Ticket,
      badge: 'QR Kodlu Online Bilet',
      badgeBg: 'bg-[#38BDF8]/20 text-[#7DD3FC] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'bilet_olusturma', title: 'BİLET OLUŞTURMA', icon: Plus, action: () => setTicketsSubTab('bilet_olusturma') },
        { id: 'bilet_siparisleri', title: 'SİPARİŞLER VE ONAY', icon: ListCheck, action: () => setTicketsSubTab('bilet_siparisleri') },
        { id: 'qr_kontrol', title: 'QR BİLET KONTROL / KAPI', icon: QrCode, action: () => setTicketsSubTab('qr_kontrol') },
      ],
    },
  ];

  // Modal & Edit State
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<string>('');
  const [selectedMemberForCard, setSelectedMemberForCard] = useState<MemberUser | null>(members[0] || null);
  const [selectedReceipt, setSelectedReceipt] = useState<DonationRecord | null>(donations[0] || null);

  // Row Expansion & Edit States
  const [expandedNewsId, setExpandedNewsId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [expandedCultureId, setExpandedCultureId] = useState<string | null>(null);
  const [editingNewsId, setEditingNewsId] = useState<string | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  // Form State - News
  const [newsForm, setNewsForm] = useState({
    titleTR: '',
    titleKM: '',
    coverImage: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80',
    gallery: '',
    contentTR: '',
    videoUrl: '',
    fileUrl: '',
    category: 'Haber',
    tags: 'Dernek, Kültür',
    publishDate: new Date().toISOString().split('T')[0],
    seoTitle: '',
    seoDescription: '',
    status: 'Yayında' as 'Yayında' | 'Taslak' | 'Arşiv',
    author: 'Yönetim Kurulu',
  });

  // Form State - Event
  const [eventForm, setEventForm] = useState({
    titleTR: '',
    titleKM: '',
    descriptionTR: '',
    time: '19:00',
    date: '2026-08-15',
    address: '15 Rue de la Paix, 75002 Paris, France',
    googleMapUrl: 'https://maps.google.com/?q=Paris',
    photo: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80',
    videoUrl: '',
    liveStreamUrl: '',
    maxCapacity: 300,
    category: 'Konser',
  });

  // Form State - Activity Creation
  const [activityForm, setActivityForm] = useState({
    titleTR: '',
    titleKM: '',
    category: 'Kültür & Sanat',
    descriptionTR: '',
    descriptionKM: '',
    date: new Date().toISOString().split('T')[0],
    location: 'Paris Kültür Evi',
    image: '',
    fileUrl: '',
    fileName: '',
  });

  // State - Activity Categories & Repository Documents
  const [activityCategories, setActivityCategories] = useState<string[]>([
    'Kültür & Sanat',
    'Dil Kursları',
    'Gençlik Atölyeleri',
    'Yıllık Şenlikler',
    'Toplumsal Dayanışma',
    'Konferans & Seminer',
  ]);
  const [newActivityCatInput, setNewActivityCatInput] = useState('');

  const [activityFiles, setActivityFiles] = useState<{ id: string; name: string; activityTitle: string; size: string; date: string; url: string }[]>([
    { id: 'af-1', name: '2026_Dil_Haftasi_Programi.pdf', activityTitle: 'Kürtçe Dil Haftası', size: '2.4 MB', date: '2026-05-10', url: '#' },
    { id: 'af-2', name: 'Nevroz_Organizasyon_Brosuru.pdf', activityTitle: 'Nevroz Kutlaması 2026', size: '4.1 MB', date: '2026-03-18', url: '#' },
  ]);

  // State - Culture Category Manager & Modes
  const [cultureCategories, setCultureCategories] = useState<string[]>([
    'Kürt Tarihi',
    'Kürtçe Dersleri',
    'Alfabe',
    'Sözlük',
    'Masallar',
    'Şiirler',
    'Müzikler',
    'Dengbêj',
    'Edebiyat',
    'Gelenekler',
    'Yemek Kültürü',
    'Kıyafetler',
    'Videolar',
    'Makaleler',
  ]);
  const [newCultureCategoryInput, setNewCultureCategoryInput] = useState('');
  const [isCultureAddOpen, setIsCultureAddOpen] = useState(false);
  const [isCultureCatManagerOpen, setIsCultureCatManagerOpen] = useState(false);

  // Form State - Member
  const [memberForm, setMemberForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'member' as 'admin' | 'editor' | 'member',
    address: '',
    city: 'Paris',
    country: 'France',
    avatar: '',
  });

  // State - Membership Applications & Pending Approvals
  const [memberApplications, setMemberApplications] = useState([
    { id: 'app-1', fullName: 'Heval Botan', email: 'heval.b@example.com', phone: '+33 6 99 88 77', city: 'Paris', occupation: 'Mimar', date: '2026-07-20', status: 'Beklemede' },
    { id: 'app-2', fullName: 'Rojda Amed', email: 'rojda.a@example.com', phone: '+33 6 11 22 33', city: 'Lyon', occupation: 'Öğretmen', date: '2026-07-19', status: 'Beklemede' },
    { id: 'app-3', fullName: 'Cengiz Baran', email: 'cengiz.b@example.com', phone: '+33 6 44 55 66', city: 'Marseille', occupation: 'Mühendis', date: '2026-07-18', status: 'Beklemede' },
  ]);

  // State - Member Working Groups
  const [memberGroups, setMemberGroups] = useState([
    { id: 'grp-1', name: 'Kültür & Sanat Komisyonu', leader: 'Dr. Serhat Botan', memberCount: 14, description: 'Şenlikler, tiyatro ve dengbêj dinletileri organizasyonu.' },
    { id: 'grp-2', name: 'Kürtçe Derslik Eğitmenleri', leader: 'Gülşah Yılmaz', memberCount: 8, description: 'Kurmancî ve Zazakî dil sınıfları müfredatı.' },
    { id: 'grp-3', name: 'Gençlik Meclisi', leader: 'Agit Demir', memberCount: 22, description: 'Gençlik spor etkinlikleri ve üniversite ağları.' },
  ]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupLeader, setNewGroupLeader] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  // State - Broadcast Notification System
  const [broadcastForm, setBroadcastForm] = useState({
    targetGroup: 'Tüm Üyeler',
    channel: 'SMS & E-Posta' as 'E-Posta' | 'SMS' | 'Mobil Bildirim' | 'SMS & E-Posta',
    subject: 'Kürt Kültür Derneği - Önemli Duyuru',
    message: 'Sayın Üyemiz, Kültür derneğimizin yeni dönem etkinlikleri ve Kürtçe dil kursu kayıtlarımız başlamıştır.',
  });
  const [broadcastLogs, setBroadcastLogs] = useState([
    { id: 'log-1', date: '2026-07-15', channel: 'E-Posta', target: 'Tüm Üyeler (1,248 Kişi)', status: 'Başarıyla İletildi' },
    { id: 'log-2', date: '2026-06-28', channel: 'SMS', target: 'Kültür Komisyonu (14 Kişi)', status: 'Başarıyla İletildi' },
  ]);

  // Form State - Donation
  const [donationForm, setDonationForm] = useState({
    donorName: '',
    donorEmail: '',
    amount: 100,
    currency: 'EUR',
    campaign: 'Kültür Evi Destek Fonu',
    paymentMethod: 'Credit Card',
  });

  // State & Form State - Virtual POS & Credit Card Payment Gateway
  const [posForm, setPosForm] = useState<any>(() => {
    return settings.paymentGateway || {
      activeProvider: 'sanal_pos',
      providerName: 'Dernek Resmi Sanal POS & SEPA Geçidi',
      testMode: false,
      merchantId: '30094812',
      apiKey: 'kkdd-pos-live-api-key-9921',
      secretKey: '30094812_SEC_KEY_2026',
      terminalId: 'TR-78200-POS',
      bankAccountName: 'Kürt Kültür ve Dayanışma Derneği Yvelines',
      iban: 'FR76 3000 4000 0123 4567 8901 234',
      swiftCode: 'AGRIFRPPXXX',
      autoDebitEnabled: true,
      minDonationAmount: 5,
      currency: 'EUR',
      redirect3dSecure: true,
    };
  });
  const [showSecretKey, setShowSecretKey] = useState(false);

  // State & Form State - Donation Campaigns & Funds (Bağış Seçenekleri)
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>(() => {
    return settings.donationCampaigns && settings.donationCampaigns.length > 0
      ? settings.donationCampaigns
      : [
          {
            id: 'camp-1',
            title: 'Dil Kursları ve Kültür Evi Destek Fonu',
            category: 'Eğitim & Dil',
            description: 'Derneğimizde verilen Kürtçe dil dersleri, öğretmen maaşları, ders materyalleri ve kültür evi kira & işletme giderlerini karşılar.',
            targetAmount: 20000,
            collectedAmount: 14500,
            isActive: true,
          },
          {
            id: 'camp-2',
            title: 'Öğrenci Burs Fonu',
            category: 'Eğitim Yardım',
            description: 'Yurtdışında ve bölgede üniversite okuyan ihtiyaç sahibi 50+ öğrenciye her ay düzenli eğitim bursu desteği sağlar.',
            targetAmount: 15000,
            collectedAmount: 11200,
            isActive: true,
          },
          {
            id: 'camp-3',
            title: 'Newroz Festivali Kültür Sponsorluğu',
            category: 'Kültür & Etkinlik',
            description: 'Geleneksel Newroz kutlaması, sahne kurulumu, dengbêj ve sanatçı ağırlama, halk oyunları kostümleri ve organizasyon fonu.',
            targetAmount: 10000,
            collectedAmount: 8900,
            isActive: true,
          },
          {
            id: 'camp-4',
            title: 'İnsani Yardım ve Acil Afet Fonu',
            category: 'Acil Yardım',
            description: 'Doğal afetler, acil insani krizler ve dayanışma ihtiyacı duyan aileler için anında devredilen acil destek fonu.',
            targetAmount: 30000,
            collectedAmount: 19750,
            isActive: true,
          },
        ];
  });
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<DonationCampaign | null>(null);
  const [campaignForm, setCampaignForm] = useState<Partial<DonationCampaign>>({
    title: '',
    category: 'Eğitim & Dil',
    description: '',
    targetAmount: 10000,
    collectedAmount: 0,
    isActive: true,
  });

  // Form State - Live Stream
  const [liveForm, setLiveForm] = useState({
    isLive: settings.liveStream?.isLive ?? true,
    title: settings.liveStream?.title || 'Kürt Dil Haftası Özel Semineri & Dengbêj Dinletisi (CANLI YAYIN)',
    description: settings.liveStream?.description || 'Canlı Kültür ve Sanat Yayın Etkinliği',
    platform: settings.liveStream?.platform || 'YouTube',
    youtubeUrl: settings.liveStream?.youtubeUrl || 'https://www.youtube.com/live/yZ7eZ8H_a1I?is=jgy0RIN_u-bmXzg1',
    facebookUrl: settings.liveStream?.facebookUrl || '',
    vimeoUrl: settings.liveStream?.vimeoUrl || '',
    embedUrl: settings.liveStream?.embedUrl || 'https://www.youtube.com/embed/yZ7eZ8H_a1I',
    coverImage: settings.liveStream?.coverImage || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1200&q=80',
    viewerCount: settings.liveStream?.viewerCount || 342,
    startDate: settings.liveStream?.startDate || '2026-07-21',
    startTime: settings.liveStream?.startTime || '20:00',
    endDate: settings.liveStream?.endDate || '2026-07-21',
    endTime: settings.liveStream?.endTime || '22:30',
    obsRtmpUrl: settings.liveStream?.obsRtmpUrl || 'rtmp://live.youtube.com/live2',
    obsStreamKey: settings.liveStream?.obsStreamKey || 'kkdd-live-2026-secret-key',
  });

  // Form State - Site Settings
  const [siteSettingsForm, setSiteSettingsForm] = useState({
    siteName: settings.siteName || 'Kürt Kültür ve Dayanışma Derneği',
    subTitle: settings.subTitle || 'Kültür, Sanat ve Kürt Dili Evi',
    logoText1: settings.logoText1 || 'KÜRT KÜLTÜR',
    logoText2: settings.logoText2 || 'DERNEĞİ',
    logoText3: settings.logoText3 || 'Kurdish Cultural Association',
    logoImageUrl: settings.logoImageUrl || '',
    address: settings.address || '250 bis boulevard maréchal juin 78200 Mantes La Jolie',
    phone: settings.phone || '+33 6 14 62 97 06',
    whatsapp: settings.whatsapp || '+33614629706',
    email: settings.email || 'kurdish.association.paris@gmail.com',
    facebook: settings.social?.facebook || 'https://facebook.com',
    instagram: settings.social?.instagram || 'https://instagram.com',
    youtube: settings.social?.youtube || 'https://youtube.com',
    twitter: settings.social?.twitter || 'https://twitter.com',
    linkedin: settings.social?.linkedin || 'https://linkedin.com',
    maintenanceMode: settings.maintenanceMode || false,
    smtpHost: 'smtp.gmail.com',
    smtpPort: '587',
    smtpUser: 'info@kurdishculture.org',
    headerAnnouncement: 'Kürt Dil Haftası Etkinlik Kayıtları Başlamıştır!',
    footerText: '© 2026 Kürt Kültür ve Dayanışma Derneği. Tüm hakları saklıdır.',
    googleMapsEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.5!2d2.332!3d48.868',
  });
  const [settingsSavedToast, setSettingsSavedToast] = useState(false);

  // Form State - Gallery Add Photo
  const [photoForm, setPhotoForm] = useState({
    titleTR: '',
    titleKM: '',
    url: '',
    category: 'Kültür Şenliği',
    date: new Date().toISOString().split('T')[0],
    tags: 'Resim, Şenlik',
  });

  // Form State - Gallery Add Video
  const [videoForm, setVideoForm] = useState({
    titleTR: '',
    titleKM: '',
    videoUrl: '',
    category: 'Dengbêj & Müzik',
    date: new Date().toISOString().split('T')[0],
    description: '',
  });

  // Form State - Document Upload
  const [documentForm, setDocumentForm] = useState({
    titleTR: '',
    category: 'Tüzük & Yönetmelik',
    fileUrl: '',
    fileType: 'PDF' as 'PDF' | 'Word' | 'Excel' | 'ZIP',
    fileSize: '1.8 MB',
  });

  // Form State - Culture Content Add
  const [cultureForm, setCultureForm] = useState({
    titleTR: '',
    titleKM: '',
    category: 'Kürt Tarihi',
    contentTR: '',
    contentKM: '',
    mediaUrl: '',
  });

  // Form State - New Admin Creation
  const [newAdminForm, setNewAdminForm] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'Admin' as 'SuperAdmin' | 'Admin' | 'Editor',
  });

  const handleCreateAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditor) {
      alert('Erişim Engellendi: Yeni yönetici hesabı ekleme yetkisi yalnızca "Süper Admin" rolündeki kullanıcılara aittir.');
      return;
    }
    if (!newAdminForm.fullName || !newAdminForm.email || !newAdminForm.password) {
      alert('Lütfen yönetici adı, e-postası ve şifresini eksiksiz girin.');
      return;
    }

    const newAcc: AdminAccount = {
      id: `admin-${Date.now()}`,
      fullName: newAdminForm.fullName,
      email: newAdminForm.email,
      password: newAdminForm.password,
      role: newAdminForm.role,
      status: 'active',
      createdAt: new Date().toISOString().split('T')[0],
      lastLogin: 'Henüz Giriş Yapılmadı',
    };

    if (setAdminAccounts) {
      setAdminAccounts((prev) => [newAcc, ...prev]);
    }
    alert(`Yeni Yönetici "${newAdminForm.fullName}" Başarıyla Eklendi ve Sistemde Yetkilendirildi!`);
    setNewAdminForm({ fullName: '', email: '', password: '', role: 'Admin' });
  };

  // SubTab & Form State - Advertisements (Reklamlar)
  const [adsSubTab, setAdsSubTab] = useState<'list' | 'add'>('list');
  const [editingAdId, setEditingAdId] = useState<string | null>(null);
  const [adForm, setAdForm] = useState({
    companyName: '',
    logo: '',
    image: '',
    category: 'Restoran & Gıda',
    address: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    contactPerson: '',
    summary: '',
    content: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: true,
  });

  const handleSaveAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adForm.companyName || !adForm.phone) {
      alert('Lütfen en az firma adını ve telefon numarasını giriniz.');
      return;
    }

    if (editingAdId) {
      const updatedItem: AdvertisementItem = {
        id: editingAdId,
        companyName: adForm.companyName,
        logo: adForm.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=300&q=80',
        image: adForm.image || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80',
        category: adForm.category,
        address: adForm.address,
        phone: adForm.phone,
        whatsapp: adForm.whatsapp || adForm.phone,
        email: adForm.email,
        website: adForm.website,
        contactPerson: adForm.contactPerson,
        summary: adForm.summary || adForm.content.slice(0, 100),
        content: adForm.content || adForm.summary,
        startDate: adForm.startDate,
        endDate: adForm.endDate,
        isActive: adForm.isActive,
        createdAt: new Date().toISOString(),
        viewCount: ads.find((a) => a.id === editingAdId)?.viewCount || 0,
      };

      if (setAds) {
        setAds((prev) => prev.map((a) => (a.id === editingAdId ? updatedItem : a)));
      }
      try {
        await updateAdApi(editingAdId, updatedItem);
      } catch (err) {
        console.error('Update ad error:', err);
      }
      setEditingAdId(null);
      alert('Reklam başarıyla güncellendi!');
    } else {
      const newItem: AdvertisementItem = {
        id: `ad-${Date.now()}`,
        companyName: adForm.companyName,
        logo: adForm.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=300&q=80',
        image: adForm.image || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80',
        category: adForm.category,
        address: adForm.address,
        phone: adForm.phone,
        whatsapp: adForm.whatsapp || adForm.phone,
        email: adForm.email,
        website: adForm.website,
        contactPerson: adForm.contactPerson,
        summary: adForm.summary || adForm.content.slice(0, 100),
        content: adForm.content || adForm.summary,
        startDate: adForm.startDate,
        endDate: adForm.endDate,
        isActive: true,
        createdAt: new Date().toISOString(),
        viewCount: 0,
      };

      if (setAds) {
        setAds((prev) => [newItem, ...prev]);
      }
      try {
        await createAdApi(newItem);
      } catch (err) {
        console.error('Create ad error:', err);
      }
      alert('Yeni Reklam Başarıyla Eklendi ve Yayına Alındı (1 Aylık Süre Başladı)!');
    }

    setAdForm({
      companyName: '',
      logo: '',
      image: '',
      category: 'Restoran & Gıda',
      address: '',
      phone: '',
      whatsapp: '',
      email: '',
      website: '',
      contactPerson: '',
      summary: '',
      content: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: true,
    });
    setAdsSubTab('list');
  };

  const handleToggleAdStatus = async (ad: AdvertisementItem) => {
    const newActiveState = !ad.isActive;
    const newStartDate = new Date().toISOString().split('T')[0];
    const newEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const updatedAd: AdvertisementItem = {
      ...ad,
      isActive: newActiveState,
      startDate: newActiveState ? newStartDate : ad.startDate,
      endDate: newActiveState ? newEndDate : ad.endDate,
    };

    if (setAds) {
      setAds((prev) => prev.map((a) => (a.id === ad.id ? updatedAd : a)));
    }

    try {
      await updateAdApi(ad.id, updatedAd);
    } catch (err) {
      console.error('Toggle ad status error:', err);
    }

    if (newActiveState) {
      alert(`"${ad.companyName}" reklamı yeniden AKTİF yapıldı. 1 aylık yeni yayın süresi başlatıldı (${newStartDate} - ${newEndDate}).`);
    } else {
      alert(`"${ad.companyName}" reklamı PASİF moda alındı.`);
    }
  };

  const handleDeleteAd = async (id: string, name: string) => {
    if (confirm(`"${name}" reklamını silmek istediğinize emin misiniz?`)) {
      if (setAds) {
        setAds((prev) => prev.filter((a) => a.id !== id));
      }
      try {
        await deleteAdApi(id);
      } catch (err) {
        console.error('Delete ad error:', err);
      }
      alert('Reklam silindi.');
    }
  };

  // SubTab & Form State - Tickets (Bilet Yönetimi & QR Kontrol)
  const [ticketsSubTab, setTicketsSubTab] = useState<'bilet_olusturma' | 'bilet_siparisleri' | 'qr_kontrol'>('bilet_olusturma');
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [ticketForm, setTicketForm] = useState({
    eventName: '',
    ticketTitle: '',
    address: '',
    date: new Date().toISOString().split('T')[0],
    time: '19:00',
    price: 20,
    currency: '€',
    image: '',
    description: '',
    quota: 200,
    isActive: true,
  });

  // Ticket Orders Filter
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'suspended'>('all');

  // QR Scanner / Gate Validator State
  const [selectedGateEvent, setSelectedGateEvent] = useState<string>('');
  const [scannedQrInput, setScannedQrInput] = useState<string>('');
  const [qrValidationResult, setQrValidationResult] = useState<any>(null);
  const [isValidatingQr, setIsValidatingQr] = useState<boolean>(false);

  // Set default selected gate event when tickets exist
  useEffect(() => {
    if (eventTickets.length > 0 && !selectedGateEvent) {
      setSelectedGateEvent(eventTickets[0].eventName);
    }
  }, [eventTickets]);

  // Handle Event Ticket Creation / Edit
  const handleSaveEventTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketForm.eventName || !ticketForm.ticketTitle || !ticketForm.address) {
      alert('Lütfen Etkinlik İsmi, Bilet İsmi ve Adres alanlarını doldurunuz.');
      return;
    }

    if (editingTicketId) {
      const updatedItem: EventTicket = {
        id: editingTicketId,
        eventName: ticketForm.eventName,
        ticketTitle: ticketForm.ticketTitle,
        address: ticketForm.address,
        date: ticketForm.date,
        time: ticketForm.time,
        price: Number(ticketForm.price),
        currency: ticketForm.currency || '€',
        image: ticketForm.image || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80',
        description: ticketForm.description,
        quota: Number(ticketForm.quota),
        soldCount: eventTickets.find((t) => t.id === editingTicketId)?.soldCount || 0,
        isActive: ticketForm.isActive,
        createdAt: new Date().toISOString(),
      };

      if (setEventTickets) {
        setEventTickets((prev) => prev.map((t) => (t.id === editingTicketId ? updatedItem : t)));
      }
      try {
        await updateEventTicketApi(editingTicketId, updatedItem);
      } catch (err) {
        console.error('Update event ticket error:', err);
      }
      setEditingTicketId(null);
      alert('Etkinlik bileti başarıyla güncellendi!');
    } else {
      const newItem: EventTicket = {
        id: `ticket-${Date.now()}`,
        eventName: ticketForm.eventName,
        ticketTitle: ticketForm.ticketTitle,
        address: ticketForm.address,
        date: ticketForm.date,
        time: ticketForm.time,
        price: Number(ticketForm.price),
        currency: ticketForm.currency || '€',
        image: ticketForm.image || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80',
        description: ticketForm.description,
        quota: Number(ticketForm.quota),
        soldCount: 0,
        isActive: true,
        createdAt: new Date().toISOString(),
      };

      if (setEventTickets) {
        setEventTickets((prev) => [newItem, ...prev]);
      }
      try {
        await createEventTicketApi(newItem);
      } catch (err) {
        console.error('Create event ticket error:', err);
      }
      alert('Yeni Etkinlik Bileti Oluşturuldu ve Satışa Sunuldu!');
    }

    setTicketForm({
      eventName: '',
      ticketTitle: '',
      address: '',
      date: new Date().toISOString().split('T')[0],
      time: '19:00',
      price: 20,
      currency: '€',
      image: '',
      description: '',
      quota: 200,
      isActive: true,
    });
  };

  const handleDeleteEventTicket = async (id: string, name: string) => {
    if (confirm(`"${name}" etkinlik biletini silmek istediğinize emin misiniz?`)) {
      if (setEventTickets) {
        setEventTickets((prev) => prev.filter((t) => t.id !== id));
      }
      try {
        await deleteEventTicketApi(id);
      } catch (err) {
        console.error('Delete event ticket error:', err);
      }
      alert('Etkinlik bileti silindi.');
    }
  };

  // Ticket Purchase Order Approval Handlers
  const handleUpdatePurchaseStatus = async (
    purchase: UserTicketPurchase,
    newStatus: 'approved' | 'rejected' | 'suspended',
    rejectionReason?: string
  ) => {
    const updated: UserTicketPurchase = {
      ...purchase,
      status: newStatus,
      rejectionReason: rejectionReason || purchase.rejectionReason,
    };

    if (setUserPurchases) {
      setUserPurchases((prev) => prev.map((p) => (p.id === purchase.id ? updated : p)));
    }

    try {
      await updateTicketPurchaseApi(purchase.id, updated);
    } catch (err) {
      console.error('Update purchase status error:', err);
    }

    const statusNames = {
      approved: '✅ ONAYLANDI (Onaylı Biletler menüsüne taşındı)',
      rejected: '❌ REDDEDİLDİ (Reddedilen Biletler menüsüne taşındı)',
      suspended: '⏸️ ASKIYA ALINDI (Durdurulan Biletler menüsüne taşındı)',
    };

    alert(`Bilet Siparişi Durumu Güncellendi: ${statusNames[newStatus]}`);
  };

  const handleDeletePurchase = async (id: string) => {
    if (confirm('Bu bilet sipariş kaydını tamamen silmek istediğinize emin misiniz?')) {
      if (setUserPurchases) {
        setUserPurchases((prev) => prev.filter((p) => p.id !== id));
      }
      try {
        await deleteTicketPurchaseApi(id);
      } catch (err) {
        console.error('Delete purchase error:', err);
      }
      alert('Sipariş kaydı silindi.');
    }
  };

  // QR Validation Scan Handler at Gate
  const handleValidateQr = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!scannedQrInput.trim()) {
      alert('Lütfen taranacak bilet kodunu veya QR katarını giriniz.');
      return;
    }

    if (!selectedGateEvent) {
      alert('Lütfen önce üst kısımdan kapıda kontrol edilen etkinliği seçiniz.');
      return;
    }

    setIsValidatingQr(true);
    try {
      const res = await validateTicketQrApi(scannedQrInput, selectedGateEvent);
      setQrValidationResult(res);

      // If validation succeeded and marked as used, update local state
      if (res.success && res.valid && res.ticketDetails && setUserPurchases) {
        setUserPurchases((prev) =>
          prev.map((p) => (p.id === res.ticketDetails?.id ? { ...p, isUsed: true, usedAt: res.scannedAt } : p))
        );
      }
    } catch (err) {
      console.error('QR Validation error:', err);
      setQrValidationResult({
        success: false,
        valid: false,
        errorCode: 'ERROR',
        message: 'QR Kod sorgulanırken sunucu hatası oluştu.',
      });
    } finally {
      setIsValidatingQr(false);
    }
  };

  // Recharts Chart Mock Data
  const memberStatsChart = [
    { month: 'Oca', total: 1100, active: 1040 },
    { month: 'Şub', total: 1140, active: 1080 },
    { month: 'Mar', total: 1180, active: 1120 },
    { month: 'Nis', total: 1200, active: 1140 },
    { month: 'May', total: 1220, active: 1160 },
    { month: 'Haz', total: 1245, active: 1180 },
  ];

  const donationStatsChart = [
    { month: 'Oca', amount: 4200 },
    { month: 'Şub', amount: 6800 },
    { month: 'Mar', amount: 12400 },
    { month: 'Nis', amount: 8900 },
    { month: 'May', amount: 9800 },
    { month: 'Haz', amount: 12250 },
  ];

  const trafficStatsChart = [
    { day: 'Pzt', views: 1200 },
    { day: 'Sal', views: 1450 },
    { day: 'Çar', views: 1800 },
    { day: 'Per', views: 1600 },
    { day: 'Cum', views: 2400 },
    { day: 'Cmt', views: 3200 },
    { day: 'Paz', views: 2900 },
  ];

  const eventCategoryChart = [
    { name: 'Konser', value: 35 },
    { name: 'Seminer', value: 25 },
    { name: 'Atölye', value: 20 },
    { name: 'Sergi', value: 20 },
  ];
  const CHART_COLORS = ['#D4AF37', '#0B3D2E', '#166534', '#1e293b'];

  // Handlers
  const handleSaveLiveStream = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const computedEmbedUrl = parseStreamEmbedUrl(liveForm.youtubeUrl || liveForm.embedUrl);
    const updated: SiteSettings = {
      ...settings,
      liveStream: {
        ...settings.liveStream,
        isLive: true, // Automatically enable live stream so it appears on home page
        title: liveForm.title,
        description: liveForm.description,
        platform: liveForm.platform as any,
        youtubeUrl: liveForm.youtubeUrl,
        facebookUrl: liveForm.facebookUrl,
        vimeoUrl: liveForm.vimeoUrl,
        embedUrl: computedEmbedUrl || liveForm.embedUrl,
        coverImage: liveForm.coverImage,
        viewerCount: Number(liveForm.viewerCount),
        startDate: liveForm.startDate,
        startTime: liveForm.startTime,
        endDate: liveForm.endDate,
        endTime: liveForm.endTime,
        obsRtmpUrl: liveForm.obsRtmpUrl,
        obsStreamKey: liveForm.obsStreamKey,
      },
    };
    setSettings(updated);
    updateSettingsApi(updated).catch(() => {});
    alert('Canlı Yayın Linki Başarıyla Güncellendi ve Ana Sayfada Yayına Alındı!');
  };

  const handleCreateNews = async (e: React.FormEvent) => {
    e.preventDefault();
    const titleVal = newsForm.titleTR;
    const contentVal = newsForm.contentTR;

    if (editingNewsId) {
      const updatedItem: NewsItem = {
        id: editingNewsId,
        title: { TR: titleVal, KM: newsForm.titleKM || titleVal, FR: titleVal, DE: titleVal, EN: titleVal },
        category: newsForm.category,
        summary: { TR: contentVal.slice(0, 100), KM: contentVal.slice(0, 100), FR: contentVal.slice(0, 100), DE: contentVal.slice(0, 100), EN: contentVal.slice(0, 100) },
        content: { TR: contentVal, KM: contentVal, FR: contentVal, DE: contentVal, EN: contentVal },
        image: newsForm.coverImage,
        date: newsForm.publishDate,
        author: newsForm.author,
        tags: newsForm.tags.split(',').map((s) => s.trim()),
        isActive: newsForm.status === 'Yayında',
      };
      const updatedNews = news.map((item) => (item.id === editingNewsId ? updatedItem : item));
      setNews(updatedNews);
      try {
        await updateNewsApi(editingNewsId, updatedItem);
      } catch (err) {
        console.error('Update news error:', err);
      }
      setEditingNewsId(null);
      alert('Haber başarıyla güncellendi!');
    } else {
      const newItem: NewsItem = {
        id: `news-${Date.now()}`,
        title: { TR: titleVal, KM: newsForm.titleKM || titleVal, FR: titleVal, DE: titleVal, EN: titleVal },
        category: newsForm.category,
        summary: { TR: contentVal.slice(0, 100), KM: contentVal.slice(0, 100), FR: contentVal.slice(0, 100), DE: contentVal.slice(0, 100), EN: contentVal.slice(0, 100) },
        content: { TR: contentVal, KM: contentVal, FR: contentVal, DE: contentVal, EN: contentVal },
        image: newsForm.coverImage,
        date: newsForm.publishDate,
        author: newsForm.author,
        tags: newsForm.tags.split(',').map((s) => s.trim()),
        isActive: newsForm.status === 'Yayında',
      };
      setNews([newItem, ...news]);
      try {
        await createNewsApi(newItem);
      } catch (err) {
        console.error('Create news error:', err);
      }
      alert('Yeni Haber / Duyuru başarıyla eklendi!');
    }
    setNewsSubTab('list');
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const titleVal = eventForm.titleTR;
    const descVal = eventForm.descriptionTR;

    if (editingEventId) {
      const existing = events.find((item) => item.id === editingEventId);
      const updatedItem: EventItem = {
        id: editingEventId,
        title: { TR: titleVal, KM: eventForm.titleKM || titleVal, FR: titleVal, DE: titleVal, EN: titleVal },
        category: eventForm.category,
        date: eventForm.date,
        time: eventForm.time,
        location: existing?.location || 'Paris Dernek Salonu',
        address: eventForm.address,
        description: { TR: descVal, KM: descVal, FR: descVal, DE: descVal, EN: descVal },
        image: eventForm.photo,
        registeredCount: existing?.registeredCount || 0,
        maxCapacity: Number(eventForm.maxCapacity),
        isPast: false,
        isActive: true,
        coordinates: existing?.coordinates || { lat: 48.8566, lng: 2.3522 },
      };
      const updatedEvents = events.map((item) => (item.id === editingEventId ? updatedItem : item));
      setEvents(updatedEvents);
      try {
        await updateEventApi(editingEventId, updatedItem);
      } catch (err) {
        console.error('Update event error:', err);
      }
      setEditingEventId(null);
      alert('Etkinlik başarıyla güncellendi!');
    } else {
      const newEvt: EventItem = {
        id: `evt-${Date.now()}`,
        title: { TR: titleVal, KM: eventForm.titleKM || titleVal, FR: titleVal, DE: titleVal, EN: titleVal },
        category: eventForm.category,
        date: eventForm.date,
        time: eventForm.time,
        location: 'Paris Dernek Salonu',
        address: eventForm.address,
        description: { TR: descVal, KM: descVal, FR: descVal, DE: descVal, EN: descVal },
        image: eventForm.photo,
        registeredCount: 0,
        maxCapacity: Number(eventForm.maxCapacity),
        isPast: false,
        isActive: true,
        coordinates: { lat: 48.8566, lng: 2.3522 },
      };
      setEvents([newEvt, ...events]);
      try {
        await createEventApi(newEvt);
      } catch (err) {
        console.error('Create event error:', err);
      }
      alert('Yeni Etkinlik Kaydedildi!');
    }
    setEventsSubTab('list');
  };

  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    const newMem: MemberUser = {
      id: `user-${Date.now()}`,
      memberNo: `KKDD-2026-0${members.length + 10}`,
      fullName: memberForm.fullName,
      email: memberForm.email,
      phone: memberForm.phone,
      role: memberForm.role,
      status: 'active',
      joinDate: new Date().toISOString().split('T')[0],
      avatar: memberForm.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=300&q=80',
      address: memberForm.address,
      city: memberForm.city,
      country: memberForm.country,
      duesStatus: 'up_to_date',
      totalDonations: 0,
    };
    setMembers([newMem, ...members]);
    try {
      await createMemberApi(newMem);
    } catch (err) {
      console.error('Create member error:', err);
    }
    alert('Yeni Üye Eklendi!');
    setMembersSubTab('list');
  };

  const handleCreateDonation = async (e: React.FormEvent) => {
    e.preventDefault();
    const newDon: DonationRecord = {
      id: `don-${Date.now()}`,
      donorName: donationForm.donorName,
      donorEmail: donationForm.donorEmail,
      amount: Number(donationForm.amount),
      currency: donationForm.currency,
      campaign: donationForm.campaign,
      date: new Date().toISOString().split('T')[0],
      paymentMethod: donationForm.paymentMethod,
      receiptNo: `RCP-2026-${Math.floor(1000 + Math.random() * 9000)}`,
      verificationStatus: 'Approved',
      status: 'Completed',
    };
    setDonations([newDon, ...donations]);
    try {
      await createDonationApi(newDon);
    } catch (err) {
      console.error('Create donation error:', err);
    }
    alert('Bağış Kaydı Oluşturuldu!');
    setDonationsSubTab('list');
  };

  const handleSavePosSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    const updatedSettings: SiteSettings = {
      ...settings,
      paymentGateway: {
        ...posForm,
        updatedAt: new Date().toISOString().split('T')[0],
      },
      donationCampaigns: campaigns,
    };
    setSettings(updatedSettings);
    try {
      await updateSettingsApi(updatedSettings);
    } catch {
      // client-side state updated
    }
    alert('💳 Kredi kartı Sanal POS & Banka Gateway entegrasyon bilgileri başarıyla güncellendi ve kaydedildi!');
  };

  const handleSaveCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignForm.title) {
      alert('Lütfen kampanya / fon başlığını giriniz.');
      return;
    }
    let updated: DonationCampaign[];
    if (editingCampaign) {
      updated = campaigns.map(c => c.id === editingCampaign.id ? { ...c, ...campaignForm } as DonationCampaign : c);
    } else {
      const newCamp: DonationCampaign = {
        id: 'camp-' + Date.now(),
        title: campaignForm.title || 'Yeni Bağış Fonu',
        category: campaignForm.category || 'Genel',
        description: campaignForm.description || '',
        targetAmount: Number(campaignForm.targetAmount) || 10000,
        collectedAmount: Number(campaignForm.collectedAmount) || 0,
        isActive: campaignForm.isActive !== false,
        createdAt: new Date().toISOString().split('T')[0],
      };
      updated = [newCamp, ...campaigns];
    }
    setCampaigns(updated);
    const updatedSettings: SiteSettings = {
      ...settings,
      donationCampaigns: updated,
      paymentGateway: posForm,
    };
    setSettings(updatedSettings);
    try {
      await updateSettingsApi(updatedSettings);
    } catch {}
    setIsCampaignModalOpen(false);
    setEditingCampaign(null);
    setCampaignForm({ title: '', category: 'Eğitim & Dil', description: '', targetAmount: 10000, collectedAmount: 0, isActive: true });
    alert('🎯 Bağış Fonu / Seçeneği başarıyla kaydedildi!');
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!confirm('Bu bağış fonunu silmek istediğinize emin misiniz?')) return;
    const updated = campaigns.filter(c => c.id !== id);
    setCampaigns(updated);
    const updatedSettings: SiteSettings = {
      ...settings,
      donationCampaigns: updated,
    };
    setSettings(updatedSettings);
    try {
      await updateSettingsApi(updatedSettings);
    } catch {}
  };

  const handleApproveDonation = async (id: string) => {
    const targetDonation = donations.find(d => d.id === id);
    const updatedItem = {
      ...targetDonation,
      verificationStatus: 'Approved' as const,
      status: 'Completed' as const,
      verifiedAt: new Date().toISOString(),
      verificationNotes: 'Dernek Yönetim Kurulu Tarafından Onaylandı ve Resmi Makbuz Düzenlendi.',
    };
    const updated = donations.map((d) => d.id === id ? updatedItem : d);
    try {
      await updateDonationApi(id, updatedItem);
    } catch {}
    setDonations(updated);
    if (onShowToast) {
      onShowToast(
        `${targetDonation?.donorName || 'Bağışçı'} kişisinin €${targetDonation?.amount || ''} tutarındaki bağışı Yönetim Kurulu tarafından onaylandı!`,
        'success',
        'Yönetim Onayı Başarılı'
      );
    }
  };

  const handleRejectDonation = async (id: string) => {
    const targetDonation = donations.find(d => d.id === id);
    const updatedItem = {
      ...targetDonation,
      verificationStatus: 'Rejected' as const,
      status: 'Cancelled' as const,
      verificationNotes: 'Dernek Yönetim Kurulu Tarafından İsim Eşleşmeme / Şüphe Nedeniyle Reddedildi.',
    };
    const updated = donations.map((d) => d.id === id ? updatedItem : d);
    try {
      await updateDonationApi(id, updatedItem);
    } catch {}
    setDonations(updated);
    if (onShowToast) {
      onShowToast(
        `${targetDonation?.donorName || 'Bağışçı'} kişisinin bağışı Yönetim Kurulu kararıyla reddedildi ve iptal edildi.`,
        'warning',
        'İşlem Reddedildi'
      );
    }
  };

  const handleToggleCampaignActive = async (id: string) => {
    const updated = campaigns.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c);
    setCampaigns(updated);
    const updatedSettings: SiteSettings = {
      ...settings,
      donationCampaigns: updated,
    };
    setSettings(updatedSettings);
    try {
      await updateSettingsApi(updatedSettings);
    } catch {}
  };

  const handleSaveSiteSettings = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const updated: SiteSettings = {
      ...settings,
      siteName: siteSettingsForm.siteName,
      subTitle: siteSettingsForm.subTitle,
      logoText1: siteSettingsForm.logoText1,
      logoText2: siteSettingsForm.logoText2,
      logoText3: siteSettingsForm.logoText3,
      logoImageUrl: siteSettingsForm.logoImageUrl,
      address: siteSettingsForm.address,
      phone: siteSettingsForm.phone,
      whatsapp: siteSettingsForm.whatsapp,
      email: siteSettingsForm.email,
      social: {
        facebook: siteSettingsForm.facebook,
        instagram: siteSettingsForm.instagram,
        youtube: siteSettingsForm.youtube,
        twitter: siteSettingsForm.twitter,
        linkedin: siteSettingsForm.linkedin,
      },
      maintenanceMode: siteSettingsForm.maintenanceMode,
    };
    setSettings(updated);
    try {
      await updateSettingsApi(updated);
    } catch {
      // API call attempted
    }
    setSettingsSavedToast(true);
    setTimeout(() => setSettingsSavedToast(false), 4000);
  };

  const handleCreatePhoto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!photoForm.url) {
      alert('Lütfen resim adresi (URL) girin!');
      return;
    }
    const newPhoto: MediaGalleryItem = {
      id: `gal-${Date.now()}`,
      type: 'photo',
      title: {
        TR: photoForm.titleTR || 'Yeni Resim',
        KM: photoForm.titleKM || photoForm.titleTR || 'Wêneya Nû',
        FR: photoForm.titleTR || 'Nouvelle Image',
        DE: photoForm.titleTR || 'Neues Bild',
        EN: photoForm.titleTR || 'New Image',
      },
      url: photoForm.url,
      thumbnail: photoForm.url,
      category: photoForm.category,
      date: photoForm.date,
      tags: photoForm.tags.split(',').map((t) => t.trim()),
    };
    try {
      await createGalleryApi(newPhoto);
    } catch {}
    setGallery([newPhoto, ...gallery]);
    alert('Fotoğraf Galerisine Başarıyla Eklendi ve Tüm Sitede Yayına Alındı!');
    setPhotoForm({ titleTR: '', titleKM: '', url: '', category: 'Kültür Şenliği', date: new Date().toISOString().split('T')[0], tags: 'Resim, Şenlik' });
    setGallerySubTab('photos');
  };

  const handleCreateVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoForm.videoUrl) {
      alert('Lütfen geçerli bir YouTube/Vimeo video linki girin!');
      return;
    }
    const embedUrl = parseStreamEmbedUrl(videoForm.videoUrl);
    const newVideo: MediaGalleryItem = {
      id: `vid-${Date.now()}`,
      type: 'video',
      title: {
        TR: videoForm.titleTR || 'Yeni Video Kaydı',
        KM: videoForm.titleKM || videoForm.titleTR || 'Vîdeoya Nû',
        FR: videoForm.titleTR || 'Nouvelle Vidéo',
        DE: videoForm.titleTR || 'Neues Video',
        EN: videoForm.titleTR || 'New Video',
      },
      url: embedUrl || videoForm.videoUrl,
      thumbnail: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=600&q=80',
      category: videoForm.category,
      date: videoForm.date,
      tags: ['Video', 'Medya'],
    };
    try {
      await createGalleryApi(newVideo);
    } catch {}
    setGallery([newVideo, ...gallery]);
    alert('Video Galerisine Başarıyla Eklendi!');
    setVideoForm({ titleTR: '', titleKM: '', videoUrl: '', category: 'Dengbêj & Müzik', date: new Date().toISOString().split('T')[0], description: '' });
    setGallerySubTab('videos');
  };

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    const newDoc: DocumentItem = {
      id: `doc-${Date.now()}`,
      title: { TR: documentForm.titleTR, KM: documentForm.titleTR, FR: documentForm.titleTR, DE: documentForm.titleTR, EN: documentForm.titleTR },
      category: documentForm.category,
      fileUrl: documentForm.fileUrl || '#',
      fileType: documentForm.fileType,
      fileSize: documentForm.fileSize,
      date: new Date().toISOString().split('T')[0],
      downloadCount: 0,
    };
    try {
      await createDocumentApi(newDoc);
    } catch {}
    setDocuments([newDoc, ...documents]);
    alert('Yeni Evrak/Doküman Depoya Yüklendi ve Kaydedildi!');
    setDocumentForm({ titleTR: '', category: 'Tüzük & Yönetmelik', fileUrl: '', fileType: 'PDF', fileSize: '1.8 MB' });
  };

  const handleCreateCultureItem = async (e: React.FormEvent) => {
    e.preventDefault();
    const newCul: CultureItem = {
      id: `cul-${Date.now()}`,
      title: { TR: cultureForm.titleTR, KM: cultureForm.titleKM || cultureForm.titleTR, FR: cultureForm.titleTR, DE: cultureForm.titleTR, EN: cultureForm.titleTR },
      category: cultureForm.category as any,
      description: { TR: cultureForm.contentTR.slice(0, 120), KM: (cultureForm.contentKM || cultureForm.contentTR).slice(0, 120), FR: cultureForm.contentTR.slice(0, 120), DE: cultureForm.contentTR.slice(0, 120), EN: cultureForm.contentTR.slice(0, 120) },
      content: { TR: cultureForm.contentTR, KM: cultureForm.contentKM || cultureForm.contentTR, FR: cultureForm.contentTR, DE: cultureForm.contentTR, EN: cultureForm.contentTR },
      image: cultureForm.mediaUrl || 'https://images.unsplash.com/photo-1541872703-74c5e44368f9?auto=format&fit=crop&q=80&w=800',
      mediaUrl: cultureForm.mediaUrl,
      date: new Date().toISOString().split('T')[0],
    };
    try {
      await createCultureApi(newCul);
    } catch {}
    setCultureItems([newCul, ...cultureItems]);
    alert('Yeni Kültür/Dil İçeriği Yayınlandı ve Kaydedildi!');
    setCultureForm({ titleTR: '', titleKM: '', category: 'Kürt Tarihi', contentTR: '', contentKM: '', mediaUrl: '' });
    setIsCultureAddOpen(false);
  };

  const handleCreateActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activityForm.titleTR) {
      alert('Lütfen faaliyet başlığını girin.');
      return;
    }
    const newAct: ActivityItem = {
      id: `act-${Date.now()}`,
      title: {
        TR: activityForm.titleTR,
        KM: activityForm.titleKM || activityForm.titleTR,
        FR: activityForm.titleTR,
        DE: activityForm.titleTR,
        EN: activityForm.titleTR,
      },
      category: activityForm.category,
      description: {
        TR: activityForm.descriptionTR,
        KM: activityForm.descriptionKM || activityForm.descriptionTR,
        FR: activityForm.descriptionTR,
        DE: activityForm.descriptionTR,
        EN: activityForm.descriptionTR,
      },
      date: activityForm.date,
      location: activityForm.location,
      image: activityForm.image || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=80',
      content: {
        TR: activityForm.descriptionTR,
        KM: activityForm.descriptionKM || activityForm.descriptionTR,
        FR: activityForm.descriptionTR,
        DE: activityForm.descriptionTR,
        EN: activityForm.descriptionTR,
      },
      participantsCount: 0,
      isActive: true,
    };
    try {
      await createActivityApi(newAct);
    } catch {}
    setActivities([newAct, ...activities]);
    if (activityForm.fileUrl) {
      setActivityFiles((prev) => [
        {
          id: `af-${Date.now()}`,
          name: activityForm.fileName || 'Faaliyet_Belgesi.pdf',
          activityTitle: activityForm.titleTR,
          size: '1.5 MB',
          date: activityForm.date,
          url: activityForm.fileUrl,
        },
        ...prev,
      ]);
    }
    alert(`Yeni Faaliyet "${activityForm.titleTR}" Başarıyla Eklendi ve Kaydedildi!`);
    setActivityForm({
      titleTR: '',
      titleKM: '',
      category: 'Kültür & Sanat',
      descriptionTR: '',
      descriptionKM: '',
      date: new Date().toISOString().split('T')[0],
      location: 'Paris Kültür Evi',
      image: '',
      fileUrl: '',
      fileName: '',
    });
    setActivitiesSubTab('list');
  };

  const handleAddActivityCategory = () => {
    if (!newActivityCatInput.trim()) return;
    if (!activityCategories.includes(newActivityCatInput.trim())) {
      setActivityCategories([...activityCategories, newActivityCatInput.trim()]);
    }
    setNewActivityCatInput('');
  };

  const handleDeleteActivityCategory = (cat: string) => {
    setActivityCategories(activityCategories.filter((c) => c !== cat));
  };

  const handleAddCultureCategory = () => {
    if (!newCultureCategoryInput.trim()) return;
    if (!cultureCategories.includes(newCultureCategoryInput.trim())) {
      setCultureCategories([...cultureCategories, newCultureCategoryInput.trim()]);
    }
    setNewCultureCategoryInput('');
  };

  const handleDeleteCultureCategory = (cat: string) => {
    setCultureCategories(cultureCategories.filter((c) => c !== cat));
  };

  const handleApproveApplication = async (appId: string) => {
    const app = memberApplications.find((a) => a.id === appId);
    if (!app) return;
    const newMember: MemberUser = {
      id: `mem-${Date.now()}`,
      memberNo: `KKDD-2026-${Math.floor(1000 + Math.random() * 9000)}`,
      fullName: app.fullName,
      email: app.email,
      phone: app.phone,
      role: 'member',
      status: 'active',
      avatar: `https://images.unsplash.com/photo-1535713875002?auto=format&fit=crop&w=300&q=80`,
      joinDate: new Date().toISOString().split('T')[0],
      address: 'Yvelines',
      city: 'Versailles',
      country: 'Fransa',
      duesStatus: 'up_to_date',
      totalDonations: 0,
    };
    try {
      await createMemberApi(newMember);
    } catch {}
    setMembers([newMember, ...members]);
    setMemberApplications(memberApplications.filter((a) => a.id !== appId));
    alert(`"${app.fullName}" isimli başvuru onaylandı ve dernek üyeliği aktif edildi!`);
  };

  const handleCreateGroup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName) return;
    setMemberGroups([
      ...memberGroups,
      {
        id: `grp-${Date.now()}`,
        name: newGroupName,
        leader: newGroupLeader || 'Atanmadı',
        memberCount: 1,
        description: newGroupDesc || 'Dernek bünyesindeki çalışma grubu.',
      },
    ]);
    setNewGroupName('');
    setNewGroupLeader('');
    setNewGroupDesc('');
    alert('Yeni Çalışma Grubu Oluşturuldu!');
  };

  const handleSendBroadcast = (e: React.FormEvent) => {
    e.preventDefault();
    setBroadcastLogs([
      {
        id: `log-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        channel: broadcastForm.channel,
        target: broadcastForm.targetGroup,
        status: 'Başarıyla İletildi (%100 Başarı)',
      },
      ...broadcastLogs,
    ]);
    alert(`Toplu Bildirim (${broadcastForm.channel}) "${broadcastForm.targetGroup}" alıcılarına başarıyla gönderildi!`);
  };

  return (
    <div className="space-y-6 pb-16 w-full max-w-full overflow-x-hidden">
      {/* ========================================================= */}
      {/* GRID BASED NAVIGATION SYSTEM (EXACT MATCH TO USER DESIGN) */}
      {/* ========================================================= */}

      {/* 1. TOP BREADCRUMB & LEVEL BACK NAVIGATION BAR */}
      {(viewLevel === 'sub_grid' || viewLevel === 'action' || activeTab !== 'dashboard') && (
        <div className="bg-[#0B1426] p-3.5 rounded-2xl border border-[#38BDF8]/40 shadow-xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (viewLevel === 'action') {
                  setViewLevel('sub_grid');
                } else {
                  setActiveTab('dashboard');
                  setViewLevel('main_grid');
                }
              }}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#132238] border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all font-bold text-xs shadow-md cursor-pointer active:scale-95"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{viewLevel === 'action' ? 'Modül Menüsüne Dön' : 'Yönetim Ana Sayfasına Dön'}</span>
            </button>

            <button
              onClick={() => {
                setActiveTab('dashboard');
                setViewLevel('main_grid');
              }}
              className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/40 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all cursor-pointer"
              title="Yönetim Ana Sayfası"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-[#7DD3FC]">
            <span className="opacity-60">Yönetim Paneli</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            <span className="uppercase text-[#38BDF8]">
              {mainGridModules.find((m) => m.id === activeTab)?.title || 'GÖSTERGE PANELİ'}
            </span>
          </div>
        </div>
      )}

      {/* 2. LEVEL 1: MAIN MODULES GRID (ANASAYFA MENÜ BASTIĞINDA AÇILAN GRID) */}
      {(activeTab === 'dashboard' || viewLevel === 'main_grid') && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-black text-[#7DD3FC] uppercase tracking-wider flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-[#38BDF8]" />
              <span>Yönetim Paneli Modülleri</span>
            </h2>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/30">
              12 Modül Aktif
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {mainGridModules.map((mod) => {
              const IconComp = mod.icon;

              return (
                <button
                  key={mod.id}
                  onClick={() => {
                    setActiveTab(mod.id as any);
                    setViewLevel('sub_grid');
                  }}
                  className="bg-[#0B1426] border border-[#38BDF8]/40 hover:border-[#38BDF8] rounded-2xl p-4 sm:p-5 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] transition-all cursor-pointer group text-left active:scale-95 min-h-[115px] sm:min-h-[130px] relative overflow-hidden"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#132238] border border-[#38BDF8]/40 flex items-center justify-center text-[#38BDF8] group-hover:scale-110 group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] transition-all shadow-md">
                      <IconComp className="w-5 h-5 stroke-[2.2]" />
                    </div>

                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide ${mod.badgeBg}`}>
                      {mod.badge}
                    </span>
                  </div>

                  <div className="mt-3">
                    <span className="text-xs sm:text-sm font-black text-white uppercase tracking-wider block group-hover:text-[#38BDF8] transition-colors line-clamp-1">
                      {mod.title}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
                      {mod.subtitle}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. LEVEL 2: SUB-MENU GRID (MODÜLE BASTIĞINDA AÇILAN ALT MENÜ GRID) */}
      {activeTab !== 'dashboard' && viewLevel === 'sub_grid' && (
        <div className="space-y-4 animate-in fade-in duration-300">
          {(() => {
            const currentMod = mainGridModules.find((m) => m.id === activeTab);
            if (!currentMod) return null;

            const IconMod = currentMod.icon;

            return (
              <>
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-black text-[#7DD3FC] uppercase tracking-wider flex items-center gap-2">
                    <IconMod className="w-4 h-4 text-[#38BDF8]" />
                    <span>{currentMod.title} - İşlem Menüsü</span>
                  </h2>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/30">
                    {currentMod.subItems.length} Seçenek
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {currentMod.subItems.map((sub) => {
                    const SubIcon = sub.icon;

                    return (
                      <button
                        key={sub.id}
                        onClick={() => {
                          sub.action();
                          setViewLevel('action');
                        }}
                        className="bg-[#0B1426] border border-[#38BDF8]/40 hover:border-[#38BDF8] rounded-2xl p-4 sm:p-5 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] transition-all cursor-pointer group text-left active:scale-95 min-h-[110px] sm:min-h-[125px] relative"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#132238] border border-[#38BDF8]/40 flex items-center justify-center text-[#38BDF8] group-hover:scale-110 group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] transition-all shadow-md">
                            <SubIcon className="w-5 h-5 stroke-[2.2]" />
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#38BDF8] group-hover:translate-x-1 transition-all" />
                        </div>

                        <div className="mt-3">
                          <span className="text-xs sm:text-sm font-black text-white uppercase tracking-wider block group-hover:text-[#38BDF8] transition-colors line-clamp-2">
                            {sub.title}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ========================================================= */}
      {/* 1. GÖSTERGE PANELİ (Dashboard) */}
      {/* ========================================================= */}
      {activeTab === 'dashboard' && (
        <div className="space-y-8 animate-in fade-in duration-300">
          {/* Top Metric Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
            <div className="glass-card p-4 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Toplam Üye</span>
              <p className="text-2xl font-extrabold text-white">{members.length + 1200}</p>
              <span className="text-[10px] text-emerald-400 font-semibold">↑ %8 Büyüme</span>
            </div>
            <div className="glass-card p-4 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Aktif Üye</span>
              <p className="text-2xl font-extrabold text-emerald-400">{members.length + 1140}</p>
              <span className="text-[10px] text-gray-400">Aidatı düzenli</span>
            </div>
            <div className="glass-card p-4 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Pasif Üye</span>
              <p className="text-2xl font-extrabold text-amber-400">60</p>
              <span className="text-[10px] text-amber-400/80">Yenileme bekleniyor</span>
            </div>
            <div className="glass-card p-4 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Bu Ay Kayıt</span>
              <p className="text-2xl font-extrabold text-white">+48</p>
              <span className="text-[10px] text-emerald-400 font-semibold">Son 30 gün</span>
            </div>
            <div className="glass-card p-4 rounded-2xl border border-white/10 space-y-1">
              <span className="text-[10px] font-bold text-gray-400 uppercase">Toplam Bağış</span>
              <p className="text-2xl font-extrabold text-[#D4AF37]">€54,350</p>
              <span className="text-[10px] text-emerald-400 font-semibold">Bu ay: €4,850</span>
            </div>
          </div>

          {/* System & Live Monitors Bar */}
          <div className="glass-card rounded-3xl p-5 border border-white/10 bg-gradient-to-r from-[#081B16] via-[#10241F] to-[#0B3D2E] space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                <Server className="w-4 h-4 text-[#D4AF37]" />
                <span>Canlı Sistem & Sunucu Durum Monitorü</span>
              </h3>
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                ● Sunucu Normal (UP)
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-center">
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Online Kullanıcı</span>
                <span className="text-lg font-extrabold text-emerald-400">42 Kişi</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Sunucu Erişimi</span>
                <span className="text-lg font-extrabold text-white">%99.9</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Disk Kullanımı</span>
                <span className="text-lg font-extrabold text-white">%24</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">RAM Kullanımı</span>
                <span className="text-lg font-extrabold text-white">%38</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">CPU Yükü</span>
                <span className="text-lg font-extrabold text-white">%12</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Günlük Ziyaretçi</span>
                <span className="text-lg font-extrabold text-[#D4AF37]">1,420</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Haftalık Trafik</span>
                <span className="text-lg font-extrabold text-white">9,850</span>
              </div>
              <div className="p-3 rounded-2xl bg-white/5 border border-white/5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase">Aylık Trafik</span>
                <span className="text-lg font-extrabold text-white">38,200</span>
              </div>
            </div>
          </div>

          {/* Dynamic Recharts Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 1. Üye İstatistikleri */}
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center justify-between">
                <span>Üye Büyüme Trend İstatistiği</span>
                <span className="text-xs text-[#D4AF37]">2026 Grafiği</span>
              </h3>
              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={memberStatsChart}>
                    <defs>
                      <linearGradient id="memColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0B3D2E" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#0B3D2E" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
                    <YAxis stroke="#a1a1aa" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: '#081B16', borderColor: '#D4AF37', borderRadius: '12px' }} />
                    <Area type="monotone" dataKey="total" stroke="#D4AF37" fillOpacity={1} fill="url(#memColor)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 2. Bağış Grafikleri */}
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center justify-between">
                <span>Aylık Toplanan Bağış Miktarı (€)</span>
                <span className="text-xs text-emerald-400">Hedef %120 Aşımı</span>
              </h3>
              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={donationStatsChart}>
                    <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
                    <YAxis stroke="#a1a1aa" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: '#081B16', borderColor: '#0B3D2E', borderRadius: '12px' }} />
                    <Bar dataKey="amount" fill="#D4AF37" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 3. Site Trafiği */}
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center justify-between">
                <span>Haftalık Site Trafiği & Ziyaret Sayıları</span>
                <span className="text-xs text-gray-400">Son 7 Gün</span>
              </h3>
              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficStatsChart}>
                    <XAxis dataKey="day" stroke="#a1a1aa" fontSize={11} />
                    <YAxis stroke="#a1a1aa" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: '#081B16', borderColor: '#38bdf8', borderRadius: '12px' }} />
                    <Line type="monotone" dataKey="views" stroke="#38bdf8" strokeWidth={3} dot={{ fill: '#38bdf8' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 4. Etkinlik Kategori Dağılımı */}
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center justify-between">
                <span>Etkinlik Türü Dağılım Oranı</span>
                <span className="text-xs text-[#D4AF37]">%100 Toplam</span>
              </h3>
              <div className="h-60 w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={eventCategoryChart}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {eventCategoryChart.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#081B16', borderColor: '#D4AF37', borderRadius: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Tables Preview: Son Giriş Yapanlar, Son Eklenen Haberler, Yaklaşan Etkinlikler, Son Faaliyetler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card rounded-3xl p-5 border border-white/10 space-y-3">
              <h4 className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                <span>Son Giriş Yapan Üyeler</span>
              </h4>
              <div className="divide-y divide-white/5 text-xs text-gray-300">
                {members.slice(0, 4).map((m) => (
                  <div key={m.id} className="py-2 flex items-center justify-between">
                    <span className="font-bold text-white">{m.fullName}</span>
                    <span className="text-[10px] text-emerald-400">Çevrimiçi • {m.memberNo}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card rounded-3xl p-5 border border-white/10 space-y-3">
              <h4 className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                <Newspaper className="w-4 h-4" />
                <span>Son Eklenen Haberler</span>
              </h4>
              <div className="divide-y divide-white/5 text-xs text-gray-300">
                {news.slice(0, 4).map((n) => (
                  <div key={n.id} className="py-2 flex items-center justify-between">
                    <span className="font-bold text-white truncate max-w-xs">{n.title[currentLang] || n.title.TR}</span>
                    <span className="text-[10px] text-gray-400">{n.date}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 2. HABER & DUYURULAR */}
      {/* ========================================================= */}
      {activeTab === 'news' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Sub Navigation Bar */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { id: 'list', label: 'Haber Listesi' },
              { id: 'add', label: 'Haber Ekle' },
              { id: 'drafts', label: 'Taslak Haberler' },
              { id: 'published', label: 'Yayında Olanlar' },
              { id: 'archive', label: 'Arşiv' },
              { id: 'categories', label: 'Kategori Yönetimi' },
              { id: 'tags', label: 'Etiket Yönetimi' },
              { id: 'authors', label: 'Yazar Yönetimi' },
              { id: 'seo', label: 'SEO Ayarları' },
              { id: 'comments', label: 'Yorum Yönetimi' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setNewsSubTab(st.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  newsSubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* Sub Tab Views */}
          {newsSubTab === 'add' ? (
            <form onSubmit={handleCreateNews} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-base font-bold text-white font-heading">
                  {editingNewsId ? 'Haberi Düzenle' : 'Yeni Haber / Duyuru Yayınla'}
                </h3>
                {editingNewsId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingNewsId(null);
                      setNewsSubTab('list');
                    }}
                    className="text-xs text-gray-400 hover:text-white"
                  >
                    İptal Et
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Haber Başlığı (Türkçe)</label>
                  <input
                    type="text"
                    required
                    value={newsForm.titleTR}
                    onChange={(e) => setNewsForm({ ...newsForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Haber Başlığı (Kürtçe / Kürtî)</label>
                  <input
                    type="text"
                    value={newsForm.titleKM}
                    onChange={(e) => setNewsForm({ ...newsForm, titleKM: e.target.value })}
                    placeholder="Sernav bi Kurdî"
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori</label>
                  <select
                    value={newsForm.category}
                    onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    <option value="Haber">Haber</option>
                    <option value="Duyurular">Duyurular</option>
                    <option value="Basın Açıklaması">Basın Açıklaması</option>
                    <option value="Kültür">Kültür</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Yazar</label>
                  <input
                    type="text"
                    value={newsForm.author}
                    onChange={(e) => setNewsForm({ ...newsForm, author: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <ImageUploadInput
                    label="Haber Kapak Fotoğrafı (Bilgisayar veya Telefondan Yükleyin)"
                    value={newsForm.coverImage}
                    onChange={(url) => setNewsForm({ ...newsForm, coverImage: url })}
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 block mb-1 font-bold">Detaylı Haber İçeriği</label>
                <textarea
                  rows={5}
                  required
                  value={newsForm.contentTR}
                  onChange={(e) => setNewsForm({ ...newsForm, contentTR: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
                <AiTextAssistant
                  fieldLabel="Haber İçeriği"
                  currentLang={currentLang}
                  onTransfer={(generated) => setNewsForm((prev) => ({ ...prev, contentTR: generated }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Video Linki</label>
                  <input
                    type="text"
                    placeholder="YouTube video URL"
                    value={newsForm.videoUrl}
                    onChange={(e) => setNewsForm({ ...newsForm, videoUrl: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Etiketler</label>
                  <input
                    type="text"
                    value={newsForm.tags}
                    onChange={(e) => setNewsForm({ ...newsForm, tags: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Durum</label>
                  <select
                    value={newsForm.status}
                    onChange={(e) => setNewsForm({ ...newsForm, status: e.target.value as any })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    <option value="Yayında">Yayında</option>
                    <option value="Taslak">Taslak</option>
                    <option value="Arşiv">Arşiv</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary py-2.5 px-6">
                  <Save className="w-4 h-4" />
                  <span>{editingNewsId ? 'Değişiklikleri Kaydet' : 'Haberi Kaydet ve Yayınla'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingNewsId(null);
                    setNewsSubTab('list');
                  }}
                  className="btn-secondary py-2.5 px-4"
                >
                  İptal
                </button>
              </div>
            </form>
          ) : newsSubTab === 'categories' ? (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading">Haber Kategorileri Yönetimi</h3>
              <p className="text-gray-300">Sistem genelinde kullanılan haber ve duyuru kategorileri listesi.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {['Haber', 'Duyurular', 'Basın Açıklaması', 'Kültür', 'Etkinlik Raporu', 'Dernek Bülteni'].map((cat) => (
                  <div key={cat} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex justify-between items-center">
                    <span className="font-bold text-white">{cat}</span>
                    <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">Aktif</span>
                  </div>
                ))}
              </div>
            </div>
          ) : newsSubTab === 'tags' ? (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading">Haber Etiket Yönetimi</h3>
              <div className="flex flex-wrap gap-2">
                {['#Dernek', '#Kültür', '#Dil', '#Nevroz', '#Edebiyat', '#Fransa', '#Paris', '#Gençlik', '#Sanat'].map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/20 text-white font-bold">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            /* Default: Haber Listesi Table with Horizontal Scroll, Row Expansion & Edit */
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <h3 className="text-sm font-bold text-white font-heading">
                    {newsSubTab === 'drafts' ? 'Taslak Haberler' : newsSubTab === 'archive' ? 'Haber Arşivi' : 'Tüm Haber & Duyurular'}{' '}
                    ({news.length} İçerik)
                  </h3>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Haber ayrıntılarını görmek için <strong>[👁️ Genişlet]</strong> butonuna basabilirsiniz.
                  </p>
                </div>
                <button onClick={() => {
                  setEditingNewsId(null);
                  setNewsForm({
                    titleTR: '',
                    titleKM: '',
                    coverImage: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=800&q=80',
                    gallery: '',
                    contentTR: '',
                    videoUrl: '',
                    fileUrl: '',
                    category: 'Haber',
                    tags: 'Dernek, Kültür',
                    publishDate: new Date().toISOString().split('T')[0],
                    seoTitle: '',
                    seoDescription: '',
                    status: 'Yayında',
                    author: 'Yönetim Kurulu',
                  });
                  setNewsSubTab('add');
                }} className="btn-primary text-xs py-2 px-4 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span>Yeni Haber Ekle</span>
                </button>
              </div>

              {/* BOUNDED HORIZONTALLY SCROLLABLE TABLE */}
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs text-gray-300 min-w-[750px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Kapak & Başlık</th>
                      <th className="p-3">Kategori</th>
                      <th className="p-3">Tarih</th>
                      <th className="p-3">Yazar</th>
                      <th className="p-3">Durum</th>
                      <th className="p-3 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {news
                      .filter((n) => {
                        if (newsSubTab === 'drafts') return !n.isActive;
                        if (newsSubTab === 'published') return n.isActive;
                        return true;
                      })
                      .map((n) => {
                        const isExpanded = expandedNewsId === n.id;
                        return (
                          <React.Fragment key={n.id}>
                            <tr className={`hover:bg-white/5 transition-colors ${isExpanded ? 'bg-white/5' : ''}`}>
                              <td className="p-3 flex items-center gap-3">
                                <img src={n.image} alt="News" className="w-12 h-12 rounded-xl object-cover border border-white/10 shrink-0" />
                                <div>
                                  <span className="font-bold text-white line-clamp-1">{n.title[currentLang] || n.title.TR}</span>
                                  {n.title.KM && <span className="text-[10px] text-gray-400 block truncate">{n.title.KM}</span>}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="px-2.5 py-1 rounded-lg bg-[#0B3D2E] text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/30">
                                  {n.category}
                                </span>
                              </td>
                              <td className="p-3 whitespace-nowrap">{n.date}</td>
                              <td className="p-3 whitespace-nowrap">{n.author || 'Yönetim Kurulu'}</td>
                              <td className="p-3 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  n.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {n.isActive ? 'Yayında' : 'Taslak'}
                                </span>
                              </td>
                              <td className="p-3 text-right whitespace-nowrap space-x-2">
                                <button
                                  onClick={() => setExpandedNewsId(isExpanded ? null : n.id)}
                                  className="px-2.5 py-1 rounded-lg bg-blue-950/80 hover:bg-blue-900 border border-blue-500/40 text-blue-300 font-bold text-xs"
                                >
                                  {isExpanded ? 'Kapat' : '👁️ Genişlet'}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingNewsId(n.id);
                                    setNewsForm({
                                      titleTR: n.title.TR || '',
                                      titleKM: n.title.KM || '',
                                      coverImage: n.image || '',
                                      gallery: '',
                                      contentTR: n.content.TR || '',
                                      videoUrl: '',
                                      fileUrl: '',
                                      category: n.category || 'Haber',
                                      tags: n.tags ? n.tags.join(', ') : 'Dernek',
                                      publishDate: n.date || new Date().toISOString().split('T')[0],
                                      seoTitle: '',
                                      seoDescription: '',
                                      status: n.isActive ? 'Yayında' : 'Taslak',
                                      author: n.author || 'Yönetim Kurulu',
                                    });
                                    setNewsSubTab('add');
                                  }}
                                  className="px-2.5 py-1 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-300 font-bold text-xs"
                                >
                                  ✏️ Düzenle
                                </button>
                                <button
                                  onClick={async () => {
                                    if (confirm('Bu haberi silmek istediğinize emin misiniz?')) {
                                      setNews(news.filter((x) => x.id !== n.id));
                                      try {
                                        await deleteNewsApi(n.id);
                                      } catch (err) {
                                        console.error('Delete news error:', err);
                                      }
                                    }
                                  }}
                                  className="px-2.5 py-1 rounded-lg bg-red-950/80 hover:bg-red-900 border border-red-500/40 text-red-300 font-bold text-xs"
                                >
                                  🗑️ Sil
                                </button>
                              </td>
                            </tr>

                            {/* EXPANDABLE DETAIL CARD ROW */}
                            {isExpanded && (
                              <tr className="bg-[#081B16] border-b border-white/10">
                                <td colSpan={6} className="p-4">
                                  <div className="glass-card rounded-2xl p-5 border border-[#D4AF37]/30 bg-black/40 space-y-4">
                                    <div className="flex flex-col md:flex-row gap-4 items-start">
                                      <img src={n.image} alt="Full Cover" className="w-full md:w-64 h-40 rounded-xl object-cover border border-white/10 shrink-0" />
                                      <div className="space-y-2 flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="px-2.5 py-0.5 rounded bg-[#0B3D2E] text-[#D4AF37] text-xs font-bold">
                                            {n.category}
                                          </span>
                                          <span className="text-xs text-gray-400">📅 {n.date}</span>
                                          <span className="text-xs text-gray-400">✍️ {n.author}</span>
                                        </div>
                                        <h4 className="text-base font-bold text-white font-heading">{n.title.TR}</h4>
                                        {n.title.KM && <p className="text-xs font-bold text-[#D4AF37]">{n.title.KM}</p>}
                                        <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{n.content.TR}</p>
                                        {n.tags && n.tags.length > 0 && (
                                          <div className="flex flex-wrap gap-1.5 pt-2">
                                            {n.tags.map((t) => (
                                              <span key={t} className="px-2 py-0.5 rounded bg-white/10 text-[10px] text-gray-300">
                                                #{t}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                                      <button
                                        onClick={() => {
                                          setEditingNewsId(n.id);
                                          setNewsForm({
                                            titleTR: n.title.TR || '',
                                            titleKM: n.title.KM || '',
                                            coverImage: n.image || '',
                                            gallery: '',
                                            contentTR: n.content.TR || '',
                                            videoUrl: '',
                                            fileUrl: '',
                                            category: n.category || 'Haber',
                                            tags: n.tags ? n.tags.join(', ') : 'Dernek',
                                            publishDate: n.date || new Date().toISOString().split('T')[0],
                                            seoTitle: '',
                                            seoDescription: '',
                                            status: n.isActive ? 'Yayında' : 'Taslak',
                                            author: n.author || 'Yönetim Kurulu',
                                          });
                                          setNewsSubTab('add');
                                        }}
                                        className="btn-primary py-1.5 px-3 text-xs"
                                      >
                                        ✏️ Bu Haberi Düzenle
                                      </button>
                                      <button
                                        onClick={() => setExpandedNewsId(null)}
                                        className="btn-secondary py-1.5 px-3 text-xs"
                                      >
                                        Görünümü Kapat
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 3. ETKİNLİKLER */}
      {/* ========================================================= */}
      {activeTab === 'events' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { id: 'list', label: 'Etkinlik Listesi' },
              { id: 'add', label: 'Etkinlik Ekle' },
              { id: 'calendar', label: 'Takvim Görünümü' },
              { id: 'attendees', label: 'Katılımcılar' },
              { id: 'qrcode', label: 'QR Kod Giriş' },
              { id: 'quota', label: 'Kontenjan Yönetimi' },
              { id: 'map', label: 'Harita Konumu' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setEventsSubTab(st.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  eventsSubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {eventsSubTab === 'add' ? (
            <form onSubmit={handleCreateEvent} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <h3 className="text-base font-bold text-white font-heading">
                  {editingEventId ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Kaydı Oluştur'}
                </h3>
                {editingEventId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEventId(null);
                      setEventsSubTab('list');
                    }}
                    className="text-xs text-gray-400 hover:text-white"
                  >
                    İptal Et
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Etkinlik Başlığı (Türkçe)</label>
                  <input
                    type="text"
                    required
                    value={eventForm.titleTR}
                    onChange={(e) => setEventForm({ ...eventForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Etkinlik Başlığı (Kürtçe)</label>
                  <input
                    type="text"
                    value={eventForm.titleKM}
                    onChange={(e) => setEventForm({ ...eventForm, titleKM: e.target.value })}
                    placeholder="Sernav bi Kurdî"
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Tarih & Saat</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                      className="w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                    <input
                      type="text"
                      value={eventForm.time}
                      onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                      className="w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori</label>
                  <select
                    value={eventForm.category}
                    onChange={(e) => setEventForm({ ...eventForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                  >
                    <option value="Konser">Konser & Müzik</option>
                    <option value="Konferans">Konferans & Seminer</option>
                    <option value="Tiyatro">Tiyatro & Gösteri</option>
                    <option value="Şenlik">Şenlik & Festival</option>
                    <option value="Atölye">Atölye & Kurs</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Açık Adres & Salon</label>
                  <input
                    type="text"
                    value={eventForm.address}
                    onChange={(e) => setEventForm({ ...eventForm, address: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kontenjan Kapasitesi</label>
                  <input
                    type="number"
                    value={eventForm.maxCapacity}
                    onChange={(e) => setEventForm({ ...eventForm, maxCapacity: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                  />
                </div>
                <div className="sm:col-span-2">
                  <ImageUploadInput
                    label="Etkinlik Afişi / Fotoğrafı (Bilgisayar veya Telefondan Yükleyin)"
                    value={eventForm.photo}
                    onChange={(url) => setEventForm({ ...eventForm, photo: url })}
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 block mb-1 font-bold">Etkinlik Açıklaması & Program Akışı</label>
                <textarea
                  rows={4}
                  value={eventForm.descriptionTR}
                  onChange={(e) => setEventForm({ ...eventForm, descriptionTR: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
                <AiTextAssistant
                  fieldLabel="Etkinlik Açıklaması"
                  currentLang={currentLang}
                  onTransfer={(generated) => setEventForm((prev) => ({ ...prev, descriptionTR: generated }))}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary py-2.5 px-6">
                  <Save className="w-4 h-4" />
                  <span>{editingEventId ? 'Değişiklikleri Kaydet' : 'Etkinliği Kaydet'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingEventId(null);
                    setEventsSubTab('list');
                  }}
                  className="btn-secondary py-2.5 px-4"
                >
                  İptal
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h3 className="text-sm font-bold text-white font-heading">
                  Kayıtlı Etkinlikler Listesi ({events.length} Etkinlik)
                </h3>
                <button
                  onClick={() => {
                    setEditingEventId(null);
                    setEventForm({
                      titleTR: '',
                      titleKM: '',
                      descriptionTR: '',
                      time: '19:00',
                      date: new Date().toISOString().split('T')[0],
                      address: '15 Rue de la Paix, Paris',
                      googleMapUrl: '',
                      photo: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=800&q=80',
                      videoUrl: '',
                      liveStreamUrl: '',
                      maxCapacity: 300,
                      category: 'Konser',
                    });
                    setEventsSubTab('add');
                  }}
                  className="btn-primary text-xs py-2 px-4 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Yeni Etkinlik Ekle</span>
                </button>
              </div>

              {/* BOUNDED HORIZONTALLY SCROLLABLE TABLE */}
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs text-gray-300 min-w-[750px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Afiş & Başlık</th>
                      <th className="p-3">Kategori</th>
                      <th className="p-3">Tarih & Saat</th>
                      <th className="p-3">Adres</th>
                      <th className="p-3">Kontenjan</th>
                      <th className="p-3 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {events.map((evt) => {
                      const isExpanded = expandedEventId === evt.id;
                      return (
                        <React.Fragment key={evt.id}>
                          <tr className={`hover:bg-white/5 transition-colors ${isExpanded ? 'bg-white/5' : ''}`}>
                            <td className="p-3 flex items-center gap-3">
                              <img src={evt.image} alt="Evt" className="w-12 h-12 rounded-xl object-cover border border-white/10 shrink-0" />
                              <div>
                                <span className="font-bold text-white line-clamp-1">{evt.title[currentLang] || evt.title.TR}</span>
                                {evt.title.KM && <span className="text-[10px] text-gray-400 block truncate">{evt.title.KM}</span>}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="px-2.5 py-1 rounded-lg bg-[#0B3D2E] text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/30">
                                {evt.category}
                              </span>
                            </td>
                            <td className="p-3 whitespace-nowrap">📅 {evt.date} ({evt.time})</td>
                            <td className="p-3 truncate max-w-xs">{evt.address}</td>
                            <td className="p-3 whitespace-nowrap">
                              <span className="font-bold text-emerald-400">{evt.registeredCount}</span> / {evt.maxCapacity}
                            </td>
                            <td className="p-3 text-right whitespace-nowrap space-x-2">
                              <button
                                onClick={() => setExpandedEventId(isExpanded ? null : evt.id)}
                                className="px-2.5 py-1 rounded-lg bg-blue-950/80 hover:bg-blue-900 border border-blue-500/40 text-blue-300 font-bold text-xs"
                              >
                                {isExpanded ? 'Kapat' : '👁️ Genişlet'}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingEventId(evt.id);
                                  setEventForm({
                                    titleTR: evt.title.TR || '',
                                    titleKM: evt.title.KM || '',
                                    descriptionTR: evt.description.TR || '',
                                    time: evt.time || '19:00',
                                    date: evt.date || '',
                                    address: evt.address || '',
                                    googleMapUrl: '',
                                    photo: evt.image || '',
                                    videoUrl: '',
                                    liveStreamUrl: '',
                                    maxCapacity: evt.maxCapacity || 300,
                                    category: evt.category || 'Konser',
                                  });
                                  setEventsSubTab('add');
                                }}
                                className="px-2.5 py-1 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-300 font-bold text-xs"
                              >
                                ✏️ Düzenle
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm('Bu etkinliği silmek istediğinize emin misiniz?')) {
                                    setEvents(events.filter((e) => e.id !== evt.id));
                                    try {
                                      await deleteEventApi(evt.id);
                                    } catch (err) {
                                      console.error('Delete event error:', err);
                                    }
                                  }
                                }}
                                className="px-2.5 py-1 rounded-lg bg-red-950/80 hover:bg-red-900 border border-red-500/40 text-red-300 font-bold text-xs"
                              >
                                🗑️ Sil
                              </button>
                            </td>
                          </tr>

                          {/* EXPANDABLE EVENT DETAIL ROW */}
                          {isExpanded && (
                            <tr className="bg-[#081B16] border-b border-white/10">
                              <td colSpan={6} className="p-4">
                                <div className="glass-card rounded-2xl p-5 border border-[#D4AF37]/30 bg-black/40 space-y-4">
                                  <div className="flex flex-col md:flex-row gap-4 items-start">
                                    <img src={evt.image} alt="Evt Cover" className="w-full md:w-64 h-40 rounded-xl object-cover border border-white/10 shrink-0" />
                                    <div className="space-y-2 flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="px-2.5 py-0.5 rounded bg-[#0B3D2E] text-[#D4AF37] text-xs font-bold">
                                          {evt.category}
                                        </span>
                                        <span className="text-xs text-emerald-400 font-bold">📅 {evt.date} - Saat: {evt.time}</span>
                                      </div>
                                      <h4 className="text-base font-bold text-white font-heading">{evt.title.TR}</h4>
                                      {evt.title.KM && <p className="text-xs font-bold text-[#D4AF37]">{evt.title.KM}</p>}
                                      <p className="text-xs text-gray-300 leading-relaxed">{evt.description.TR}</p>
                                      <p className="text-xs text-blue-300 font-mono">📍 Adres: {evt.address}</p>
                                      <div className="pt-2 flex items-center gap-4 text-xs">
                                        <span className="text-gray-400">Kayıtlı Katılımcı: <strong className="text-white">{evt.registeredCount} Kişi</strong></span>
                                        <span className="text-gray-400">Kontenjan Sınırı: <strong className="text-[#D4AF37]">{evt.maxCapacity} Kişi</strong></span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                                    <button
                                      onClick={() => {
                                        setEditingEventId(evt.id);
                                        setEventForm({
                                          titleTR: evt.title.TR || '',
                                          titleKM: evt.title.KM || '',
                                          descriptionTR: evt.description.TR || '',
                                          time: evt.time || '19:00',
                                          date: evt.date || '',
                                          address: evt.address || '',
                                          googleMapUrl: '',
                                          photo: evt.image || '',
                                          videoUrl: '',
                                          liveStreamUrl: '',
                                          maxCapacity: evt.maxCapacity || 300,
                                          category: evt.category || 'Konser',
                                        });
                                        setEventsSubTab('add');
                                      }}
                                      className="btn-primary py-1.5 px-3 text-xs"
                                    >
                                      ✏️ Etkinliği Düzenle
                                    </button>
                                    <button
                                      onClick={() => setExpandedEventId(null)}
                                      className="btn-secondary py-1.5 px-3 text-xs"
                                    >
                                      Kapat
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 4. FAALİYETLER */}
      {/* ========================================================= */}
      {activeTab === 'activities' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-2 scrollbar-none border-b border-white/10">
            {[
              { id: 'list', label: 'Faaliyet Listesi' },
              { id: 'add', label: 'Yeni Faaliyet' },
              { id: 'categories', label: 'Kategori Yönetimi' },
              { id: 'files', label: 'Dosyalar & Dokümanlar' },
              { id: 'gallery', label: 'Galeri' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setActivitiesSubTab(st.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  activitiesSubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* SubTab 1: Faaliyet Listesi */}
          {activitiesSubTab === 'list' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-[#D4AF37] font-heading">
                  Kayıtlı Dernek Faaliyetleri ({activities.length})
                </h3>
                <button
                  onClick={() => setActivitiesSubTab('add')}
                  className="btn-primary py-2 text-xs flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Yeni Faaliyet Ekle</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activities.map((act) => (
                  <div key={act.id} className="glass-card rounded-2xl p-4 border border-white/10 space-y-3 flex flex-col justify-between">
                    <div className="space-y-2">
                      <img src={act.image} alt={act.title.TR} className="w-full h-36 rounded-xl object-cover border border-white/10" />
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-0.5 rounded bg-[#0B3D2E] text-[#D4AF37] text-[10px] font-bold">
                          {act.category}
                        </span>
                        <span className="text-[10px] text-gray-400">📅 {act.date}</span>
                      </div>
                      <h4 className="font-bold text-white text-sm">{act.title[currentLang] || act.title.TR}</h4>
                      <p className="text-[11px] text-gray-300 line-clamp-3">{act.description[currentLang] || act.description.TR}</p>
                      {act.location && (
                        <p className="text-[10px] text-emerald-400">📍 {act.location}</p>
                      )}
                    </div>
                    <div className="pt-2 flex justify-between items-center text-xs border-t border-white/10">
                      <span className="text-[10px] text-gray-400">ID: {act.id}</span>
                      <button
                        onClick={async () => {
                          if (confirm('Bu faaliyeti silmek istediğinize emin misiniz?')) {
                            setActivities(activities.filter((a) => a.id !== act.id));
                            try {
                              await deleteActivityApi(act.id);
                            } catch (err) {
                              console.error('Delete activity error:', err);
                            }
                          }
                        }}
                        className="text-red-400 hover:text-red-300 font-bold text-xs flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Sil</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SubTab 2: Yeni Faaliyet Ekle Form */}
          {activitiesSubTab === 'add' && (
            <form onSubmit={handleCreateActivity} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <h3 className="text-base font-bold text-white font-heading border-b border-white/10 pb-3">
                Yeni Faaliyet Kaydı Oluştur
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Faaliyet Başlığı (Türkçe)</label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Kürt Dil ve Edebiyat Günleri"
                    value={activityForm.titleTR}
                    onChange={(e) => setActivityForm({ ...activityForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Faaliyet Başlığı (Kürtçe / Kürtî)</label>
                  <input
                    type="text"
                    placeholder="Örn: Rojên Ziman û Edebiyata Kurdî"
                    value={activityForm.titleKM}
                    onChange={(e) => setActivityForm({ ...activityForm, titleKM: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori</label>
                  <select
                    value={activityForm.category}
                    onChange={(e) => setActivityForm({ ...activityForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    {activityCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Tarih & Konum</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={activityForm.date}
                      onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })}
                      className="w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                    <input
                      type="text"
                      placeholder="Konum (Paris vb.)"
                      value={activityForm.location}
                      onChange={(e) => setActivityForm({ ...activityForm, location: e.target.value })}
                      className="w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload Input for PC or Phone */}
              <div className="pt-2 border-t border-white/10">
                <ImageUploadInput
                  label="Faaliyet Görseli / Kapak Fotoğrafı (Telefon veya Bilgisayardan Yükleyin)"
                  value={activityForm.image}
                  onChange={(url) => setActivityForm({ ...activityForm, image: url })}
                />
              </div>

              {/* Document File Attachment */}
              <div className="pt-2 border-t border-white/10 space-y-2">
                <label className="text-gray-300 block font-bold">Faaliyet İlgili Belge / Dosya Yükle (PDF / Broşür)</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="Dosya adı (Örn: Faaliyet_Programi.pdf)"
                    value={activityForm.fileName}
                    onChange={(e) => setActivityForm({ ...activityForm, fileName: e.target.value })}
                    className="sm:w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                  <input
                    type="text"
                    placeholder="Dosya indirme linki veya URL adresi"
                    value={activityForm.fileUrl}
                    onChange={(e) => setActivityForm({ ...activityForm, fileUrl: e.target.value })}
                    className="sm:w-1/2 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 block mb-1 font-bold">Detaylı Açıklama (Türkçe)</label>
                <textarea
                  rows={3}
                  value={activityForm.descriptionTR}
                  onChange={(e) => setActivityForm({ ...activityForm, descriptionTR: e.target.value })}
                  placeholder="Faaliyet özeti, hedefleri ve içeriği..."
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
              </div>

              <button type="submit" className="btn-primary py-2.5 w-full sm:w-auto">
                <Save className="w-4 h-4" />
                <span>Faaliyeti Kaydet ve Yayınla</span>
              </button>
            </form>
          )}

          {/* SubTab 3: Kategori Yönetimi */}
          {activitiesSubTab === 'categories' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading">
                Faaliyet Kategorileri Yönetimi
              </h3>

              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Yeni Kategori Adı Girin..."
                  value={newActivityCatInput}
                  onChange={(e) => setNewActivityCatInput(e.target.value)}
                  className="flex-1 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs"
                />
                <button
                  type="button"
                  onClick={handleAddActivityCategory}
                  className="btn-primary text-xs py-2.5 px-4"
                >
                  <Plus className="w-4 h-4" />
                  <span>Kategori Ekle</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {activityCategories.map((cat) => (
                  <div key={cat} className="p-3.5 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between">
                    <span className="font-bold text-white text-xs">{cat}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteActivityCategory(cat)}
                      className="text-red-400 hover:text-red-300 text-xs font-bold"
                    >
                      Sil
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SubTab 4: Dosyalar & Dokümanlar */}
          {activitiesSubTab === 'files' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white font-heading">Faaliyet Belge & Evrak Arşivi</h3>
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Belge Adı</th>
                      <th className="p-3">İlişkili Faaliyet</th>
                      <th className="p-3">Boyut</th>
                      <th className="p-3">Tarih</th>
                      <th className="p-3 text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {activityFiles.map((file) => (
                      <tr key={file.id} className="hover:bg-white/5">
                        <td className="p-3 font-bold text-white flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#D4AF37]" />
                          <span>{file.name}</span>
                        </td>
                        <td className="p-3 text-gray-300">{file.activityTitle}</td>
                        <td className="p-3 text-gray-400">{file.size}</td>
                        <td className="p-3 text-gray-400">{file.date}</td>
                        <td className="p-3 text-right">
                          <a href={file.url} download className="text-emerald-400 font-bold hover:underline">İndir</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 5: Galeri */}
          {activitiesSubTab === 'gallery' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white font-heading">Faaliyet Medya Galerisi</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {activities.map((act) => (
                  <div key={act.id} className="relative group rounded-2xl overflow-hidden border border-white/10">
                    <img src={act.image} alt="Act" className="w-full h-32 object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity p-2 flex flex-col justify-between text-[10px]">
                      <span className="font-bold text-white">{act.title[currentLang] || act.title.TR}</span>
                      <span className="text-[#D4AF37]">{act.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 5. KÜLTÜR & DİL */}
      {/* ========================================================= */}
      {activeTab === 'culture' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Action & Toggle Controls */}
          <div className="glass-card p-4 rounded-3xl border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-[#D4AF37] font-heading">
                Kürt Kültürü, Tarihi ve Dil Modülü
              </h3>
              <p className="text-[11px] text-gray-300">
                15 Farklı kategoriye özel ders, sözlük, alfabe, dengbêj ve edebiyat içerikleri.
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsCultureAddOpen(!isCultureAddOpen)}
                className="btn-primary text-xs py-2 px-3 flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4" />
                <span>{isCultureAddOpen ? 'Kapat' : 'Yeni İçerik Ekle'}</span>
              </button>
              <button
                type="button"
                onClick={() => setIsCultureCatManagerOpen(!isCultureCatManagerOpen)}
                className="btn-secondary text-xs py-2 px-3 flex-1 sm:flex-none"
              >
                <Settings className="w-4 h-4" />
                <span>Kategorileri Yönet</span>
              </button>
            </div>
          </div>

          {/* Form Modal / Accordion: Yeni Kültür İçeriği Ekle */}
          {isCultureAddOpen && (
            <form onSubmit={handleCreateCultureItem} className="glass-card rounded-3xl p-6 border border-[#D4AF37]/40 bg-gradient-to-r from-[#081B16] via-[#10241F] to-[#0B3D2E] space-y-4 text-xs">
              <h3 className="text-sm font-bold text-[#D4AF37] font-heading border-b border-white/10 pb-2">
                Yeni Kültür / Dil / Tarih / Dengbêj / Ders İçeriği Ekle
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Başlık / Ders Adı (Türkçe)</label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Mem û Zîn Destanı ve Ahmedi Hani"
                    value={cultureForm.titleTR}
                    onChange={(e) => setCultureForm({ ...cultureForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Başlık / Sernav (Kürtçe / Kürtî)</label>
                  <input
                    type="text"
                    placeholder="Örn: Destana Mem û Zînê û Ehmedê Xanî"
                    value={cultureForm.titleKM}
                    onChange={(e) => setCultureForm({ ...cultureForm, titleKM: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori Seçin</label>
                  <select
                    value={cultureForm.category}
                    onChange={(e) => setCultureForm({ ...cultureForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    {cultureCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Ses / Video / Medya Linki (Dengbêj, Müzik, Youtube)</label>
                  <input
                    type="text"
                    placeholder="Örn: https://www.youtube.com/watch?... veya MP3 URL"
                    value={cultureForm.mediaUrl}
                    onChange={(e) => setCultureForm({ ...cultureForm, mediaUrl: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono text-xs"
                  />
                </div>
              </div>

              {/* Photo Upload Component (PC or Phone file picker / URL) */}
              <div className="pt-2 border-t border-white/10">
                <ImageUploadInput
                  label="Görsel / Kapak Fotoğrafı (Bilgisayar veya Telefondan Seçin)"
                  value={cultureForm.mediaUrl}
                  onChange={(url) => setCultureForm({ ...cultureForm, mediaUrl: url })}
                />
              </div>

              <div>
                <label className="text-gray-300 block mb-1 font-bold">İçerik Metni & Detaylar (Türkçe)</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Kültürel makale, ders metni, sözlük anlamı veya şiir dörttükleri..."
                  value={cultureForm.contentTR}
                  onChange={(e) => setCultureForm({ ...cultureForm, contentTR: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-1 font-bold">İçerik Metni (Kürtçe / Pênas bi Kurdî)</label>
                <textarea
                  rows={3}
                  placeholder="Agahiyên bi zimanê kurdî..."
                  value={cultureForm.contentKM}
                  onChange={(e) => setCultureForm({ ...cultureForm, contentKM: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
              </div>

              <div className="flex gap-3">
                <button type="submit" className="btn-primary py-2.5">
                  <Save className="w-4 h-4" />
                  <span>Kültür İçeriğini Kaydet ve Yayınla</span>
                </button>
                <button type="button" onClick={() => setIsCultureAddOpen(false)} className="btn-secondary py-2.5">
                  İptal
                </button>
              </div>
            </form>
          )}

          {/* Category Management Drawer */}
          {isCultureCatManagerOpen && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-sm font-bold text-[#D4AF37] font-heading">
                Kültür & Dil Kategorileri Yönetimi (Ekle / Çıkart)
              </h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Yeni Kategori Adı (Örn: Kürt Tiyatrosu)"
                  value={newCultureCategoryInput}
                  onChange={(e) => setNewCultureCategoryInput(e.target.value)}
                  className="flex-1 p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs"
                />
                <button
                  type="button"
                  onClick={handleAddCultureCategory}
                  className="btn-primary text-xs py-2.5 px-4"
                >
                  Ekle
                </button>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {cultureCategories.map((cat) => (
                  <div key={cat} className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 text-xs text-white font-bold">
                    <span>{cat}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteCultureCategory(cat)}
                      className="text-red-400 hover:text-red-300 text-xs ml-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Filter Bar */}
          <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-2 scrollbar-none border-b border-white/10">
            {['Tümü', ...cultureCategories].map((cat) => (
              <button
                key={cat}
                onClick={() => setCultureCategoryFilter(cat)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  cultureCategoryFilter === cat
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37] shadow-lg'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Content Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cultureItems
              .filter((c) => cultureCategoryFilter === 'Tümü' || c.category === cultureCategoryFilter)
              .map((cul) => (
                <div key={cul.id} className="glass-card rounded-2xl p-4 border border-white/10 space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    {cul.image && (
                      <img src={cul.image} alt="Cul" className="w-full h-36 rounded-xl object-cover border border-white/10" />
                    )}
                    <div className="flex justify-between items-center">
                      <span className="px-2 py-0.5 rounded bg-[#0B3D2E] text-[#D4AF37] text-[10px] font-bold">
                        {cul.category}
                      </span>
                      {cul.date && <span className="text-[10px] text-gray-400">📅 {cul.date}</span>}
                    </div>
                    <h4 className="font-bold text-white text-sm">{cul.title[currentLang] || cul.title.TR}</h4>
                    <p className="text-[11px] text-gray-300 line-clamp-4 leading-relaxed">
                      {cul.content[currentLang] || cul.content.TR}
                    </p>
                    {cul.mediaUrl && (
                      <div className="p-2 rounded-xl bg-white/5 border border-white/10 text-[10px] text-emerald-400 font-mono truncate">
                        🔗 {cul.mediaUrl}
                      </div>
                    )}
                  </div>
                  <div className="pt-2 flex justify-between items-center text-xs border-t border-white/10">
                    <span className="text-[10px] text-gray-400">ID: {cul.id}</span>
                    <button
                      onClick={async () => {
                        if (confirm('Bu kültür içeriğini silmek istediğinize emin misiniz?')) {
                          setCultureItems(cultureItems.filter((x) => x.id !== cul.id));
                          try {
                            await deleteCultureApi(cul.id);
                          } catch (err) {
                            console.error('Delete culture error:', err);
                          }
                        }
                      }}
                      className="text-red-400 hover:text-red-300 text-xs font-bold underline flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Sil</span>
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 6. ÜYE YÖNETİMİ */}
      {/* ========================================================= */}
      {activeTab === 'members' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-2 scrollbar-none border-b border-white/10">
            {[
              { id: 'list', label: 'Üye Listesi' },
              { id: 'add', label: 'Yeni Üye Ekle' },
              { id: 'applications', label: 'Başvurular' },
              { id: 'pending', label: 'Onay Bekleyenler' },
              { id: 'dues', label: 'Aidat Takibi' },
              { id: 'qrcard', label: 'QR Üye Kartı' },
              { id: 'roles', label: 'Roller & Yetkiler' },
              { id: 'groups', label: 'Gruplar' },
              { id: 'message', label: 'Mesaj & Bildirim Gönder' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setMembersSubTab(st.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  membersSubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* SubTab 1: Üye Listesi */}
          {membersSubTab === 'list' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-white font-heading">
                  Kayıtlı Aktif Üye Listesi ({members.length} Kişi)
                </h3>
                <button onClick={() => setMembersSubTab('add')} className="btn-primary text-xs py-2 px-3">
                  <Plus className="w-4 h-4" />
                  <span>Yeni Üye Kaydet</span>
                </button>
              </div>

              {/* BOUNDED TABLE CONTAINER TO PREVENT PAGE OVERFLOW ON MOBILE */}
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Üye No & Ad Soyad</th>
                      <th className="p-3">E-Posta</th>
                      <th className="p-3">Telefon</th>
                      <th className="p-3">Rol</th>
                      <th className="p-3">Aidat Durumu</th>
                      <th className="p-3 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {members.map((m) => (
                      <tr key={m.id} className="hover:bg-white/5">
                        <td className="p-3 font-bold text-white flex items-center gap-2">
                          <img src={m.avatar} alt="A" className="w-8 h-8 rounded-full object-cover border border-[#D4AF37]" />
                          <div>
                            <div>{m.fullName}</div>
                            <div className="text-[10px] text-gray-400 font-mono">{m.memberNo}</div>
                          </div>
                        </td>
                        <td className="p-3 text-gray-300">{m.email}</td>
                        <td className="p-3 text-gray-400">{m.phone || '+33 6 12 34 56'}</td>
                        <td className="p-3 uppercase font-bold text-[10px] text-emerald-400">{m.role}</td>
                        <td className="p-3">
                          {m.duesStatus === 'up_to_date' ? (
                            <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-extrabold border border-emerald-500/30">
                              ✓ Ödendi
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full bg-red-600 text-white text-[10px] font-black border border-red-400 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]">
                              🔴 ÖDEME YAPILMADI
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right space-x-2">
                          <button
                            onClick={() => {
                              setSelectedMemberForCard(m);
                              setMembersSubTab('qrcard');
                            }}
                            className="text-[#D4AF37] hover:underline font-bold text-xs"
                          >
                            Kart
                          </button>
                          <button
                            onClick={async () => {
                              if (confirm('Bu üye kaydını silmek istediğinize emin misiniz?')) {
                                setMembers(members.filter((x) => x.id !== m.id));
                                try {
                                  await deleteMemberApi(m.id);
                                } catch (err) {
                                  console.error('Delete member error:', err);
                                }
                              }
                            }}
                            className="text-red-400 hover:underline font-bold text-xs"
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 2: Yeni Üye Ekle */}
          {membersSubTab === 'add' && (
            <form onSubmit={handleCreateMember} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <h3 className="text-base font-bold text-white font-heading">Yeni Üye Kaydı Oluştur</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Ad Soyad</label>
                  <input
                    type="text"
                    required
                    value={memberForm.fullName}
                    onChange={(e) => setMemberForm({ ...memberForm, fullName: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">E-Posta Adresi</label>
                  <input
                    type="email"
                    required
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Telefon Numarası</label>
                  <input
                    type="text"
                    value={memberForm.phone}
                    onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Üye Rolü</label>
                  <select
                    value={memberForm.role}
                    onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value as any })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    <option value="member">Dernek Üyesi</option>
                    <option value="editor">İçerik Editörü</option>
                    <option value="admin">Yönetim Kurulu Üyesi</option>
                  </select>
                </div>
              </div>

              {/* Photo Upload Component for Member Avatar */}
              <div className="pt-2 border-t border-white/10">
                <ImageUploadInput
                  label="Üye Profil Fotoğrafı (Telefon veya Bilgisayardan Yükleyin)"
                  value={memberForm.avatar}
                  onChange={(url) => setMemberForm({ ...memberForm, avatar: url })}
                />
              </div>

              <button type="submit" className="btn-primary py-2.5">
                <Save className="w-4 h-4" />
                <span>Üyeyi Kaydet</span>
              </button>
            </form>
          )}

          {/* SubTab 3: Başvurular */}
          {membersSubTab === 'applications' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white font-heading">Online Üyelik Başvuruları</h3>
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Aday Ad Soyad</th>
                      <th className="p-3">E-Posta & Telefon</th>
                      <th className="p-3">Şehir / Meslek</th>
                      <th className="p-3">Başvuru Tarihi</th>
                      <th className="p-3 text-right">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {memberApplications.map((app) => (
                      <tr key={app.id} className="hover:bg-white/5">
                        <td className="p-3 font-bold text-white">{app.fullName}</td>
                        <td className="p-3 text-gray-300">{app.email} • {app.phone}</td>
                        <td className="p-3 text-gray-400">{app.city} / {app.occupation}</td>
                        <td className="p-3 text-gray-400">{app.date}</td>
                        <td className="p-3 text-right space-x-2">
                          <button
                            onClick={() => handleApproveApplication(app.id)}
                            className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs"
                          >
                            Onayla
                          </button>
                          <button
                            onClick={async () => {
                              if (confirm('Bu başvuruyu silmek/reddetmek istediğinize emin misiniz?')) {
                                setMemberApplications(memberApplications.filter((a) => a.id !== app.id));
                                try {
                                  await deleteMembershipApplicationApi(app.id);
                                } catch (err) {
                                  console.error('Delete application error:', err);
                                }
                              }
                            }}
                            className="px-2.5 py-1 rounded bg-red-950 hover:bg-red-900 text-red-300 font-bold text-xs"
                          >
                            Reddet
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 4: Onay Bekleyenler */}
          {membersSubTab === 'pending' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-amber-400 font-heading">Yönetim Kurulu Onayı Bekleyen Üyeler</h3>
              <p className="text-xs text-gray-300">Belgeleri eksik veya inceleme aşamasındaki üyeler.</p>
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Ad Soyad</th>
                      <th className="p-3">E-Posta</th>
                      <th className="p-3">Durum</th>
                      <th className="p-3 text-right">Aksiyon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {memberApplications.slice(0, 2).map((app) => (
                      <tr key={app.id} className="hover:bg-white/5">
                        <td className="p-3 font-bold text-white">{app.fullName}</td>
                        <td className="p-3 text-gray-300">{app.email}</td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold">
                            Onay Bekliyor
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => handleApproveApplication(app.id)}
                            className="btn-primary py-1 px-3 text-xs"
                          >
                            Üyeliği Aktif Et
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 5: Aidat Takibi */}
          {membersSubTab === 'dues' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/10 pb-4">
                <div>
                  <h3 className="text-base font-bold text-[#D4AF37] font-heading flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-[#D4AF37]" />
                    <span>Resmi Dernek Üye Aidat & Ödeme Takip Portalı (2026)</span>
                  </h3>
                  <p className="text-xs text-gray-300 mt-0.5">
                    Tüm üyelerin ödeme geçmişi, makbuzları ve ödemeyen borçlu üyelerin kırmızı uyarı takibi.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (onShowToast) onShowToast('Tüm borçlu üyelere SMS ve E-posta ödeme hatırlatması gönderildi.', 'info', 'Toplu Ödeme Hatırlatması');
                    }}
                    className="btn-warning text-xs py-2 px-3 flex items-center gap-1.5 cursor-pointer"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>Borçlu Üyelere Hatırlatma Gönder</span>
                  </button>
                </div>
              </div>

              {/* Summary Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div className="p-4 rounded-2xl bg-[#0B3D2E] border border-emerald-500/40 space-y-1">
                  <p className="text-emerald-300 text-[10px] uppercase font-bold">TOPLAM TAHSİL EDİLEN AİDAT</p>
                  <p className="text-xl font-black text-white">
                    €{members.filter((m) => m.duesStatus === 'up_to_date').length * 120}
                  </p>
                  <p className="text-[10px] text-emerald-400 font-semibold">
                    ✓ {members.filter((m) => m.duesStatus === 'up_to_date').length} Üye Tam Ödeme Yaptı
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-red-950/60 border border-red-500/60 space-y-1">
                  <p className="text-red-300 text-[10px] uppercase font-bold">🔴 TAHSİL EDİLMEYEN BORÇ TUTARI</p>
                  <p className="text-xl font-black text-red-200">
                    €{members.filter((m) => m.duesStatus === 'overdue').length * 120}
                  </p>
                  <p className="text-[10px] text-red-300 font-extrabold flex items-center gap-1">
                    <ShieldAlert className="w-3.5 h-3.5 text-red-400" />
                    <span>{members.filter((m) => m.duesStatus === 'overdue').length} Üye Ödeme Yapmadı (Kırmızı Uyarı)</span>
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-1">
                  <p className="text-gray-400 text-[10px] uppercase font-bold">TOPLAM KAYITLI ÜYE</p>
                  <p className="text-xl font-black text-white">{members.length} Üye</p>
                  <p className="text-[10px] text-gray-300">Yıllık Standart Aidat: €120.00</p>
                </div>

                <div className="p-4 rounded-2xl bg-[#132238] border border-[#38BDF8]/40 space-y-1">
                  <p className="text-[#7DD3FC] text-[10px] uppercase font-bold">TAHSİLAT ORANI</p>
                  <p className="text-xl font-black text-[#38BDF8]">
                    %{Math.round((members.filter((m) => m.duesStatus === 'up_to_date').length / (members.length || 1)) * 100)}
                  </p>
                  <p className="text-[10px] text-[#38BDF8]">Otomatik Sanal POS & SEPA</p>
                </div>
              </div>

              {/* Dues Status Filter Bar */}
              <div className="flex items-center gap-2 bg-black/30 p-2 rounded-2xl border border-white/10">
                <span className="text-xs font-bold text-gray-400 px-2">Filtrele:</span>
                <button
                  onClick={() => setAdminDuesFilter('all')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    adminDuesFilter === 'all'
                      ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  Tüm Üyeler ({members.length})
                </button>
                <button
                  onClick={() => setAdminDuesFilter('paid')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    adminDuesFilter === 'paid'
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  ✓ Ödeyen Üyeler ({members.filter((m) => m.duesStatus === 'up_to_date').length})
                </button>
                <button
                  onClick={() => setAdminDuesFilter('overdue')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    adminDuesFilter === 'overdue'
                      ? 'bg-red-600 text-white border border-red-400 shadow-[0_0_15px_rgba(220,38,38,0.5)]'
                      : 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20'
                  }`}
                >
                  🔴 Ödeme Yapmayanlar / Borçlular ({members.filter((m) => m.duesStatus === 'overdue').length})
                </button>
              </div>

              {/* Members Payment Tracking Table */}
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[700px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3.5">Üye Bilgisi</th>
                      <th className="p-3.5">Yıllık Aidat Miktarı</th>
                      <th className="p-3.5">Son Ödeme Tarihi</th>
                      <th className="p-3.5">Ödeme Durumu & Uyarı</th>
                      <th className="p-3.5 text-right">Yönetim İşlemi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {members
                      .filter((m) => {
                        if (adminDuesFilter === 'paid') return m.duesStatus === 'up_to_date';
                        if (adminDuesFilter === 'overdue') return m.duesStatus === 'overdue';
                        return true;
                      })
                      .map((m) => {
                        const isPaid = m.duesStatus === 'up_to_date';
                        const mockReceiptNo = `MKB-2026-${m.id.replace(/\D/g, '').padStart(4, '0') || '0042'}`;

                        return (
                          <tr
                            key={m.id}
                            className={`transition-colors ${
                              isPaid
                                ? 'hover:bg-emerald-500/5'
                                : 'bg-red-950/20 hover:bg-red-900/30 border-l-4 border-l-red-500'
                            }`}
                          >
                            <td className="p-3.5 font-bold text-white flex items-center gap-3">
                              <img src={m.avatar} alt="M" className="w-8 h-8 rounded-xl object-cover border border-white/15" />
                              <div>
                                <p className="text-white font-bold">{m.fullName}</p>
                                <p className="text-[10px] text-[#D4AF37] font-mono">Üye No: {m.memberNo} • {m.email}</p>
                              </div>
                            </td>

                            <td className="p-3.5 font-bold text-white">
                              €120.00 / Yıl
                              <span className="block text-[10px] text-gray-400 font-normal">2026 Dönemi</span>
                            </td>

                            <td className="p-3.5 text-gray-300 font-mono">
                              {isPaid ? '2026-01-15' : 'ÖDENMEDİ'}
                            </td>

                            <td className="p-3.5">
                              {isPaid ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-extrabold border border-emerald-500/40">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  <span>ÖDENDİ (#{mockReceiptNo})</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-[10px] font-black border border-red-400 animate-pulse shadow-[0_0_12px_rgba(239,68,68,0.6)]">
                                  <ShieldAlert className="w-3.5 h-3.5 text-white shrink-0" />
                                  <span>🔴 ÖDEME YAPILMADI / 120€ BORÇLU</span>
                                </span>
                              )}
                            </td>

                            <td className="p-3.5 text-right space-x-2">
                              {isPaid ? (
                                <button
                                  onClick={() =>
                                    setSelectedAdminReceipt({
                                      receiptNo: mockReceiptNo,
                                      payerName: m.fullName,
                                      memberNo: m.memberNo,
                                      date: '2026-01-15',
                                      amount: 120,
                                      currency: 'EUR',
                                      title: '2026 Yılı Dernek Üyelik Aidatı',
                                      paymentMethod: 'Dernek Sanal POS Cihazı',
                                    })
                                  }
                                  className="px-3 py-1.5 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 text-[11px] font-bold inline-flex items-center gap-1.5 cursor-pointer"
                                >
                                  <Receipt className="w-3.5 h-3.5" />
                                  <span>Makbuz İndir / Yazdır</span>
                                </button>
                              ) : (
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setMembers(
                                        members.map((mem) =>
                                          mem.id === m.id ? { ...mem, duesStatus: 'up_to_date' } : mem
                                        )
                                      );
                                      if (onShowToast) {
                                        onShowToast(`${m.fullName} isimli üyenin aidatı tahsil edildi olarak kaydedildi. Makbuz üretildi!`, 'success', 'Aidat Tahsilatı');
                                      }
                                    }}
                                    className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    <span>Ödendi Olarak İşle</span>
                                  </button>

                                  <button
                                    onClick={() => {
                                      if (onShowToast) {
                                        onShowToast(`${m.fullName} kişisine SMS ve e-posta ödeme uyarısı gönderildi.`, 'info', 'Borç Uyarısı');
                                      }
                                    }}
                                    className="px-2.5 py-1.5 rounded-xl bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30 text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer"
                                    title="Üyeye SMS / E-Posta Ödeme Uyarısı Gönder"
                                  >
                                    <Send className="w-3.5 h-3.5 text-red-400" />
                                    <span>Uyarı Gönder</span>
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 6: QR Üye Kartı */}
          {membersSubTab === 'qrcard' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading">Dijital QR Üye Kartı Oluşturucu</h3>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="w-full max-w-sm rounded-3xl p-6 bg-gradient-to-br from-[#0B3D2E] via-[#10241F] to-[#081B16] border-2 border-[#D4AF37] shadow-2xl text-white space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-[#D4AF37]">KÜRT KÜLTÜR DERNEĞİ</span>
                    <span className="text-[10px] text-emerald-400">RESMİ ÜYE KARTI</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <img src={selectedMemberForCard?.avatar} alt="Avatar" className="w-16 h-16 rounded-2xl object-cover border border-[#D4AF37]" />
                    <div>
                      <h4 className="font-extrabold text-sm">{selectedMemberForCard?.fullName}</h4>
                      <p className="text-[10px] text-gray-300">No: {selectedMemberForCard?.memberNo}</p>
                      <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[9px] font-bold mt-1 inline-block">
                        Aktif Üye
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                    <QrCode className="w-12 h-12 text-[#D4AF37]" />
                    <span className="text-[10px] text-gray-400">Son Geçerlilik: 2026/12</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs text-gray-300">Kart Sahibi Üye Seçin:</p>
                  <select
                    onChange={(e) => {
                      const m = members.find((x) => x.id === e.target.value);
                      if (m) setSelectedMemberForCard(m);
                    }}
                    className="p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs w-full"
                  >
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.fullName} ({m.memberNo})</option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => alert('Üye Kartı Yazdırıldı!')} className="btn-primary text-xs py-2">
                      <Printer className="w-4 h-4" />
                      <span>Yazdır</span>
                    </button>
                    <button onClick={() => alert('QR Kart indirildi.')} className="btn-secondary text-xs py-2">
                      <Download className="w-4 h-4" />
                      <span>İndir</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SubTab 7: Roller & Yetkiler */}
          {membersSubTab === 'roles' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-white font-heading">Roller ve Sistem İzin Matrisi</h3>
              <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Sistem Rolü</th>
                      <th className="p-3">Haber & Etkinlik</th>
                      <th className="p-3">Faaliyet & Kültür</th>
                      <th className="p-3">Üye Yönetimi</th>
                      <th className="p-3">Canlı Yayın & Ayarlar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {[
                      { role: 'Süper Admin', news: true, act: true, mem: true, live: true },
                      { role: 'Yönetim Kurulu', news: true, act: true, mem: true, live: true },
                      { role: 'İçerik Editörü', news: true, act: true, mem: false, live: false },
                      { role: 'Derslik Eğitmeni', news: false, act: true, mem: false, live: false },
                      { role: 'Standart Üye', news: false, act: false, mem: false, live: false },
                    ].map((row) => (
                      <tr key={row.role} className="hover:bg-white/5">
                        <td className="p-3 font-bold text-white">{row.role}</td>
                        <td className="p-3">{row.news ? '✅ Tam Yetki' : '❌ Salt Okunur'}</td>
                        <td className="p-3">{row.act ? '✅ Tam Yetki' : '❌ Salt Okunur'}</td>
                        <td className="p-3">{row.mem ? '✅ Tam Yetki' : '❌ Erişim Yok'}</td>
                        <td className="p-3">{row.live ? '✅ Tam Yetki' : '❌ Erişim Yok'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SubTab 8: Gruplar */}
          {membersSubTab === 'groups' && (
            <div className="space-y-4">
              <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
                <h3 className="text-base font-bold text-white font-heading">Dernek İçi Çalışma Komisyonları & Gruplar</h3>

                <form onSubmit={handleCreateGroup} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    required
                    placeholder="Grup Adı (Örn: Kadın Meclisi)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs"
                  />
                  <input
                    type="text"
                    placeholder="Grup Sorumlusu"
                    value={newGroupLeader}
                    onChange={(e) => setNewGroupLeader(e.target.value)}
                    className="p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs"
                  />
                  <button type="submit" className="btn-primary text-xs py-2.5">
                    <Plus className="w-4 h-4" />
                    <span>Yeni Grup Oluştur</span>
                  </button>
                </form>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {memberGroups.map((grp) => (
                  <div key={grp.id} className="glass-card rounded-2xl p-4 border border-white/10 space-y-2">
                    <h4 className="font-bold text-[#D4AF37] text-sm">{grp.name}</h4>
                    <p className="text-[10px] text-gray-300">👤 Sorumlu: {grp.leader}</p>
                    <p className="text-[10px] text-emerald-400 font-bold">👥 Üye Sayısı: {grp.memberCount} Kişi</p>
                    <p className="text-[11px] text-gray-400">{grp.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SubTab 9: Mesaj & Bildirim Gönder */}
          {membersSubTab === 'message' && (
            <div className="space-y-6">
              <form onSubmit={handleSendBroadcast} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
                <h3 className="text-base font-bold text-white font-heading">Üyelere Toplu Mesaj, E-Posta & SMS Gönderimi</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Hedef Kitle / Grup</label>
                    <select
                      value={broadcastForm.targetGroup}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, targetGroup: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    >
                      <option value="Tüm Üyeler">Tüm Kayıtlı Üyeler (1,248 Kişi)</option>
                      <option value="Kültür & Sanat Komisyonu">Kültür & Sanat Komisyonu</option>
                      <option value="Kürtçe Derslik Eğitmenleri">Kürtçe Derslik Eğitmenleri</option>
                      <option value="Gençlik Meclisi">Gençlik Meclisi</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Gönderim Kanalı</label>
                    <select
                      value={broadcastForm.channel}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, channel: e.target.value as any })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    >
                      <option value="SMS & E-Posta">SMS & E-Posta (Çift Kanal)</option>
                      <option value="E-Posta">Yalnızca E-Posta</option>
                      <option value="SMS">Yalnızca SMS</option>
                      <option value="Mobil Bildirim">Mobil Anlık Bildirim (Push)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Mesaj Konusu / Başlık</label>
                  <input
                    type="text"
                    required
                    value={broadcastForm.subject}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, subject: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Mesaj İçeriği</label>
                  <textarea
                    rows={4}
                    required
                    value={broadcastForm.message}
                    onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <button type="submit" className="btn-primary py-2.5">
                  <Send className="w-4 h-4" />
                  <span>Toplu Bildirimi Gönder</span>
                </button>
              </form>

              <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
                <h3 className="text-sm font-bold text-[#D4AF37] font-heading">Son Gönderim Geçmişi (Loglar)</h3>
                <div className="overflow-x-auto w-full max-w-full rounded-2xl border border-white/10 bg-black/20">
                  <table className="w-full text-left text-xs min-w-[650px]">
                    <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                      <tr>
                        <th className="p-3">Tarih</th>
                        <th className="p-3">Kanal</th>
                        <th className="p-3">Hedef Alıcı</th>
                        <th className="p-3 text-right">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {broadcastLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-white/5">
                          <td className="p-3 text-gray-400">{log.date}</td>
                          <td className="p-3 font-bold text-white">{log.channel}</td>
                          <td className="p-3 text-gray-300">{log.target}</td>
                          <td className="p-3 text-right text-emerald-400 font-bold">{log.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 7. BAĞIŞLAR & RAPOR */}
      {/* ========================================================= */}
      {activeTab === 'donations' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { id: 'list', label: '📊 Bağış Kayıtları' },
              { id: 'campaigns', label: '🎯 Bağış Fonları & Seçenekleri' },
              { id: 'pos_settings', label: '💳 Sanal POS & Banka Yapılandırması' },
              { id: 'add', label: '➕ Manuel Bağış Girişi' },
              { id: 'receipts', label: '📄 Makbuz Oluştur' },
              { id: 'income', label: '📈 Gelir Raporu' },
              { id: 'expense', label: '📉 Gider Raporu' },
              { id: 'export', label: '📥 Dışa Aktar (Excel/PDF/CSV)' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setDonationsSubTab(st.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  donationsSubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37] shadow-lg'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {/* SubTab: BAĞIŞ FONLARI & SEÇENEKLERİ YÖNETİMİ */}
          {donationsSubTab === 'campaigns' && (
            <div className="space-y-6">
              <div className="glass-card rounded-3xl p-6 border border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-base font-bold text-white font-heading flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#D4AF37]" />
                    <span>Bağış Fonları & Seçenekleri Yönetimi</span>
                  </h3>
                  <p className="text-xs text-gray-300 mt-1">
                    Sitede ve bağış ekranında seçilebilecek bağış kategorilerini, hedef tutarları ve ne için bağış toplandığı açıklamalarını buradan yönetin.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingCampaign(null);
                    setCampaignForm({ title: '', category: 'Eğitim & Dil', description: '', targetAmount: 10000, collectedAmount: 0, isActive: true });
                    setIsCampaignModalOpen(true);
                  }}
                  className="btn-primary py-2.5 px-4 text-xs shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>Yeni Bağış Fonu Ekle</span>
                </button>
              </div>

              {/* Campaign Cards List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {campaigns.map((c) => (
                  <div key={c.id} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 hover:border-[#D4AF37]/40 transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="px-2.5 py-0.5 rounded-md bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/30">
                          {c.category || 'Bağış Fonu'}
                        </span>
                        <h4 className="font-extrabold text-white text-base mt-2">{c.title}</h4>
                      </div>
                      <button
                        onClick={() => handleToggleCampaignActive(c.id)}
                        className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all ${
                          c.isActive !== false ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
                        }`}
                      >
                        {c.isActive !== false ? '✓ Yayında (Aktif)' : '✕ Pasif'}
                      </button>
                    </div>

                    <p className="text-xs text-gray-300 leading-relaxed bg-black/20 p-3 rounded-2xl border border-white/5">
                      {c.description}
                    </p>

                    <div className="space-y-1.5 pt-1">
                      <div className="flex justify-between text-xs text-gray-300 font-medium">
                        <span>Hedef: €{(c.targetAmount || 0).toLocaleString()}</span>
                        <span className="text-emerald-400 font-bold">Toplanan: €{(c.collectedAmount || 0).toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-black/40 h-2 rounded-full overflow-hidden border border-white/10">
                        <div
                          className="bg-gradient-to-r from-emerald-500 to-[#D4AF37] h-full rounded-full"
                          style={{ width: `${Math.min(100, Math.round(((c.collectedAmount || 0) / (c.targetAmount || 1)) * 100))}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                      <button
                        onClick={() => {
                          setEditingCampaign(c);
                          setCampaignForm(c);
                          setIsCampaignModalOpen(true);
                        }}
                        className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-amber-400 font-bold text-xs flex items-center gap-1.5"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        <span>Düzenle</span>
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(c.id)}
                        className="px-3 py-1.5 rounded-xl bg-red-950/60 hover:bg-red-900 text-red-400 font-bold text-xs flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Sil</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Campaign Edit/Add Modal */}
              {isCampaignModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                  <div className="glass-card rounded-3xl border-2 border-[#D4AF37] p-6 sm:p-8 max-w-lg w-full space-y-5 animate-in zoom-in-95 duration-200">
                    <div className="flex justify-between items-center border-b border-white/10 pb-3">
                      <h3 className="font-extrabold text-white text-base flex items-center gap-2">
                        <Target className="w-5 h-5 text-[#D4AF37]" />
                        <span>{editingCampaign ? 'Bağış Fonunu Düzenle' : 'Yeni Bağış Fonu / Kampanyası Ekle'}</span>
                      </h3>
                      <button onClick={() => setIsCampaignModalOpen(false)} className="p-1 rounded-lg text-gray-400 hover:text-white">
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <form onSubmit={handleSaveCampaign} className="space-y-4 text-xs">
                      <div>
                        <label className="text-gray-300 font-bold block mb-1">Bağış Fonu / Kampanya Başlığı *</label>
                        <input
                          type="text"
                          required
                          value={campaignForm.title || ''}
                          onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })}
                          placeholder="Örn: Kürtçe Dil Kursları ve Kültür Evi Destek Fonu"
                          className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-gray-300 font-bold block mb-1">Kategori</label>
                          <select
                            value={campaignForm.category || 'Eğitim & Dil'}
                            onChange={(e) => setCampaignForm({ ...campaignForm, category: e.target.value })}
                            className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                          >
                            <option value="Eğitim & Dil">Eğitim & Dil</option>
                            <option value="Eğitim Yardım">Eğitim Yardım / Burs</option>
                            <option value="Kültür & Etkinlik">Kültür & Etkinlik</option>
                            <option value="Acil Yardım">Acil Yardım & Afet</option>
                            <option value="Sosyal Dayanışma">Sosyal Dayanışma</option>
                            <option value="Genel Bağış">Genel Bağış</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-gray-300 font-bold block mb-1">Hedef Tutar (€)</label>
                          <input
                            type="number"
                            value={campaignForm.targetAmount || 10000}
                            onChange={(e) => setCampaignForm({ ...campaignForm, targetAmount: Number(e.target.value) })}
                            className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-gray-300 font-bold block mb-1">Toplanan Tutar (€)</label>
                        <input
                          type="number"
                          value={campaignForm.collectedAmount || 0}
                          onChange={(e) => setCampaignForm({ ...campaignForm, collectedAmount: Number(e.target.value) })}
                          className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                        />
                      </div>

                      <div>
                        <label className="text-gray-300 font-bold block mb-1">Açıklama (Ne İçin Bağış Toplandığı Detayı) *</label>
                        <textarea
                          rows={4}
                          required
                          value={campaignForm.description || ''}
                          onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                          placeholder="Bu bağış fonu ile Kürtçe derslik materyalleri, eğitmen desteği ve bina giderleri karşılanmaktadır..."
                          className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white leading-relaxed"
                        />
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          id="campActiveCheck"
                          checked={campaignForm.isActive !== false}
                          onChange={(e) => setCampaignForm({ ...campaignForm, isActive: e.target.checked })}
                          className="w-4 h-4 accent-[#D4AF37]"
                        />
                        <label htmlFor="campActiveCheck" className="text-white font-bold cursor-pointer">
                          Bu bağış seçeneğini sitede yayına al (Aktif)
                        </label>
                      </div>

                      <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
                        <button
                          type="button"
                          onClick={() => setIsCampaignModalOpen(false)}
                          className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 font-bold"
                        >
                          İptal
                        </button>
                        <button type="submit" className="btn-primary py-2.5 px-6">
                          <Save className="w-4 h-4" />
                          <span>Fonu Kaydet</span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SubTab: KREDİ KARTI SANAL POS & BANKA YAPILANDIRMASI */}
          {donationsSubTab === 'pos_settings' && (
            <div className="glass-card rounded-3xl p-6 sm:p-8 border border-white/10 space-y-6">
              <div className="border-b border-white/10 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-[#D4AF37]" />
                    <span>Kredi Kartı Sanal POS & Banka Entegrasyon Yapılandırması</span>
                  </h3>
                  <p className="text-xs text-gray-300 mt-1">
                    Bağışçıların kredi kartı ve otomatik ödemelerinin doğrudan aktarılacağı dernek banka hesabı ve POS API bilgilerini girin.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSavePosSettings}
                  className="btn-primary py-2.5 px-6 shrink-0"
                >
                  <Save className="w-4 h-4" />
                  <span>Sanal POS Bilgilerini Kaydet</span>
                </button>
              </div>

              <form onSubmit={handleSavePosSettings} className="space-y-6 text-xs">
                {/* 1. POS SAĞLAYICI SEÇİMİ & MOD */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    <span>1. Aktif Sanal POS Altyapısı & Çalışma Modu</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-gray-300 font-bold block mb-1">Aktif Ödeme Gateway / POS</label>
                      <select
                        value={posForm.activeProvider || 'sanal_pos'}
                        onChange={(e) => setPosForm({ ...posForm, activeProvider: e.target.value as any })}
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                      >
                        <option value="sanal_pos">Dernek Sanal POS Cihazı</option>
                        <option value="sepa">Resmi Dernek SEPA IBAN Transfer Entegrasyonu</option>
                        <option value="custom_pos">Özel Sanal POS Entegrasyonu</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-gray-300 font-bold block mb-1">POS Sağlayıcı Görünen Unvanı</label>
                      <input
                        type="text"
                        value={posForm.providerName || ''}
                        onChange={(e) => setPosForm({ ...posForm, providerName: e.target.value })}
                        placeholder="Örn: Dernek Sanal POS Cihazı & SEPA Geçidi"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 font-bold block mb-1">Çalışma Modu (Test vs Canlı)</label>
                      <button
                        type="button"
                        onClick={() => setPosForm({ ...posForm, testMode: !posForm.testMode })}
                        className={`w-full p-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                          posForm.testMode
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50'
                            : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                        }`}
                      >
                        <ShieldAlert className="w-4 h-4" />
                        <span>{posForm.testMode ? '🧪 TEST / SANDBOX MODU AÇIK' : '🟢 CANLI (PRODUCTION) MOD - AKTİF'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* 2. API & MAĞAZA KİMLİK BİLGİLERİ */}
                <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <span>2. Banka POS API & Mağaza Güvenlik Bilgileri</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="text-gray-300 font-bold block mb-1">Merchant / Mağaza ID</label>
                      <input
                        type="text"
                        value={posForm.merchantId || ''}
                        onChange={(e) => setPosForm({ ...posForm, merchantId: e.target.value })}
                        placeholder="30094812"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 font-bold block mb-1">Terminal ID</label>
                      <input
                        type="text"
                        value={posForm.terminalId || ''}
                        onChange={(e) => setPosForm({ ...posForm, terminalId: e.target.value })}
                        placeholder="TR-78200-POS"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 font-bold block mb-1">API Key</label>
                      <input
                        type="text"
                        value={posForm.apiKey || ''}
                        onChange={(e) => setPosForm({ ...posForm, apiKey: e.target.value })}
                        placeholder="kkdd-pos-live-api-key-9921"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 font-bold block mb-1 flex items-center justify-between">
                        <span>Secret Key</span>
                        <button
                          type="button"
                          onClick={() => setShowSecretKey(!showSecretKey)}
                          className="text-[#D4AF37] hover:underline text-[10px]"
                        >
                          {showSecretKey ? 'Gizle' : 'Göster'}
                        </button>
                      </label>
                      <input
                        type={showSecretKey ? 'text' : 'password'}
                        value={posForm.secretKey || ''}
                        onChange={(e) => setPosForm({ ...posForm, secretKey: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                      />
                    </div>
                  </div>
                </div>

                {/* 3. ÖDEMENİN BİRİKECEĞİ DERNEK BANKA HESABI & IBAN */}
                <div className="p-4 rounded-2xl bg-[#0B3D2E]/80 border border-[#D4AF37]/40 space-y-4">
                  <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-[#D4AF37]" />
                    <span>3. Ödemelerin İşleneceği Dernek Banka Hesabı (Paranın Gideceği Banka)</span>
                  </h4>
                  <p className="text-[11px] text-gray-200">
                    Kredi kartı çekimleri doğrudan burada belirttiğiniz dernek resmi banka hesabına aktarılır ve bağışçı ödeme onayında bu bilgileri görür.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="text-gray-200 font-bold block mb-1">Dernek Banka Hesabı Adı *</label>
                      <input
                        type="text"
                        required
                        value={posForm.bankAccountName || ''}
                        onChange={(e) => setPosForm({ ...posForm, bankAccountName: e.target.value })}
                        placeholder="Kürt Kültür ve Dayanışma Derneği Yvelines"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-200 font-bold block mb-1">Resmi Dernek IBAN Numarası *</label>
                      <input
                        type="text"
                        required
                        value={posForm.iban || ''}
                        onChange={(e) => setPosForm({ ...posForm, iban: e.target.value })}
                        placeholder="TR33 0006 2000 0000 1234 5678 90"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-200 font-bold block mb-1">SWIFT / BIC Kodu</label>
                      <input
                        type="text"
                        value={posForm.swiftCode || ''}
                        onChange={(e) => setPosForm({ ...posForm, swiftCode: e.target.value })}
                        placeholder="GAGRTR2A"
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-gray-200 font-bold block mb-1">Para Birimi</label>
                      <select
                        value={posForm.currency || 'EUR'}
                        onChange={(e) => setPosForm({ ...posForm, currency: e.target.value as any })}
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                      >
                        <option value="EUR">EUR (€) - Euro (Varsayılan)</option>
                        <option value="TRY">TRY (₺) - Türk Lirası</option>
                        <option value="USD">USD ($) - ABD Doları</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-gray-200 font-bold block mb-1">Min. Bağış Tutarı (€)</label>
                      <input
                        type="number"
                        value={posForm.minDonationAmount || 5}
                        onChange={(e) => setPosForm({ ...posForm, minDonationAmount: Number(e.target.value) })}
                        className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row gap-4 border-t border-white/10">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={posForm.redirect3dSecure !== false}
                        onChange={(e) => setPosForm({ ...posForm, redirect3dSecure: e.target.checked })}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="text-xs text-white font-bold">3D Secure Şifreli Ödeme Zorunluluğu (Tavsiye Edilir)</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={posForm.autoDebitEnabled !== false}
                        onChange={(e) => setPosForm({ ...posForm, autoDebitEnabled: e.target.checked })}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <span className="text-xs text-white font-bold">Otomatik Aylık Çekim (Direct Debit & e-Mandate) İzni</span>
                    </label>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button type="submit" className="btn-primary py-3 px-8 text-sm">
                    <Save className="w-4 h-4" />
                    <span>Sanal POS Bilgilerini Kaydet ve Dernek Hesabına Bağla</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {donationsSubTab === 'add' ? (
            <form onSubmit={handleCreateDonation} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
              <h3 className="text-base font-bold text-white font-heading">Yeni Bağış Girişi</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Bağışçı Adı</label>
                  <input
                    type="text"
                    required
                    value={donationForm.donorName}
                    onChange={(e) => setDonationForm({ ...donationForm, donorName: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Tutar (€)</label>
                  <input
                    type="number"
                    required
                    value={donationForm.amount}
                    onChange={(e) => setDonationForm({ ...donationForm, amount: Number(e.target.value) })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary py-2.5">
                <Save className="w-4 h-4" />
                <span>Bağışı Kaydet</span>
              </button>
            </form>
          ) : donationsSubTab === 'export' ? (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-center">
              <h3 className="text-base font-bold text-[#D4AF37]">Mali Rapor & Veri Dışa Aktarma</h3>
              <p className="text-xs text-gray-300">Tüm mali yıl bağış ve dernek gider kayıtlarını istediğiniz formatta indirin.</p>
              <div className="flex justify-center gap-4 pt-2">
                <button onClick={() => alert('Excel (.xlsx) formatında indirildi.')} className="btn-primary text-xs py-2.5">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>Excel İndir</span>
                </button>
                <button onClick={() => alert('PDF Formatında indirildi.')} className="btn-secondary text-xs py-2.5">
                  <FileText className="w-4 h-4" />
                  <span>PDF İndir</span>
                </button>
                <button onClick={() => alert('CSV formatında indirildi.')} className="btn-warning text-xs py-2.5">
                  <Download className="w-4 h-4" />
                  <span>CSV İndir</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* LIVE SECURITY & AUDIT MONITOR BANNER */}
              <div className="glass-card rounded-2xl p-5 border-2 border-red-500/80 bg-red-950/40 shadow-xl shadow-red-950/50 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-red-500/30 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-red-500/20 text-red-400 border border-red-500/40 shrink-0">
                      <ShieldAlert className="w-6 h-6 animate-pulse" />
                    </div>
                    <div>
                      <h3 className="text-base font-extrabold text-white flex items-center gap-2">
                        <span>🚨 CANLI GÜVENLİK VE YÖNETİM DENETİM MONİTÖRÜ</span>
                        <span className="px-2 py-0.5 rounded-full bg-red-500/30 text-red-200 border border-red-500/60 font-mono text-[10px] uppercase tracking-wider animate-pulse font-bold">
                          Canlı
                        </span>
                      </h3>
                      <p className="text-xs text-red-200/90 mt-0.5">
                        Üye ismi uyuşmazlıkları, Sanal POS 3D Secure doğrulamaları ve Yönetim Kurulu finansal onay bekleyen işlemler anlık olarak denetlenmektedir.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setDonationApprovalFilter('pending')}
                      className={`px-3.5 py-2 rounded-xl text-xs font-black flex items-center gap-2 transition-all cursor-pointer ${
                        donationApprovalFilter === 'pending'
                          ? 'bg-red-500 text-white shadow-lg shadow-red-500/50 scale-105'
                          : 'bg-red-500/20 text-red-300 border border-red-500/50 hover:bg-red-500/30'
                      }`}
                    >
                      <AlertTriangle className="w-4 h-4 text-red-300" />
                      <span>🚨 Kırmızı Alarm & Bekleyenler ({donations.filter(d => !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval').length})</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-gray-200">
                  <div className="p-3 rounded-xl bg-black/40 border border-red-500/30 flex items-center justify-between">
                    <span className="text-red-300/90 font-medium">Güvenlik Onayı Bekleyen İşlemler:</span>
                    <span className="font-extrabold text-red-400 font-mono text-sm px-2 py-0.5 rounded bg-red-500/20 border border-red-500/40">
                      {donations.filter(d => !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval').length} Kayıt (RED ALARM)
                    </span>
                  </div>
                  <div className="p-3 rounded-xl bg-black/40 border border-white/10 flex items-center justify-between">
                    <span className="text-emerald-300 font-medium">Yönetimce Onaylanan Tamamlanmış Ödemeler:</span>
                    <span className="font-extrabold text-emerald-400 font-mono text-sm px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40">
                      {donations.filter(d => d.verificationStatus === 'Approved').length} İşlem (Tamamlandı)
                    </span>
                  </div>
                  <div className="p-3 rounded-xl bg-black/40 border border-red-900/50 flex items-center justify-between">
                    <span className="text-red-300 font-medium">İsim Uyuşmazlığı / Reddedilen Bağışlar:</span>
                    <span className="font-extrabold text-red-300 font-mono text-sm px-2 py-0.5 rounded bg-red-900/40 border border-red-700/50">
                      {donations.filter(d => d.verificationStatus === 'Rejected').length} İptal
                    </span>
                  </div>
                </div>
              </div>

              {/* STATS OVERVIEW CARDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glass-card rounded-2xl p-4 border border-white/10 space-y-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Toplanan Toplam Bağış</span>
                  <p className="text-xl font-black text-[#D4AF37]">
                    €{donations.reduce((sum, d) => sum + (d.amount || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-emerald-400 font-bold">{donations.length} Adet İşlem Kayıtlı</p>
                </div>

                <div className="glass-card rounded-2xl p-4 border-2 border-red-500/80 bg-red-950/40 space-y-1 shadow-lg shadow-red-950/40">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-extrabold text-red-300 uppercase tracking-wider block flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                      <span>🚨 ONAY BEKLEYENLER (RED ALARM)</span>
                    </span>
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                    </span>
                  </div>
                  <p className="text-xl font-black text-red-300">
                    {donations.filter(d => !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval').length} Adet İşlem
                  </p>
                  <p className="text-[10px] text-red-200 font-semibold">
                    €{donations.filter(d => !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval').reduce((s, d) => s + (d.amount || 0), 0).toLocaleString()} Yönetim Onayı Bekliyor
                  </p>
                </div>

                <div className="glass-card rounded-2xl p-4 border border-emerald-500/40 bg-emerald-500/5 space-y-1">
                  <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider block">Yönetimce Onaylanan Bağışlar</span>
                  <p className="text-xl font-black text-emerald-300">
                    {donations.filter(d => d.verificationStatus === 'Approved').length} Adet
                  </p>
                  <p className="text-[10px] text-emerald-400">
                    €{donations.filter(d => d.verificationStatus === 'Approved').reduce((s, d) => s + (d.amount || 0), 0).toLocaleString()} Onaylı Fon
                  </p>
                </div>

                <div className="glass-card rounded-2xl p-4 border border-red-900/50 bg-red-950/20 space-y-1">
                  <span className="text-[10px] font-bold text-red-300 uppercase tracking-wider block">Şüpheli / İptal Bağışlar</span>
                  <p className="text-xl font-black text-red-300">
                    {donations.filter(d => d.verificationStatus === 'Rejected').length} Adet
                  </p>
                  <p className="text-[10px] text-red-400">İsim Uyuşmazlığı Nedeniyle Reddedildi</p>
                </div>
              </div>

              {/* FILTER BAR & SEARCH */}
              <div className="glass-card rounded-2xl p-4 border border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { id: 'all', label: `Tüm Bağışlar (${donations.length})` },
                    {
                      id: 'pending',
                      label: `🚨 ONAY BEKLEYENLER (${donations.filter(d => !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval').length})`,
                    },
                    {
                      id: 'approved',
                      label: `✅ Onaylananlar (${donations.filter(d => d.verificationStatus === 'Approved').length})`,
                    },
                    {
                      id: 'rejected',
                      label: `❌ Reddedilenler (${donations.filter(d => d.verificationStatus === 'Rejected').length})`,
                    },
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setDonationApprovalFilter(filter.id as any)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        donationApprovalFilter === filter.id
                          ? filter.id === 'pending'
                            ? 'bg-red-600 text-white border border-red-400 shadow-lg shadow-red-600/40 scale-105'
                            : 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37] shadow-md'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                <div className="text-xs text-gray-400 font-mono">
                  Güvenli Bağış Doğrulama Denetim Modülü
                </div>
              </div>

              {/* DONATIONS TABLE WITH HIGHLIGHTS & ACTIONS */}
              <div className="glass-card rounded-3xl p-6 border border-white/10 overflow-x-auto">
                <table className="w-full text-left text-xs text-gray-300 min-w-[850px]">
                  <thead className="bg-white/5 text-[#D4AF37] uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-3">Makbuz / POS Ref</th>
                      <th className="p-3">Bağışçı / Üye Eşleşmesi</th>
                      <th className="p-3">Kampanya & Tutar</th>
                      <th className="p-3">Ödeme Yöntemi</th>
                      <th className="p-3">GÜVENLİK VE ONAY DURUMU</th>
                      <th className="p-3 text-right">Yönetim Kurulu Aksiyonları</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {donations
                      .filter((d) => {
                        if (donationApprovalFilter === 'pending') return !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval';
                        if (donationApprovalFilter === 'approved') return d.verificationStatus === 'Approved';
                        if (donationApprovalFilter === 'rejected') return d.verificationStatus === 'Rejected';
                        return true;
                      })
                      .map((d) => {
                        const isPending = !d.verificationStatus || d.verificationStatus === 'Pending_Admin_Approval';
                        const isApproved = d.verificationStatus === 'Approved';
                        const isRejected = d.verificationStatus === 'Rejected';

                        return (
                          <tr
                            key={d.id}
                            className={`transition-all ${
                              isPending
                                ? 'bg-red-950/40 border-l-4 border-l-red-500 hover:bg-red-900/50'
                                : isRejected
                                ? 'bg-red-950/60 border-l-4 border-l-red-600 opacity-90 hover:bg-red-900/60'
                                : 'hover:bg-white/5 border-l-4 border-l-emerald-500'
                            }`}
                          >
                            <td className="p-3">
                              <span className="font-mono text-white font-bold block">{d.receiptNo}</span>
                              <span className="text-[10px] text-gray-400">{d.date}</span>
                              {d.mandateId && (
                                <span className="text-[9px] font-mono text-[#D4AF37] block">Ref: {d.mandateId}</span>
                              )}
                            </td>

                            <td className="p-3">
                              <div className="font-bold text-white text-sm flex items-center gap-1.5">
                                {isPending && <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 animate-bounce" />}
                                <span>{d.donorName}</span>
                              </div>
                              <div className="text-[11px] text-gray-300 font-mono">{d.donorEmail}</div>
                              {d.memberFullNameMatched && (
                                <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-md">
                                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                                  <span>Eşleşen Üye: {d.memberFullNameMatched}</span>
                                </div>
                              )}
                              {d.cardHolderNameMatched && (
                                <div className="mt-0.5 text-[10px] text-amber-200 block font-mono">
                                  💳 Kart Sahibi: {d.cardHolderNameMatched}
                                </div>
                              )}
                            </td>

                            <td className="p-3">
                              <div className="font-medium text-gray-200">{d.campaign}</div>
                              <div className="font-extrabold text-[#D4AF37] text-sm mt-0.5">
                                €{d.amount} {d.currency}
                              </div>
                            </td>

                            <td className="p-3">
                              <span className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-gray-200 font-medium inline-block">
                                {d.paymentMethod}
                              </span>
                            </td>

                            <td className="p-3">
                              {/* VISUAL STATUS INDICATORS */}
                              {isPending && (
                                <div className="space-y-1">
                                  <span className="px-3 py-1 rounded-full bg-red-500/30 text-red-200 border-2 border-red-500 font-extrabold text-[11px] inline-flex items-center gap-1.5 shadow-lg shadow-red-500/30 animate-pulse">
                                    <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
                                    <span>🚨 ONAY VE DENETİM BEKLİYOR (RED ALARM)</span>
                                  </span>
                                  <p className="text-[10px] text-red-200 font-medium">
                                    Sunucu doğrulamasından geçti; Yönetim Kurulu onayı gerekiyor.
                                  </p>
                                </div>
                              )}
                              {isApproved && (
                                <div className="space-y-1">
                                  <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold text-[11px] inline-flex items-center gap-1.5 shadow-sm">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                    <span>✅ TAMAMLANDI VE YÖNETİMCE ONAYLANDI</span>
                                  </span>
                                  <p className="text-[10px] text-emerald-300/80">
                                    Resmi bağış makbuzu düzenlendi ve muhasebeleştirildi.
                                  </p>
                                </div>
                              )}
                              {isRejected && (
                                <div className="space-y-1">
                                  <span className="px-3 py-1 rounded-full bg-red-900/80 text-red-200 border border-red-500 font-bold text-[11px] inline-flex items-center gap-1.5 shadow-sm">
                                    <XCircle className="w-3.5 h-3.5 text-red-400" />
                                    <span>❌ REDDEDİLDİ / ŞÜPHELİ İŞLEM İPTAL EDİLDİ</span>
                                  </span>
                                  <p className="text-[10px] text-red-300">
                                    İsim uyuşmazlığı veya yönetim kararıyla engellendi.
                                  </p>
                                </div>
                              )}
                              {d.verificationNotes && (
                                <p className="text-[10px] text-gray-300 mt-1 max-w-xs leading-tight font-mono bg-black/30 p-1.5 rounded border border-white/5">
                                  {d.verificationNotes}
                                </p>
                              )}
                            </td>

                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {isPending && (
                                  <>
                                    <button
                                      onClick={() => handleApproveDonation(d.id)}
                                      className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs flex items-center gap-1 shadow-md transition-all hover:scale-105 cursor-pointer border border-emerald-400/50"
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                      <span>✓ Onayla</span>
                                    </button>
                                    <button
                                      onClick={() => handleRejectDonation(d.id)}
                                      className="px-3 py-1.5 rounded-xl bg-red-900 hover:bg-red-800 text-red-100 font-extrabold text-xs flex items-center gap-1 shadow-md transition-all hover:scale-105 cursor-pointer border border-red-600"
                                    >
                                      <XCircle className="w-3.5 h-3.5" />
                                      <span>Reddet</span>
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => setSelectedAdminReceipt({
                                    receiptNo: d.receiptNo,
                                    payerName: d.donorName,
                                    memberNo: d.memberFullNameMatched ? 'KKDD-2026-0042' : undefined,
                                    amount: `${d.amount} ${d.currency}`,
                                    campaign: d.campaign,
                                    paymentMethod: d.paymentMethod,
                                    date: d.date,
                                  })}
                                  className="px-2.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-[#D4AF37] font-bold text-xs flex items-center gap-1 border border-[#D4AF37]/30 transition-all cursor-pointer"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  <span>Makbuz</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 8. GALERİ */}
      {/* ========================================================= */}
      {activeTab === 'gallery' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { id: 'photos', label: '📷 Fotoğraf Galerisi' },
              { id: 'videos', label: '🎬 Video Galerisi' },
              { id: 'add_photo', label: '➕ Fotoğraf / Resim Ekle' },
              { id: 'add_video', label: '🎥 Video Linki Ekle' },
              { id: 'new_album', label: '📁 Yeni Albüm Oluştur' },
            ].map((st) => (
              <button
                key={st.id}
                onClick={() => setGallerySubTab(st.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  gallerySubTab === st.id
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {gallerySubTab === 'add_photo' ? (
            <form onSubmit={handleCreatePhoto} className="glass-card rounded-3xl p-6 border border-[#D4AF37]/30 space-y-4 text-xs">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading border-b border-white/10 pb-3 flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                <span>Galeriden Yeni Fotoğraf / Resim Ekle</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Resim Başlığı (TR)</label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Kürt Dil Şenliği Açılış Töreni"
                    value={photoForm.titleTR}
                    onChange={(e) => setPhotoForm({ ...photoForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Resim Başlığı (Kürtçe - Kurdî)</label>
                  <input
                    type="text"
                    placeholder="Örn: Vekirina Cıvaka Zimanê Kurdî"
                    value={photoForm.titleKM}
                    onChange={(e) => setPhotoForm({ ...photoForm, titleKM: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <ImageUploadInput
                    label="Resim / Fotoğraf (Bilgisayar veya Telefondan Seçin veya URL Yapıştırın)"
                    value={photoForm.url}
                    onChange={(url) => setPhotoForm({ ...photoForm, url: url })}
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori / Albüm</label>
                  <select
                    value={photoForm.category}
                    onChange={(e) => setPhotoForm({ ...photoForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    <option value="Kültür Şenliği">Kültür Şenliği</option>
                    <option value="Dil Kursları">Dil Kursları</option>
                    <option value="Konser & Müzik">Konser & Müzik</option>
                    <option value="Seminer & Panel">Seminer & Panel</option>
                    <option value="Sergi & Tiyatro">Sergi & Tiyatro</option>
                    <option value="Genel Arşiv">Genel Arşiv</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Tarih</label>
                  <input
                    type="date"
                    value={photoForm.date}
                    onChange={(e) => setPhotoForm({ ...photoForm, date: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>

              {photoForm.url && (
                <div className="p-3 rounded-2xl bg-black/40 border border-white/10">
                  <span className="text-[11px] font-bold text-emerald-400 block mb-2">Önizleme:</span>
                  <img src={photoForm.url} alt="Önizleme" className="max-h-48 rounded-xl object-cover" />
                </div>
              )}

              <button type="submit" className="btn-primary py-2.5 px-6">
                <Save className="w-4 h-4" />
                <span>Fotoğrafı Galeride Yayınla</span>
              </button>
            </form>
          ) : gallerySubTab === 'add_video' ? (
            <form onSubmit={handleCreateVideo} className="glass-card rounded-3xl p-6 border border-[#D4AF37]/30 space-y-4 text-xs">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading border-b border-white/10 pb-3 flex items-center gap-2">
                <Video className="w-5 h-5" />
                <span>Galeriden Yeni Video Bağlantısı Ekle</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Video Başlığı (TR)</label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Dengbêj Dinletisi ve Kültür Gecesi Kaydı"
                    value={videoForm.titleTR}
                    onChange={(e) => setVideoForm({ ...videoForm, titleTR: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Video Başlığı (Kürtçe - Kurdî)</label>
                  <input
                    type="text"
                    placeholder="Örn: Şeva Dengbêjan û Hunera Kurdî"
                    value={videoForm.titleKM}
                    onChange={(e) => setVideoForm({ ...videoForm, titleKM: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-gray-300 block mb-1 font-bold">YouTube / Vimeo Video Adresi</label>
                  <input
                    type="text"
                    required
                    placeholder="https://www.youtube.com/watch?v=... veya https://youtu.be/..."
                    value={videoForm.videoUrl}
                    onChange={(e) => setVideoForm({ ...videoForm, videoUrl: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kategori</label>
                  <select
                    value={videoForm.category}
                    onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  >
                    <option value="Dengbêj & Müzik">Dengbêj & Müzik</option>
                    <option value="Konferans & Seminer">Konferans & Seminer</option>
                    <option value="Kültür & Sanat">Kültür & Sanat</option>
                    <option value="Belgesel & Arşiv">Belgesel & Arşiv</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Yayın Tarihi</label>
                  <input
                    type="date"
                    value={videoForm.date}
                    onChange={(e) => setVideoForm({ ...videoForm, date: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>

              {videoForm.videoUrl && (
                <div className="p-3 rounded-2xl bg-black/40 border border-white/10">
                  <span className="text-[11px] font-bold text-emerald-400 block mb-2">Video Önizlemesi:</span>
                  <iframe
                    src={parseStreamEmbedUrl(videoForm.videoUrl)}
                    title="Video Preview"
                    className="w-full max-w-md aspect-video rounded-xl border border-white/10"
                    allowFullScreen
                  />
                </div>
              )}

              <button type="submit" className="btn-primary py-2.5 px-6">
                <Save className="w-4 h-4" />
                <span>Videoyu Galeride Yayınla</span>
              </button>
            </form>
          ) : gallerySubTab === 'videos' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.filter((g) => g.type === 'video').map((v) => (
                <div key={v.id} className="glass-card rounded-2xl overflow-hidden border border-white/10 group space-y-2 p-3">
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
                    <iframe src={parseStreamEmbedUrl(v.url)} title={v.title[currentLang] || v.title.TR} className="w-full h-full border-0" allowFullScreen />
                  </div>
                  <div className="flex justify-between items-start gap-2 pt-1">
                    <div>
                      <h5 className="font-bold text-xs text-white line-clamp-1">{v.title[currentLang] || v.title.TR}</h5>
                      <p className="text-[10px] text-[#D4AF37]">{v.category} • {v.date}</p>
                    </div>
                    <button
                      onClick={async () => {
                        if (confirm('Bu videoyu galeriden silmek istediğinize emin misiniz?')) {
                          setGallery(gallery.filter((x) => x.id !== v.id));
                          try {
                            await deleteGalleryApi(v.id);
                          } catch (err) {
                            console.error('Delete gallery video error:', err);
                          }
                        }
                      }}
                      className="p-1.5 rounded-lg bg-red-950/80 text-red-400 hover:bg-red-900"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Photos list (default) */
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.filter((g) => g.type === 'photo' || !g.type).map((g) => (
                <div key={g.id} className="glass-card rounded-2xl overflow-hidden border border-white/10 group">
                  <div className="relative aspect-video">
                    <img src={g.url} alt="G" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <button
                      onClick={async () => {
                        if (confirm('Bu fotoğrafı galeriden silmek istediğinize emin misiniz?')) {
                          setGallery(gallery.filter((x) => x.id !== g.id));
                          try {
                            await deleteGalleryApi(g.id);
                          } catch (err) {
                            console.error('Delete gallery photo error:', err);
                          }
                        }
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-950/80 text-red-400 hover:bg-red-900 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="p-3">
                    <h5 className="font-bold text-xs text-white truncate">{g.title[currentLang] || g.title.TR}</h5>
                    <p className="text-[10px] text-[#D4AF37]">{g.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 9. BELGELER */}
      {/* ========================================================= */}
      {activeTab === 'documents' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <form onSubmit={handleCreateDocument} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 text-xs">
            <h3 className="text-base font-bold text-[#D4AF37] font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              <span>Yeni Evrak / Doküman Yükle</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-300 block mb-1 font-bold">Doküman Adı</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: 2026 Dernek Ana Tüzüğü ve Yönetmelik"
                  value={documentForm.titleTR}
                  onChange={(e) => setDocumentForm({ ...documentForm, titleTR: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-1 font-bold">Kategori</label>
                <select
                  value={documentForm.category}
                  onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                >
                  <option value="Tüzük & Yönetmelik">Tüzük & Yönetmelik</option>
                  <option value="Mali Raporlar">Mali Raporlar</option>
                  <option value="Başvuru Formları">Başvuru Formları</option>
                  <option value="Ders Materyalleri">Ders Materyalleri</option>
                </select>
              </div>
              <div>
                <label className="text-gray-300 block mb-1 font-bold">Dosya Formatı</label>
                <select
                  value={documentForm.fileType}
                  onChange={(e) => setDocumentForm({ ...documentForm, fileType: e.target.value as any })}
                  className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                >
                  <option value="PDF">PDF Dokümanı</option>
                  <option value="Word">Word Belgesi (.docx)</option>
                  <option value="Excel">Excel Tablosu (.xlsx)</option>
                  <option value="ZIP">ZIP Arşivi (.zip)</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary py-2 px-6">
              <Save className="w-4 h-4" />
              <span>Dokümanı Depoya Yükle</span>
            </button>
          </form>

          <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
            <h3 className="text-base font-bold text-white font-heading">Resmi Tüzük ve Evrak Deposu ({documents.length} Dosya)</h3>
            <div className="divide-y divide-white/5 text-xs text-gray-300">
              {documents.map((doc) => (
                <div key={doc.id} className="py-3 flex items-center justify-between gap-4">
                  <div>
                    <h5 className="font-bold text-white">{doc.title[currentLang] || doc.title.TR}</h5>
                    <p className="text-[10px] text-gray-400">{doc.category} • {doc.fileType} • {doc.fileSize} • {doc.date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => alert(`${doc.title.TR} indiriliyor...`)} className="btn-secondary text-xs py-1.5 px-3">
                      <Download className="w-3.5 h-3.5" />
                      <span>İndir ({doc.downloadCount})</span>
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm('Bu dokümanı silmek istediğinize emin misiniz?')) {
                          setDocuments(documents.filter((x) => x.id !== doc.id));
                          try {
                            await deleteDocumentApi(doc.id);
                          } catch (err) {
                            console.error('Delete document error:', err);
                          }
                        }
                      }}
                      className="p-1.5 rounded-lg bg-red-950/80 text-red-400 hover:bg-red-900"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 10. SİTE YÖNETİMİ */}
      {/* ========================================================= */}
      {activeTab === 'settings' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {settingsSavedToast && (
            <div className="p-4 rounded-2xl bg-emerald-500/20 border-2 border-emerald-500 text-emerald-300 text-sm font-bold flex items-center gap-3 animate-bounce">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <span>✓ Site Ayarları Başarıyla Kaydedildi ve Tüm Sitede Güncellendi!</span>
            </div>
          )}

          <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <h2 className="text-lg font-bold text-white font-heading flex items-center gap-2">
                <Sliders className="w-5 h-5 text-[#D4AF37]" />
                <span>Site Yönetim Ayarları & Sistem Yapılandırması</span>
              </h2>
              <button onClick={handleSaveSiteSettings} className="btn-primary py-2 px-6 shadow-xl">
                <Save className="w-4 h-4" />
                <span>Tüm Ayarları Kaydet</span>
              </button>
            </div>

            <form onSubmit={handleSaveSiteSettings} className="space-y-6 text-xs">
              {/* Sub-section 1: Genel Bilgiler */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <h3 className="text-sm font-bold text-[#D4AF37]">1. Kurum / Dernek Genel Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Kurum / Dernek Adı</label>
                    <input
                      type="text"
                      required
                      value={siteSettingsForm.siteName}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, siteName: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Dernek Sloganı / Alt Başlık</label>
                    <input
                      type="text"
                      value={siteSettingsForm.subTitle}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, subTitle: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Resmi İletişim E-Posta</label>
                    <input
                      type="email"
                      required
                      value={siteSettingsForm.email}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, email: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Telefon Numarası</label>
                    <input
                      type="text"
                      value={siteSettingsForm.phone}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, phone: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">WhatsApp Destek Hattı</label>
                    <input
                      type="text"
                      value={siteSettingsForm.whatsapp}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, whatsapp: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Genel Dernek Adresi</label>
                    <input
                      type="text"
                      value={siteSettingsForm.address}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, address: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Sub-section 2: Logo Bilgileri */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <h3 className="text-sm font-bold text-[#D4AF37]">2. Logo Metinleri & Görsel Yapılandırması</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Logo Satır 1</label>
                    <input
                      type="text"
                      value={siteSettingsForm.logoText1}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logoText1: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Logo Satır 2</label>
                    <input
                      type="text"
                      value={siteSettingsForm.logoText2}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logoText2: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Logo Satır 3 (İngilizce/Fransızca)</label>
                    <input
                      type="text"
                      value={siteSettingsForm.logoText3}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, logoText3: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-white/10">
                  <ImageUploadInput
                    label="Dernek Logosu / Özel Amblem (Bilgisayar veya Telefondan Yükleyin)"
                    value={siteSettingsForm.logoImageUrl || ''}
                    onChange={(url) => setSiteSettingsForm({ ...siteSettingsForm, logoImageUrl: url })}
                    placeholder="Resim URL adresi veya cihazınızdan özel logo dosyası seçin..."
                  />
                </div>
              </div>

              {/* Sub-section 3: Sosyal Medya */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <h3 className="text-sm font-bold text-[#D4AF37]">3. Sosyal Medya Hesap Linkleri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Facebook</label>
                    <input
                      type="text"
                      value={siteSettingsForm.facebook}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, facebook: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Instagram</label>
                    <input
                      type="text"
                      value={siteSettingsForm.instagram}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, instagram: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">YouTube</label>
                    <input
                      type="text"
                      value={siteSettingsForm.youtube}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, youtube: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">X / Twitter</label>
                    <input
                      type="text"
                      value={siteSettingsForm.twitter}
                      onChange={(e) => setSiteSettingsForm({ ...siteSettingsForm, twitter: e.target.value })}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Sub-section 4: Kredi Kartı & Sanal POS API Ayarları */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-[#D4AF37] flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#D4AF37]" />
                    <span>4. Kredi Kartı Sanal POS & Banka Entegrasyon API Ayarları</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('donations');
                      setDonationsSubTab('pos_settings');
                    }}
                    className="text-[11px] text-[#D4AF37] hover:underline font-bold flex items-center gap-1"
                  >
                    <span>Gelişmiş POS Paneline Git &rarr;</span>
                  </button>
                </div>
                <p className="text-[11px] text-gray-300">
                  Kredi kartı ödemelerinin sorunsuz işlenebilmesi için dernek Sanal POS ve SEPA API/Mağaza güvenlik anahtarlarını girin.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="text-gray-300 font-bold block mb-1">POS Sağlayıcısı</label>
                    <select
                      value={posForm.activeProvider || 'sanal_pos'}
                      onChange={(e) => setPosForm({ ...posForm, activeProvider: e.target.value as any })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                    >
                      <option value="sanal_pos">Dernek Sanal POS</option>
                      <option value="sepa">Resmi SEPA / IBAN Entegrasyonu</option>
                      <option value="custom_pos">Özel POS Entegrasyonu</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Mağaza / Merchant ID</label>
                    <input
                      type="text"
                      value={posForm.merchantId || ''}
                      onChange={(e) => setPosForm({ ...posForm, merchantId: e.target.value })}
                      placeholder="30094812"
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">API Key</label>
                    <input
                      type="text"
                      value={posForm.apiKey || ''}
                      onChange={(e) => setPosForm({ ...posForm, apiKey: e.target.value })}
                      placeholder="pos-live-api-key"
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1 flex items-center justify-between">
                      <span>Secret Key</span>
                      <button
                        type="button"
                        onClick={() => setShowSecretKey(!showSecretKey)}
                        className="text-[#D4AF37] hover:underline text-[10px]"
                      >
                        {showSecretKey ? 'Gizle' : 'Göster'}
                      </button>
                    </label>
                    <input
                      type={showSecretKey ? 'text' : 'password'}
                      value={posForm.secretKey || ''}
                      onChange={(e) => setPosForm({ ...posForm, secretKey: e.target.value })}
                      placeholder="••••••••••••••••"
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Dernek Banka IBAN Numarası (Ödemelerin Aktarılacağı Hesabı)</label>
                    <input
                      type="text"
                      value={posForm.iban || ''}
                      onChange={(e) => setPosForm({ ...posForm, iban: e.target.value })}
                      placeholder="TR33 0006 2000 0000 1234 5678 90"
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 font-bold block mb-1">Çalışma Modu (Ortam Toggles)</label>
                    <button
                      type="button"
                      onClick={() => setPosForm({ ...posForm, testMode: !posForm.testMode })}
                      className={`w-full p-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                        posForm.testMode
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50'
                          : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                      }`}
                    >
                      <span>{posForm.testMode ? '🧪 TEST / SANDBOX MODU (Demo Çekim)' : '🟢 CANLI (PRODUCTION) MOD (Gerçek Banka Çekimi)'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Sub-section 5: Bakım Modu */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-white">Bakım Modu</h3>
                  <p className="text-[11px] text-gray-400">Bakım modunu açtığınızda yöneticiler hariç tüm ziyaretçiler bakım ekranı görür.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSiteSettingsForm({ ...siteSettingsForm, maintenanceMode: !siteSettingsForm.maintenanceMode })}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    siteSettingsForm.maintenanceMode ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  {siteSettingsForm.maintenanceMode ? 'BAKIM MODU AÇIK' : 'PASİF'}
                </button>
              </div>

              <div className="pt-2 flex justify-end">
                <button type="submit" className="btn-primary py-3 px-8 text-sm">
                  <Save className="w-4 h-4" />
                  <span>Site Ayarlarını Kaydet ve Uygula</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 11. YENİ MODÜL: 📺 CANLI YAYIN MODÜLÜ */}
      {/* ========================================================= */}
      {activeTab === 'live' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="glass-card rounded-3xl p-6 border-2 border-red-500/50 bg-gradient-to-r from-red-950/40 via-[#10241F] to-[#081B16] space-y-6 shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center animate-pulse">
                  <Tv className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-white font-heading">
                    📺 Canlı Yayın Yönetim Paneli
                  </h2>
                  <p className="text-xs text-gray-300">
                    YouTube, Facebook, Vimeo ve OBS canlı yayın bağlantılarını anlık yönetin.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-white">Yayın Durumu:</span>
                <button
                  type="button"
                  onClick={() => setLiveForm({ ...liveForm, isLive: !liveForm.isLive })}
                  className={`px-4 py-2 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 ${
                    liveForm.isLive
                      ? 'bg-red-600 text-white shadow-[0_0_20px_rgba(220,38,38,0.6)]'
                      : 'bg-gray-800 text-gray-400'
                  }`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${liveForm.isLive ? 'bg-white animate-ping' : 'bg-gray-500'}`} />
                  <span>{liveForm.isLive ? 'CANLI YAYIN AKTİF' : 'PASİF'}</span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveLiveStream} className="space-y-5 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Yayın Başlığı</label>
                  <input
                    type="text"
                    required
                    value={liveForm.title}
                    onChange={(e) => setLiveForm({ ...liveForm, title: e.target.value })}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                  />
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Platform Seçimi</label>
                  <select
                    value={liveForm.platform}
                    onChange={(e) => setLiveForm({ ...liveForm, platform: e.target.value as any })}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                  >
                    <option value="YouTube">YouTube Live</option>
                    <option value="Facebook">Facebook Live</option>
                    <option value="Vimeo">Vimeo</option>
                    <option value="Zoom">Zoom Webinar</option>
                    <option value="OBS">OBS Studio Custom RTMP</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="text-gray-300 block mb-1 font-bold">Yayın Açıklaması</label>
                  <textarea
                    rows={2}
                    value={liveForm.description}
                    onChange={(e) => setLiveForm({ ...liveForm, description: e.target.value })}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div className="md:col-span-2 p-4 rounded-2xl bg-[#081B16] border border-[#D4AF37]/30 space-y-3">
                  <div>
                    <label className="text-[#D4AF37] block mb-1 font-bold">YouTube / Canlı Yayın Linki</label>
                    <p className="text-[11px] text-gray-400 mb-2">Herhangi bir YouTube canlı yayın veya video linkini buraya yapıştırın (Örn: https://www.youtube.com/live/yZ7eZ8H_a1I?is=jgy0RIN_u-bmXzg1 veya watch/embed linki). Sistem otomatik olarak oynatıcı adresine dönüştürür ve ana sayfada yayına alır.</p>
                    <input
                      type="text"
                      placeholder="https://www.youtube.com/live/yZ7eZ8H_a1I?is=jgy0RIN_u-bmXzg1"
                      value={liveForm.youtubeUrl}
                      onChange={(e) => {
                        const val = e.target.value;
                        const parsed = parseStreamEmbedUrl(val);
                        setLiveForm({
                          ...liveForm,
                          youtubeUrl: val,
                          embedUrl: parsed || val,
                        });
                      }}
                      className="w-full p-3 rounded-xl bg-[#030D0A] border border-[#D4AF37]/50 text-white font-mono text-xs focus:ring-2 focus:ring-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Gömülü Oynatıcı Adresi (Otomatik Oluşturulur)</label>
                    <input
                      type="text"
                      required
                      value={liveForm.embedUrl}
                      onChange={(e) => setLiveForm({ ...liveForm, embedUrl: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#030D0A] border border-white/10 text-gray-300 font-mono text-xs"
                    />
                  </div>

                  {/* Live Stream Preview Player in Admin Panel */}
                  {liveForm.embedUrl && (
                    <div className="pt-2">
                      <span className="block text-[11px] font-bold text-emerald-400 mb-1">
                        ✓ Oynatıcı Önizlemesi (Ana Sayfada Gözükecek Ekran):
                      </span>
                      <div className="w-full max-w-xl aspect-video rounded-xl overflow-hidden border border-white/20 bg-black">
                        <iframe
                          src={parseStreamEmbedUrl(liveForm.embedUrl || liveForm.youtubeUrl)}
                          title="Admin Stream Preview"
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Facebook Canlı Yayın Linki</label>
                  <input
                    type="text"
                    value={liveForm.facebookUrl}
                    onChange={(e) => setLiveForm({ ...liveForm, facebookUrl: e.target.value })}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Tahmini Canlı İzleyici Sayısı</label>
                  <input
                    type="number"
                    value={liveForm.viewerCount}
                    onChange={(e) => setLiveForm({ ...liveForm, viewerCount: Number(e.target.value) })}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>

              {/* OBS Studio RTMP Config Card */}
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                <h4 className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  <span>OBS Studio & Harici Yayın Programı Bilgileri</span>
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 block mb-1 text-[11px]">RTMP Sunucu Adresi</label>
                    <input
                      type="text"
                      value={liveForm.obsRtmpUrl}
                      onChange={(e) => setLiveForm({ ...liveForm, obsRtmpUrl: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 block mb-1 text-[11px]">Yayın Anahtarı (Stream Key)</label>
                    <input
                      type="password"
                      value={liveForm.obsStreamKey}
                      onChange={(e) => setLiveForm({ ...liveForm, obsStreamKey: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Başlama Tarihi</label>
                  <input
                    type="date"
                    value={liveForm.startDate}
                    onChange={(e) => setLiveForm({ ...liveForm, startDate: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Başlama Saati</label>
                  <input
                    type="time"
                    value={liveForm.startTime}
                    onChange={(e) => setLiveForm({ ...liveForm, startTime: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Bitiş Tarihi</label>
                  <input
                    type="date"
                    value={liveForm.endDate}
                    onChange={(e) => setLiveForm({ ...liveForm, endDate: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Bitiş Saati</label>
                  <input
                    type="time"
                    value={liveForm.endTime}
                    onChange={(e) => setLiveForm({ ...liveForm, endTime: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-white/10 flex justify-end">
                <button type="submit" className="btn-primary text-sm py-3 px-6 shadow-xl">
                  <Radio className="w-5 h-5 text-red-400" />
                  <span>Tüm Sitede Canlı Yayın Ayarlarını Güncelle & Aktifleştir</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 12. YÖNETİCİLER (Admin Accounts Management) */}
      {/* ========================================================= */}
      {activeTab === 'admins' && (
        <div className="space-y-8 animate-in fade-in duration-300">
          <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
              <div>
                <span className="px-3 py-1 rounded-full bg-[#0B3D2E] text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/30 uppercase">
                  Güvenlik & Yetkilendirme
                </span>
                <h2 className="text-xl font-extrabold text-white font-heading mt-1">
                  Yönetici Hesapları & Rol Yönetimi ({adminAccounts.length})
                </h2>
                <p className="text-xs text-gray-300">
                  Panel yetkililerini görüntüleyin. Süper Admin hesapları yeni yöneticiler ekleyip silebilir.
                </p>
              </div>
              <div className="p-3 rounded-2xl bg-[#081B16] border border-[#D4AF37]/40 flex items-center gap-3">
                <ShieldAlert className="w-6 h-6 text-[#D4AF37]" />
                <div>
                  <span className="block text-[10px] text-gray-400">Aktif Oturum Sahibi:</span>
                  <div className="flex items-center gap-1.5 font-bold text-xs">
                    <span className={isEditor ? 'text-blue-300' : 'text-emerald-400'}>
                      {currentAdminUser?.fullName || 'Yönetim Kurulu Başkanı (Süper Admin)'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-extrabold border ${
                      isEditor
                        ? 'bg-blue-950 text-blue-300 border-blue-500/40'
                        : 'bg-purple-950 text-purple-300 border-purple-500/40'
                    }`}>
                      {isEditor ? 'İçerik Editörü' : '★ Süper Admin'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Existing Admin Accounts List Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400 font-bold uppercase text-[10px]">
                    <th className="py-3 px-3">Ad Soyad</th>
                    <th className="py-3 px-3">E-Posta Adresi</th>
                    <th className="py-3 px-3">Yetki Rolü</th>
                    <th className="py-3 px-3">Durum</th>
                    <th className="py-3 px-3">Kayıt Tarihi</th>
                    <th className="py-3 px-3">Son Giriş</th>
                    <th className="py-3 px-3 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-gray-300">
                  {adminAccounts.map((acc) => (
                    <tr key={acc.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-3.5 px-3 font-bold text-white flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#0B3D2E] border border-[#D4AF37] flex items-center justify-center text-[10px] font-bold text-[#D4AF37]">
                          {acc.fullName.charAt(0)}
                        </div>
                        <span>{acc.fullName}</span>
                      </td>
                      <td className="py-3.5 px-3 font-mono text-gray-300">{acc.email}</td>
                      <td className="py-3.5 px-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${
                          acc.role === 'SuperAdmin'
                            ? 'bg-purple-950/80 text-purple-300 border-purple-500/50'
                            : acc.role === 'Admin'
                            ? 'bg-emerald-950/80 text-emerald-300 border-emerald-500/50'
                            : 'bg-blue-950/80 text-blue-300 border-blue-500/50'
                        }`}>
                          {acc.role === 'SuperAdmin' ? '★ Süper Admin' : acc.role === 'Admin' ? 'Yönetici' : 'İçerik Editörü'}
                        </span>
                      </td>
                      <td className="py-3.5 px-3">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                          ● {acc.status === 'active' ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="py-3.5 px-3 text-gray-400">{acc.createdAt}</td>
                      <td className="py-3.5 px-3 text-gray-400 font-mono text-[10px]">{acc.lastLogin}</td>
                      <td className="py-3.5 px-3 text-right space-x-2">
                        <button
                          disabled={isEditor}
                          onClick={() => {
                            if (isEditor) {
                              alert('Erişim Engellendi: Yönetici hesaplarını silme yetkisi yalnızca "Süper Admin" rolüne aittir.');
                              return;
                            }
                            if (setAdminAccounts) {
                              if (adminAccounts.length <= 1) {
                                alert('En az bir yönetici kalmalıdır.');
                                return;
                              }
                              setAdminAccounts(adminAccounts.filter((a) => a.id !== acc.id));
                            }
                          }}
                          className={`p-1.5 rounded-xl border text-[10px] font-bold transition-all ${
                            isEditor
                              ? 'bg-gray-800/40 text-gray-500 border-gray-700 cursor-not-allowed opacity-50'
                              : 'bg-red-950/60 hover:bg-red-800 text-red-300 border-red-500/40'
                          }`}
                          title={isEditor ? 'Yönetici silme yetkisi yalnızca Süper Admin içindir' : 'Yöneticiyi Kaldır'}
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Form: Add New Admin Account (Super Admin only) */}
          {isEditor ? (
            <div className="glass-card rounded-3xl p-6 border border-blue-500/30 bg-blue-950/20 space-y-3">
              <div className="flex items-center gap-3 text-blue-300">
                <ShieldAlert className="w-6 h-6 text-[#D4AF37]" />
                <h3 className="text-base font-bold text-white font-heading">
                  Yönetici Hesabı Ekleme Kısıtlandı (İçerik Editörü Rolü)
                </h3>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                Sistemde <strong>İçerik Editörü</strong> rolüyle oturum açmış bulunmaktasınız. Yeni yönetici hesapları tanımlama, yetki seviyelerini belirleme veya mevcut yöneticileri silme işlemleri yalnızca <strong>Süper Admin</strong> yetkisindeki hesaplar için geçerlidir. Editörler tüm modül içeriklerini serbestçe güncelleyebilir.
              </p>
              <div className="pt-2">
                <button
                  onClick={onLogout}
                  className="px-4 py-2.5 rounded-xl bg-[#D4AF37] text-[#081B16] font-extrabold text-xs hover:bg-white transition-all shadow-md inline-flex items-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  <span>Süper Admin Hesabıyla Giriş Yap</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-base font-bold text-[#D4AF37] font-heading flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                <span>Sisteme Yeni Yönetici Hesabı Ekle</span>
              </h3>

              <form onSubmit={handleCreateAdmin} className="space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Yönetici Adı Soyadı</label>
                    <input
                      type="text"
                      required
                      placeholder="Örn: Azad Med"
                      value={newAdminForm.fullName}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, fullName: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Giriş E-Posta Adresi</label>
                    <input
                      type="email"
                      required
                      placeholder="azad@kurdishculture.org"
                      value={newAdminForm.email}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Giriş Parolası / Şifre</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={newAdminForm.password}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Yetki Derecesi / Rol</label>
                    <select
                      value={newAdminForm.role}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, role: e.target.value as any })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                    >
                      <option value="SuperAdmin">★ Süper Admin (Tam Yetki)</option>
                      <option value="Admin">Dernek Yöneticisi (Tüm Modüller)</option>
                      <option value="Editor">İçerik Editörü (Haber/Galeri/Kültür)</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button type="submit" className="btn-primary py-3 px-6 shadow-xl">
                    <UserPlus className="w-4 h-4" />
                    <span>Yeni Yöneticiyi Kaydet ve Yetkilendir</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 13. REKLAM YÖNETİMİ (Advertisement Management) */}
      {/* ========================================================= */}
      {activeTab === 'ads' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* SubTab Navigation */}
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <button
              onClick={() => setAdsSubTab('list')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                adsSubTab === 'list'
                  ? 'bg-[#D4AF37] text-[#081B16] font-extrabold shadow-md'
                  : 'bg-[#081B16] text-gray-300 hover:text-white border border-white/10'
              }`}
            >
              <List className="w-4 h-4" />
              <span>Yayınlanan Reklamlar ({ads.length})</span>
            </button>
            <button
              onClick={() => {
                setEditingAdId(null);
                setAdForm({
                  companyName: '',
                  logo: '',
                  image: '',
                  category: 'Restoran & Gıda',
                  address: '',
                  phone: '',
                  whatsapp: '',
                  email: '',
                  website: '',
                  contactPerson: '',
                  summary: '',
                  content: '',
                  startDate: new Date().toISOString().split('T')[0],
                  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  isActive: true,
                });
                setAdsSubTab('add');
              }}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                adsSubTab === 'add'
                  ? 'bg-[#D4AF37] text-[#081B16] font-extrabold shadow-md'
                  : 'bg-[#081B16] text-gray-300 hover:text-white border border-white/10'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>{editingAdId ? 'Reklamı Düzenle' : 'Yeni Firma / Şahıs Reklamı Ekle'}</span>
            </button>
          </div>

          {/* SubTab: List */}
          {adsSubTab === 'list' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-3">
                  <Megaphone className="w-6 h-6 text-[#D4AF37]" />
                  <div>
                    <h2 className="text-lg font-extrabold text-white font-heading">
                      Sitede Yayınlanan Firma & Şahıs Reklamları
                    </h2>
                    <p className="text-xs text-gray-300">
                      Reklamlar eklenme tarihinden itibaren varsayılan 1 ay süreyle yayınlanır.
                    </p>
                  </div>
                </div>
              </div>

              {ads.length === 0 ? (
                <div className="text-center py-10 space-y-3">
                  <Megaphone className="w-10 h-10 text-gray-500 mx-auto" />
                  <p className="text-sm font-semibold text-gray-300">Henüz yayınlanmış bir reklam bulunmuyor.</p>
                  <button
                    onClick={() => setAdsSubTab('add')}
                    className="btn-primary text-xs py-2 px-4 inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    <span>İlk Reklamı Ekle</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {ads.map((ad) => (
                    <div
                      key={ad.id}
                      className={`glass-card rounded-2xl border p-5 space-y-4 relative flex flex-col justify-between ${
                        ad.isActive ? 'border-[#D4AF37]/50 bg-[#081B16]/90' : 'border-gray-700 bg-gray-900/50 opacity-60'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            {ad.logo ? (
                              <img src={ad.logo} alt={ad.companyName} className="w-10 h-10 rounded-xl object-cover border border-white/20" />
                            ) : (
                              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center font-bold text-[#D4AF37]">
                                {ad.companyName.charAt(0)}
                              </div>
                            )}
                            <div>
                              <h3 className="font-extrabold text-white text-sm font-heading">{ad.companyName}</h3>
                              <span className="text-[10px] font-bold text-[#D4AF37] px-2 py-0.5 rounded bg-black/40">
                                {ad.category}
                              </span>
                            </div>
                          </div>

                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            ad.isActive ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-gray-800 text-gray-400'
                          }`}>
                            {ad.isActive ? 'YAYINDA' : 'PASİF'}
                          </span>
                        </div>

                        {ad.image && (
                          <img src={ad.image} alt={ad.companyName} className="w-full h-32 object-cover rounded-xl border border-white/10" />
                        )}

                        <p className="text-xs text-gray-300 line-clamp-2">{ad.summary}</p>

                        <div className="text-[11px] text-gray-400 space-y-1 bg-black/30 p-2.5 rounded-xl border border-white/5 font-mono">
                          <p>📍 {ad.address || 'Adres belirtilmemiş'}</p>
                          <p>📞 {ad.phone || 'Telefon yok'}</p>
                          <p className="text-[#D4AF37]">📅 Yayın: {ad.startDate} - {ad.endDate}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/10">
                        <button
                          onClick={() => handleToggleAdStatus(ad)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            ad.isActive ? 'bg-amber-950/60 text-amber-300 border border-amber-500/40' : 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40'
                          }`}
                        >
                          {ad.isActive ? 'Pasife Al' : 'Yeniden Yayınla (1 Ay)'}
                        </button>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingAdId(ad.id);
                              setAdForm({
                                companyName: ad.companyName,
                                logo: ad.logo || '',
                                image: ad.image || '',
                                category: ad.category || 'Restoran & Gıda',
                                address: ad.address || '',
                                phone: ad.phone || '',
                                whatsapp: ad.whatsapp || '',
                                email: ad.email || '',
                                website: ad.website || '',
                                contactPerson: ad.contactPerson || '',
                                summary: ad.summary || '',
                                content: ad.content || '',
                                startDate: ad.startDate || new Date().toISOString().split('T')[0],
                                endDate: ad.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                                isActive: ad.isActive,
                              });
                              setAdsSubTab('add');
                            }}
                            className="p-1.5 rounded-lg bg-blue-950/60 hover:bg-blue-800 text-blue-300 border border-blue-500/40"
                            title="Düzenle"
                          >
                            <FileEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAd(ad.id, ad.companyName)}
                            className="p-1.5 rounded-lg bg-rose-950/60 hover:bg-rose-800 text-rose-300 border border-rose-500/40"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SubTab: Add / Edit Form */}
          {adsSubTab === 'add' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
              <h3 className="text-base font-extrabold text-[#D4AF37] font-heading flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                <span>{editingAdId ? 'Reklam Bilgilerini Güncelle' : 'Yeni Firma veya Şahıs Reklamı Oluştur'}</span>
              </h3>

              <form onSubmit={handleSaveAd} className="space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Firma / Şahıs Adı *</label>
                    <input
                      type="text"
                      required
                      placeholder="Örn: Mantes Döner & Grill"
                      value={adForm.companyName}
                      onChange={(e) => setAdForm({ ...adForm, companyName: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Sektör / Kategori</label>
                    <select
                      value={adForm.category}
                      onChange={(e) => setAdForm({ ...adForm, category: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-bold"
                    >
                      <option value="Restoran & Gıda">Restoran & Gıda</option>
                      <option value="İnşaat & Mimarlık">İnşaat & Mimarlık</option>
                      <option value="Hukuk & Danışmanlık">Hukuk & Danışmanlık</option>
                      <option value="Sağlık & Güzellik">Sağlık & Güzellik</option>
                      <option value="Otomotiv & Lojistik">Otomotiv & Lojistik</option>
                      <option value="Eğitim & Tercüme">Eğitim & Tercüme</option>
                      <option value="Teknoloji & Medya">Teknoloji & Medya</option>
                      <option value="Diğer Hizmetler">Diğer Hizmetler</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Yetkili / İletişim Kişisi</label>
                    <input
                      type="text"
                      placeholder="Örn: Ahmet Yılmaz"
                      value={adForm.contactPerson}
                      onChange={(e) => setAdForm({ ...adForm, contactPerson: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Telefon Numarası</label>
                    <input
                      type="text"
                      placeholder="+33 6 12 34 56 78"
                      value={adForm.phone}
                      onChange={(e) => setAdForm({ ...adForm, phone: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">WhatsApp Hattı</label>
                    <input
                      type="text"
                      placeholder="+33 6 12 34 56 78"
                      value={adForm.whatsapp}
                      onChange={(e) => setAdForm({ ...adForm, whatsapp: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Web Sitesi URL</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={adForm.website}
                      onChange={(e) => setAdForm({ ...adForm, website: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ImageUploadInput
                    label="Firma Logosu (İsteğe Bağlı)"
                    value={adForm.logo}
                    onChange={(url) => setAdForm({ ...adForm, logo: url })}
                  />
                  <ImageUploadInput
                    label="Reklam Görseli / Afişi *"
                    value={adForm.image}
                    onChange={(url) => setAdForm({ ...adForm, image: url })}
                  />
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Adres Bilgisi</label>
                  <input
                    type="text"
                    placeholder="250 Boulevard Marechal Juin, 78200 Mantes-la-Jolie"
                    value={adForm.address}
                    onChange={(e) => setAdForm({ ...adForm, address: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="text-gray-300 block mb-1 font-bold">Kısa Açıklama / Slogan</label>
                  <textarea
                    rows={2}
                    placeholder="Özel soslu döner ve ızgara çeşitlerimizle hizmetinizdeyiz."
                    value={adForm.summary}
                    onChange={(e) => setAdForm({ ...adForm, summary: e.target.value })}
                    className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                  <AiTextAssistant
                    fieldLabel="Reklam Açıklaması / Slogan"
                    currentLang={currentLang}
                    onTransfer={(generated) => setAdForm((prev) => ({ ...prev, summary: generated }))}
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setAdsSubTab('list')}
                    className="px-4 py-2.5 rounded-xl bg-gray-800 text-gray-300 text-xs font-bold"
                  >
                    İptal
                  </button>
                  <button type="submit" className="btn-primary py-2.5 px-6 text-xs">
                    <Save className="w-4 h-4" />
                    <span>{editingAdId ? 'Güncelle' : 'Kaydet ve 1 Aylık Yayınla'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ========================================================= */}
      {/* 14. BİLET YÖNETİMİ & QR KONTROL (Tickets Management) */}
      {/* ========================================================= */}
      {activeTab === 'tickets' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* SubTab Navigation */}
          <div className="flex flex-wrap items-center gap-3 border-b border-white/10 pb-4">
            <button
              onClick={() => setTicketsSubTab('bilet_olusturma')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                ticketsSubTab === 'bilet_olusturma'
                  ? 'bg-gradient-to-r from-[#38BDF8] to-[#0284C7] text-[#0B1426] font-black shadow-md'
                  : 'bg-[#081B16] text-gray-300 hover:text-white border border-white/10'
              }`}
            >
              <Ticket className="w-4 h-4" />
              <span>1. Bilet Oluşturma ({eventTickets.length})</span>
            </button>

            <button
              onClick={() => setTicketsSubTab('bilet_siparisleri')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                ticketsSubTab === 'bilet_siparisleri'
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-[#0B1426] font-black shadow-md'
                  : 'bg-[#081B16] text-gray-300 hover:text-white border border-white/10'
              }`}
            >
              <ListCheck className="w-4 h-4" />
              <span>2. Siparişler & Yönetim Onayı ({userPurchases.length})</span>
            </button>

            <button
              onClick={() => setTicketsSubTab('qr_kontrol')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                ticketsSubTab === 'qr_kontrol'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black shadow-md animate-pulse'
                  : 'bg-[#081B16] text-gray-300 hover:text-white border border-white/10'
              }`}
            >
              <QrCode className="w-4 h-4" />
              <span>3. QR Bilet Kontrol / Kapı Scanner</span>
            </button>
          </div>

          {/* SUBTAB 1: BİLET OLUŞTURMA */}
          {ticketsSubTab === 'bilet_olusturma' && (
            <div className="space-y-6">
              {/* Form */}
              <div className="glass-card rounded-3xl p-6 border border-[#38BDF8]/40 bg-[#0B1426]/90 space-y-4">
                <div className="flex items-center gap-3 border-b border-white/10 pb-3">
                  <Ticket className="w-6 h-6 text-[#38BDF8]" />
                  <div>
                    <h3 className="text-base font-extrabold text-white font-heading">
                      {editingTicketId ? 'Etkinlik Biletini Düzenle' : 'Yeni QR Kodlu Etkinlik Bileti Tanımla'}
                    </h3>
                    <p className="text-xs text-gray-300">
                      Sitede online bilet satışında görünecek etkinlik ve bilet detaylarını giriniz.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSaveEventTicket} className="space-y-4 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Etkinlik İsmi *</label>
                      <input
                        type="text"
                        required
                        placeholder="Örn: 15. Newroz Kültür Festivali"
                        value={ticketForm.eventName}
                        onChange={(e) => setTicketForm({ ...ticketForm, eventName: e.target.value })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Bilet İsmi / Kategorisi *</label>
                      <input
                        type="text"
                        required
                        placeholder="Örn: Genel Giriş Bileti"
                        value={ticketForm.ticketTitle}
                        onChange={(e) => setTicketForm({ ...ticketForm, ticketTitle: e.target.value })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Bilet Fiyatı & Para Birimi *</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          required
                          min={0}
                          value={ticketForm.price}
                          onChange={(e) => setTicketForm({ ...ticketForm, price: Number(e.target.value) })}
                          className="w-2/3 p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                        />
                        <select
                          value={ticketForm.currency}
                          onChange={(e) => setTicketForm({ ...ticketForm, currency: e.target.value })}
                          className="w-1/3 p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                        >
                          <option value="€">EUR (€)</option>
                          <option value="₺">TRY (₺)</option>
                          <option value="$">USD ($)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Etkinlik Tarihi *</label>
                      <input
                        type="date"
                        required
                        value={ticketForm.date}
                        onChange={(e) => setTicketForm({ ...ticketForm, date: e.target.value })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Başlama Saati</label>
                      <input
                        type="time"
                        value={ticketForm.time}
                        onChange={(e) => setTicketForm({ ...ticketForm, time: e.target.value })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>

                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Kontenjan / Bilet Adedi</label>
                      <input
                        type="number"
                        min={1}
                        value={ticketForm.quota}
                        onChange={(e) => setTicketForm({ ...ticketForm, quota: Number(e.target.value) })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-300 block mb-1 font-bold">Etkinlik Adresi / Salon *</label>
                      <input
                        type="text"
                        required
                        placeholder="Örn: Mantes-la-Jolie Kültür Merkezi Salon A"
                        value={ticketForm.address}
                        onChange={(e) => setTicketForm({ ...ticketForm, address: e.target.value })}
                        className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white font-bold"
                      />
                    </div>

                    <ImageUploadInput
                      label="Bilet Afiş Görseli URL"
                      value={ticketForm.image}
                      onChange={(url) => setTicketForm({ ...ticketForm, image: url })}
                    />
                  </div>

                  <div>
                    <label className="text-gray-300 block mb-1 font-bold">Bilet Açıklaması & Kurallar</label>
                    <textarea
                      rows={2}
                      placeholder="Etkinlik kuralları, yaş sınırı ve kapı açılış saati gibi bilgiler..."
                      value={ticketForm.description}
                      onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                      className="w-full p-2.5 rounded-xl bg-[#081B16] border border-white/20 text-white"
                    />
                    <AiTextAssistant
                      fieldLabel="Bilet Kuralları & Açıklama"
                      currentLang={currentLang}
                      onTransfer={(generated) => setTicketForm((prev) => ({ ...prev, description: generated }))}
                    />
                  </div>

                  <div className="pt-2 flex items-center justify-end gap-3">
                    {editingTicketId && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingTicketId(null);
                          setTicketForm({
                            eventName: '',
                            ticketTitle: '',
                            address: '',
                            date: new Date().toISOString().split('T')[0],
                            time: '19:00',
                            price: 20,
                            currency: '€',
                            image: '',
                            description: '',
                            quota: 200,
                            isActive: true,
                          });
                        }}
                        className="px-4 py-2.5 rounded-xl bg-gray-800 text-gray-300 text-xs font-bold"
                      >
                        İptal
                      </button>
                    )}
                    <button
                      type="submit"
                      className="px-6 py-3 rounded-2xl bg-gradient-to-r from-[#38BDF8] to-[#0284C7] hover:from-[#7DD3FC] hover:to-[#38BDF8] text-[#0B1426] font-black text-xs uppercase tracking-wider shadow-lg transition-all"
                    >
                      {editingTicketId ? 'Güncelle' : 'Bileti Oluştur ve Satışa Aç'}
                    </button>
                  </div>
                </form>
              </div>

              {/* List of Created Tickets */}
              <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
                <h3 className="text-sm font-extrabold text-white uppercase tracking-wider">
                  Tanımlanmış Etkinlik Biletleri ({eventTickets.length})
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {eventTickets.map((t) => (
                    <div
                      key={t.id}
                      className="glass-card rounded-2xl p-4 border border-white/10 bg-[#0B1426] space-y-3 relative flex flex-col justify-between"
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[10px] font-bold text-[#38BDF8] uppercase tracking-wider">
                            {t.eventName}
                          </span>
                          <span className="px-2.5 py-0.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs">
                            {t.price} {t.currency || '€'}
                          </span>
                        </div>
                        <h4 className="font-extrabold text-white text-sm font-heading">{t.ticketTitle}</h4>
                        <p className="text-[11px] text-gray-300">📅 {t.date} {t.time} • 📍 {t.address}</p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-white/10 text-xs">
                        <span className="text-[10px] text-gray-400">Kontenjan: {t.quota}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingTicketId(t.id);
                              setTicketForm({
                                eventName: t.eventName,
                                ticketTitle: t.ticketTitle,
                                address: t.address,
                                date: t.date,
                                time: t.time,
                                price: t.price,
                                currency: t.currency || '€',
                                image: t.image,
                                description: t.description,
                                quota: t.quota,
                                isActive: t.isActive,
                              });
                            }}
                            className="p-1.5 rounded-lg bg-blue-950/60 text-blue-300 border border-blue-500/40 hover:bg-blue-800"
                            title="Düzenle"
                          >
                            <FileEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEventTicket(t.id, t.ticketTitle)}
                            className="p-1.5 rounded-lg bg-rose-950/60 text-rose-300 border border-rose-500/40 hover:bg-rose-800"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SUBTAB 2: BİLET SİPARİŞLERİ VE ONAY SÜRECİ */}
          {ticketsSubTab === 'bilet_siparisleri' && (
            <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <h3 className="text-lg font-extrabold text-white font-heading">
                    Satın Alınan Biletler & Yönetim Onay Paneli
                  </h3>
                  <p className="text-xs text-gray-300">
                    Kullanıcılar tarafından sipariş edilen biletleri inceleyin, onaylayın, reddedin veya askıya alın.
                  </p>
                </div>

                {/* Filter Tabs */}
                <div className="flex flex-wrap items-center gap-1.5 bg-black/40 p-1.5 rounded-2xl border border-white/10 text-xs font-bold">
                  <button
                    onClick={() => setPurchaseStatusFilter('all')}
                    className={`px-3 py-1.5 rounded-xl transition-all ${
                      purchaseStatusFilter === 'all' ? 'bg-white text-[#0B1426]' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    Tümü ({userPurchases.length})
                  </button>
                  <button
                    onClick={() => setPurchaseStatusFilter('pending')}
                    className={`px-3 py-1.5 rounded-xl transition-all ${
                      purchaseStatusFilter === 'pending' ? 'bg-amber-500 text-[#0B1426]' : 'text-amber-300'
                    }`}
                  >
                    ⏳ Bekleyenler ({userPurchases.filter((p) => p.status === 'pending').length})
                  </button>
                  <button
                    onClick={() => setPurchaseStatusFilter('approved')}
                    className={`px-3 py-1.5 rounded-xl transition-all ${
                      purchaseStatusFilter === 'approved' ? 'bg-emerald-500 text-[#0B1426]' : 'text-emerald-300'
                    }`}
                  >
                    ✅ Onaylı ({userPurchases.filter((p) => p.status === 'approved').length})
                  </button>
                  <button
                    onClick={() => setPurchaseStatusFilter('rejected')}
                    className={`px-3 py-1.5 rounded-xl transition-all ${
                      purchaseStatusFilter === 'rejected' ? 'bg-rose-500 text-white' : 'text-rose-300'
                    }`}
                  >
                    ❌ Reddedilen ({userPurchases.filter((p) => p.status === 'rejected').length})
                  </button>
                </div>
              </div>

              {/* Table of Purchases */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 font-bold uppercase text-[10px]">
                      <th className="py-3 px-3">Bilet No</th>
                      <th className="py-3 px-3">Bilet Sahibi (Alıcı)</th>
                      <th className="py-3 px-3">Etkinlik & Bilet</th>
                      <th className="py-3 px-3">Ödeme & Tutar</th>
                      <th className="py-3 px-3">Durum</th>
                      <th className="py-3 px-3">Kullanım</th>
                      <th className="py-3 px-3 text-right">Yönetim Aksiyonu</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-gray-300">
                    {userPurchases
                      .filter((p) => (purchaseStatusFilter === 'all' ? true : p.status === purchaseStatusFilter))
                      .map((p) => (
                        <tr key={p.id} className="hover:bg-white/5 transition-colors">
                          <td className="py-3.5 px-3 font-mono font-bold text-[#D4AF37]">{p.id}</td>
                          <td className="py-3.5 px-3">
                            <span className="font-bold text-white block">{p.userFullName}</span>
                            <span className="text-[10px] text-gray-400 block font-mono">{p.userPhone}</span>
                            <span className="text-[10px] text-gray-400 block font-mono">{p.userEmail}</span>
                          </td>
                          <td className="py-3.5 px-3">
                            <span className="font-extrabold text-[#38BDF8] block text-[11px]">{p.eventName}</span>
                            <span className="text-gray-200 font-bold block">{p.ticketTitle}</span>
                            <span className="text-[10px] text-gray-400">{p.date} {p.time}</span>
                          </td>
                          <td className="py-3.5 px-3">
                            <span className="font-black text-emerald-400 text-sm block">
                              {p.price} {p.currency || '€'}
                            </span>
                            <span className="text-[10px] text-gray-400 block uppercase">
                              {p.paymentMethod === 'credit_card' ? 'Kredi Kartı POS' : 'Bank Havale / EFT'}
                            </span>
                          </td>
                          <td className="py-3.5 px-3">
                            {p.status === 'approved' && (
                              <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold">
                                ✅ ONAYLANDI
                              </span>
                            )}
                            {p.status === 'pending' && (
                              <span className="px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold animate-pulse">
                                ⏳ BEKLEMEDE
                              </span>
                            )}
                            {p.status === 'rejected' && (
                              <span className="px-2.5 py-1 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-bold">
                                ❌ REDDEDİLDİ
                              </span>
                            )}
                            {p.status === 'suspended' && (
                              <span className="px-2.5 py-1 rounded-full bg-gray-500/20 text-gray-300 border border-gray-500/40 text-[10px] font-bold">
                                ⏸️ ASKIYA ALINDI
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-3">
                            {p.isUsed ? (
                              <span className="text-rose-400 font-bold text-[10px] block">
                                🛑 OKUNDU ({p.usedAt ? p.usedAt.split('T')[1]?.slice(0, 5) : 'Kullanıldı'})
                              </span>
                            ) : (
                              <span className="text-emerald-400 font-bold text-[10px] block">
                                🟢 OKUNMADI (Kullanılabilir)
                              </span>
                            )}
                          </td>
                          <td className="py-3.5 px-3 text-right space-x-1.5">
                            {p.status !== 'approved' && (
                              <button
                                onClick={() => handleUpdatePurchaseStatus(p, 'approved')}
                                className="px-2.5 py-1.5 rounded-lg bg-emerald-950/80 hover:bg-emerald-800 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold"
                              >
                                Onayla
                              </button>
                            )}
                            {p.status !== 'rejected' && (
                              <button
                                onClick={() => {
                                  const reason = prompt('Red gerekçesini giriniz:', 'Ödeme onaylanamadı / Geçersiz dekont.');
                                  if (reason !== null) {
                                    handleUpdatePurchaseStatus(p, 'rejected', reason);
                                  }
                                }}
                                className="px-2.5 py-1.5 rounded-lg bg-rose-950/80 hover:bg-rose-800 text-rose-300 border border-rose-500/40 text-[10px] font-bold"
                              >
                                Reddet
                              </button>
                            )}
                            {p.status !== 'suspended' && (
                              <button
                                onClick={() => handleUpdatePurchaseStatus(p, 'suspended')}
                                className="px-2.5 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-600 text-[10px] font-bold"
                              >
                                Askıya Al
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePurchase(p.id)}
                              className="p-1.5 rounded-lg bg-rose-950/60 text-rose-300 border border-rose-500/40 hover:bg-rose-800"
                              title="Sil"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SUBTAB 3: QR BİLET KONTROL / KAPI SCANNER */}
          {ticketsSubTab === 'qr_kontrol' && (
            <div className="space-y-6">
              {/* Mandatory Top Event Selector Box */}
              <div className="glass-card rounded-3xl p-6 border-2 border-[#38BDF8] bg-gradient-to-r from-[#0B1426] via-[#0F283E] to-[#0A324A] text-white space-y-4 shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#38BDF8] text-[#0B1426] flex items-center justify-center font-black">
                    <QrCode className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black font-heading text-white">
                      🎫 Kapı Giriş Bilet QR Okuma & Kontrol Sistemi
                    </h3>
                    <p className="text-xs text-[#7DD3FC] font-semibold">
                      Giriş kapısında bilet okuturken lütfen öncelikle kontrol edilecek etkinliği seçiniz.
                    </p>
                  </div>
                </div>

                {/* Event Selector Dropdown */}
                <div className="p-4 rounded-2xl bg-black/50 border border-white/20 space-y-2">
                  <label className="text-xs font-black text-[#D4AF37] uppercase tracking-wider block">
                    1. AŞAMA: KAPIDA HANGİ ETKİNLİĞİN BİLETİ KONTROL EDİLECEK? (ETKİNLİK SEÇİNİZ) *
                  </label>
                  <select
                    value={selectedGateEvent}
                    onChange={(e) => {
                      setSelectedGateEvent(e.target.value);
                      setQrValidationResult(null);
                    }}
                    className="w-full p-3 rounded-xl bg-[#0B1426] border-2 border-[#38BDF8] text-white font-extrabold text-sm focus:outline-none focus:border-[#7DD3FC]"
                  >
                    {eventTickets.map((t) => (
                      <option key={t.id} value={t.eventName}>
                        {t.eventName} — ({t.ticketTitle} / {t.date})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-300 italic">
                    * KURAL: Farklı bir etkinliğe ait bilet okutulursa sistem "Bu bilet bu etkinlik için değildir!" uyarısı verir ve bilet kesinlikle işleme alınmaz.
                  </p>
                </div>

                {/* QR Scanner / Manual Input */}
                <form onSubmit={handleValidateQr} className="p-4 rounded-2xl bg-[#132238] border border-white/10 space-y-3">
                  <label className="text-xs font-bold text-gray-200 block">
                    2. AŞAMA: BİLET QR KODUNU TARA / KODU GİR *
                  </label>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      required
                      placeholder="Örn: TK-841920 veya QR Kod Barkod Katarını Yapıştırın"
                      value={scannedQrInput}
                      onChange={(e) => setScannedQrInput(e.target.value)}
                      className="flex-1 p-3.5 rounded-xl bg-[#0B1426] border border-white/20 text-white font-mono font-bold text-sm focus:outline-none focus:border-emerald-400"
                    />
                    <button
                      type="submit"
                      disabled={isValidatingQr}
                      className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg transition-all"
                    >
                      {isValidatingQr ? <RefreshCw className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                      <span>Bileti Tara ve Kontrol Et</span>
                    </button>
                  </div>
                </form>

                {/* Validation Result Box */}
                {qrValidationResult && (
                  <div
                    className={`p-6 rounded-3xl border-2 space-y-4 animate-in zoom-in-95 ${
                      qrValidationResult.valid && qrValidationResult.success
                        ? 'border-emerald-500 bg-emerald-950/80 text-emerald-100 shadow-[0_0_30px_rgba(16,185,129,0.5)]'
                        : qrValidationResult.errorCode === 'ALREADY_USED'
                        ? 'border-rose-500 bg-rose-950/80 text-rose-100 shadow-[0_0_30px_rgba(244,63,94,0.5)]'
                        : qrValidationResult.errorCode === 'WRONG_EVENT'
                        ? 'border-amber-500 bg-amber-950/80 text-amber-100 shadow-[0_0_30px_rgba(245,158,11,0.5)]'
                        : 'border-rose-600 bg-rose-950/90 text-rose-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {qrValidationResult.valid && qrValidationResult.success ? (
                        <CheckCircle2 className="w-10 h-10 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-10 h-10 text-rose-400 flex-shrink-0" />
                      )}
                      <div>
                        <h4 className="text-xl font-black font-heading tracking-wide">
                          {qrValidationResult.valid && qrValidationResult.success
                            ? '✅ GİRİŞ ONAYLANDI! (BİLET GEÇERLİ)'
                            : qrValidationResult.errorCode === 'ALREADY_USED'
                            ? '🛑 RED! BU BİLET DAHA ÖNCE OKUNMUŞTUR!'
                            : qrValidationResult.errorCode === 'WRONG_EVENT'
                            ? '🛑 RED! BU BİLET BU ETKİNLİK İÇİN DEĞİLDİR!'
                            : '🛑 RED! BİLET GEÇERSİZ / ONAYSIZ!'}
                        </h4>
                        <p className="text-xs font-bold mt-1 opacity-90">{qrValidationResult.message}</p>
                      </div>
                    </div>

                    {/* Details if ticket details exist */}
                    {qrValidationResult.ticketDetails && (
                      <div className="p-4 rounded-2xl bg-black/40 border border-white/10 text-xs space-y-2 font-mono">
                        <div className="grid grid-cols-2 gap-2">
                          <p>Bilet No: <strong className="text-[#D4AF37]">{qrValidationResult.ticketDetails.id}</strong></p>
                          <p>Bilet Sahibi: <strong className="text-white">{qrValidationResult.ticketDetails.userFullName}</strong></p>
                          <p>Etkinlik: <strong className="text-white">{qrValidationResult.ticketDetails.eventName}</strong></p>
                          <p>Bilet Türü: <strong className="text-white font-bold">{qrValidationResult.ticketDetails.ticketTitle}</strong></p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* OFFICIAL PRINTABLE RECEIPT MODAL FOR ADMIN */}
      {selectedAdminReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto print:p-0 print:bg-white print:fixed print:inset-0">
          <div className="bg-[#0B1426] border-2 border-[#D4AF37] rounded-3xl p-6 sm:p-8 w-full max-w-2xl text-white space-y-6 shadow-[0_0_50px_rgba(212,175,55,0.3)] animate-in zoom-in-95 print:border-none print:shadow-none print:bg-white print:text-black">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-[#D4AF37]/40 pb-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Receipt className="w-7 h-7 text-[#D4AF37]" />
                  <h2 className="text-lg font-black text-white uppercase tracking-wider font-heading print:text-black">
                    RESMİ TAHSİLAT MAKBUZU
                  </h2>
                </div>
                <p className="text-xs font-bold text-[#D4AF37] uppercase">
                  KÜRT KÜLTÜR VE DAYANIŞMA DERNEĞİ YVELINES
                </p>
                <p className="text-[10px] text-gray-300 print:text-gray-700">
                  ASSOC. CULTURELLE FRANCO-KURDE DE YVELINES • SIRET: 881 290 144 00018
                </p>
                <p className="text-[10px] text-gray-400 print:text-gray-600">
                  📍 250 bis boulevard maréchal juin 78200 Mantes La Jolie • 📞 +33 6 14 62 97 06
                </p>
              </div>

              <div className="text-right space-y-1">
                <span className="px-3 py-1 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40 font-mono text-xs font-extrabold block print:border-black print:text-black">
                  {selectedAdminReceipt.receiptNo}
                </span>
                <p className="text-[10px] text-gray-300 print:text-gray-700 font-mono">
                  Tarih: {selectedAdminReceipt.date}
                </p>
              </div>
            </div>

            {/* Receipt Content Table */}
            <div className="bg-[#10241F] border border-[#D4AF37]/30 rounded-2xl p-5 space-y-3 text-xs print:bg-gray-50 print:border-gray-300 print:text-black">
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-white/10 print:border-gray-300">
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">ÖDEYEN ÜYE / KİŞİ</span>
                  <span className="font-extrabold text-white text-sm block mt-0.5 print:text-black">{selectedAdminReceipt.payerName}</span>
                  {selectedAdminReceipt.memberNo && (
                    <span className="text-[10px] font-mono text-[#D4AF37] block mt-0.5">Üye No: {selectedAdminReceipt.memberNo}</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">ÖDEME YÖNTEMİ</span>
                  <span className="font-bold text-emerald-400 block mt-0.5 print:text-emerald-800">{selectedAdminReceipt.paymentMethod}</span>
                  <span className="text-[10px] text-gray-400 block mt-0.5">Dernek Resmi Tahsilat Cihazı</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">TAHSİLAT NEDENİ / AÇIKLAMA</span>
                  <span className="font-bold text-white block mt-0.5 print:text-black">{selectedAdminReceipt.title}</span>
                </div>
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">TAHSİL EDİLEN TUTAR</span>
                  <span className="font-black text-xl text-[#D4AF37] block mt-0.5 print:text-black">
                    €{selectedAdminReceipt.amount.toFixed(2)} {selectedAdminReceipt.currency}
                  </span>
                </div>
              </div>
            </div>

            {/* Official Stamp & Verification Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10 print:border-gray-300">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white p-1 rounded-xl shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-black" fill="currentColor">
                    <path d="M0,0 h30 v30 h-30 z M10,10 h10 v10 h-10 z M70,0 h30 v30 h-30 z M80,10 h10 v10 h-10 z M0,70 h30 v30 h-30 z M10,80 h10 v10 h-10 z M40,10 h20 v10 h-20 z M40,40 h20 v20 h-20 z M70,70 h10 v10 h-10 z M80,80 h20 v20 h-20 z" />
                  </svg>
                </div>
                <div className="text-[10px] text-gray-400 print:text-gray-600">
                  <p className="font-bold text-emerald-400 print:text-emerald-800">✓ DİJİTAL KAŞELİ RESMİ MAKBUZ</p>
                  <p>İşbu makbuz dernek tüzüğü uyarınca tahsil edilmiş resmi belgedir.</p>
                </div>
              </div>

              <div className="text-right text-[10px] text-gray-400 print:text-gray-600">
                <p className="font-extrabold text-white print:text-black uppercase">Dernek Yönetim Kurulu Saymanlığı</p>
                <p className="italic mt-0.5">Kürt Kültür Derneği Yvelines</p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10 print:hidden">
              <button
                onClick={() => window.print()}
                className="btn-primary text-xs py-2.5 px-5 flex items-center gap-2 cursor-pointer"
              >
                <Printer className="w-4 h-4" />
                <span>Makbuzu Yazdır / PDF İndir</span>
              </button>
              <button
                onClick={() => setSelectedAdminReceipt(null)}
                className="btn-secondary text-xs py-2.5 px-4 cursor-pointer"
              >
                <span>Kapat</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
